import { isPackageLine, packageLineName, toPackageFiscalLine } from './utils/packageLine';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Grid,
  styled,
  AlertColor,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  TextField, // ✅ YENİ: Müşteri notu modalı için
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Divider,
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useAuth } from '../../../contexts/AuthContext';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getPostLogoutRoute } from '../../../services/auth/terminalSession';
import { SALESAPI_URL, ACCOUNTINGAPI_URL } from '../../../config/api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PersonnelPermissions } from './components/modals/QuickSaleScreenSettingsModal';
import type { PendingSale } from './components/modals/PendingSalesModal';
import type { PaymentDetails } from './components/modals/PartialPaymentModal';
import QuickSaleAppBar from './components/QuickSaleAppBar';
import useScreenAnnouncements from './hooks/useScreenAnnouncements';
import useCustomerDisplay from './hooks/useCustomerDisplay';
import useProductRefund from './hooks/useProductRefund';
import useCashierAuth from './hooks/useCashierAuth';
import useSplitCartPayment from './hooks/useSplitCartPayment';
import CustomerSelectionPanel from './components/CustomerSelectionPanel';
import ProductDisplayArea from './components/ProductDisplayArea';
import CartSection from './components/CartSection';
import ActionControlsPanel from './components/ActionControlsPanel';
import AccessControlLivePanel from './components/AccessControlLivePanel';
import type { CardScanEvent, CardScanEntryResult } from './components/AccessControlLivePanel';
import ProductSearchBar, { ProductSearchBarRef } from './components/ProductSearchBar';
import { usePayment } from './hooks/usePayment';
import { useOpenCashDrawer } from './hooks/useOpenCashDrawer';
import { useQuickSaleCart } from './hooks/useQuickSaleCart';
import { useQuickSaleDiscount } from './hooks/useQuickSaleDiscount';
import { useQuickSalePending } from './hooks/useQuickSalePending';
import { useQuickSaleUI } from './hooks/useQuickSaleUI';
import { getFiscalDevice } from './hooks/fiscalDevices';
import { FiscalDeviceType } from './hooks/fiscalDevices/types';
import { FiscalProductItem } from './hooks/fiscalDevices/types';
import { useQuickSaleData } from './hooks/useQuickSaleData';
import { useQuickSaleProductSearch } from './hooks/useQuickSaleProductSearch';
import { useQuickSaleCustomers } from './hooks/useQuickSaleCustomers';
import { useQuickSaleProducts } from './hooks/useQuickSaleProducts';
import { useQuickSaleModals } from './hooks/useQuickSaleModals';
import { useScanAlerts } from './hooks/useScanAlerts';
import { hasPendingPosRefundsInStorage } from './components/modals/PosRefundModal'; // ✅ YENİ
import PrepaidPavoP2PModals from './components/PrepaidPavoP2PModals';
import QuickSaleMiscDialogs from './components/QuickSaleMiscDialogs';
import PaymentFlowModals from './components/PaymentFlowModals';
import { getPavoPaySales, setLocalServiceUrl as setPavoPayLocalServiceUrl, PavoPayCustomerParty, setPendingCustomerParty, clearPendingCustomerParty, setPendingIsEInvoice, setPendingNotificationPhone, linkClubEntryCard } from './hooks/fiscalDevices/pavopay';
import { setLocalServiceUrl as setHuginLocalServiceUrl } from './hooks/fiscalDevices/hugin'; // ✅ YENİ: Hugin için localServiceUrl
import { setLocalServiceUrl as setInposLocalServiceUrl } from './hooks/fiscalDevices/inpos'; // ✅ YENİ: InPos için localServiceUrl
import { setLocalServiceUrl as setBekoTokenLocalServiceUrl, bekoTokenDevice, setTargetDeviceIndex as setBekoTargetDevice } from './hooks/fiscalDevices/beko-token';
import { setLocalServiceUrl as setIngenicoLocalServiceUrl, checkIngenicoOpenTransaction, cancelIngenicoOpenTransaction, IngenicoOpenTransactionData, syncDepartmentVatMapping, DepartmentVatSyncResult } from './hooks/fiscalDevices/ingenico';
import { useLocalServiceUpdateCheck } from './hooks/useLocalServiceUpdateCheck';
import LocalServiceUpdateModal from './components/modals/LocalServiceUpdateModal';
import QuickSaleModals from './components/QuickSaleModals';
import RestaurantFlowModals from './components/RestaurantFlowModals';
import ClubEntryFlowModals from './components/ClubEntryFlowModals';
import PrepaidCardPanel from './components/PrepaidCardPanel';
import PrepaidCardSearchModal from './components/modals/PrepaidCardSearchModal';
import salesService, { createPackageOrder, CreatePackageOrderDto, CreatePackageOrderItemDto, addItemsToPackageOrder, QuickSaleOtherPaymentWithInvoiceRequest } from '../../../services/salesService'; // ✅ YENİ: SQL'den ürün arama ve paket sipariş için
import { clubEntryService as clubEntryServiceStatic } from '../../../services/clubEntryService'; // Karta tanımlı ön ödemeli ürün hakkı (entitlement) için
import accountingService from '../../../services/accountingService'; // ✅ YENİ: Paket modunda cari seçimi için
import { cardManagementService } from '../../../services/cardManagementService';
import { setRestP2PConfig, clearRestP2PConfig } from '../../../services/restP2PConfig';
import { setPavoCardMediator, DEFAULT_PAVO_CARD_MEDIATOR } from '../../../services/pavoMediatorConfig';
import { useRestaurantCartOperations, useRestaurantKitchenOperations, useRestaurantTableOperations, useRestaurantNumpadOperations } from '../restaurant/hooks'; // ✅ REFACTORED: Restoran hook'ları
import {
  recordTableOpener as recordRestaurantTableOpener,
  getTableOpenerName as getRestaurantTableOpenerName,
  extractFirstOpenerFromActions as extractFirstOpenerFromWaiterActions,
  getUseTableOpenerForReceipt as getUseTableOpenerForReceiptSetting,
} from '../restaurant/utils/tableOpenersStorage'; // ✅ Adisyon fişi: masayı ilk açan garson takibi
import { useKitchenPrint, KitchenOrderItem } from '../restaurant/components/KitchenPrintService'; // ✅ YENİ: Mutfak fişi yazdırma servisi
import AddAccount from '../../accounting/accounts/AddAccount'; // ✅ YENİ: Cari hesap ekleme modalı
import SplitCartModal from './components/modals/SplitCartModal';
import { parseVatRate } from './utils/vatTaxGroup';

import CallSplitIcon from '@mui/icons-material/CallSplit'; // ✅ YENİ: Sepet bölme ikonu
import CashierLoginModal, { CashierInfo } from './components/modals/CashierLoginModal'; // ✅ YENİ: Kasiyer giriş modalı
import MenuOptionSelectionModal from './components/modals/MenuOptionSelectionModal'; // ✅ YENİ: Menü opsiyon seçim modalı
import QuickSaleKitchenOrdersModal from './components/modals/QuickSaleKitchenOrdersModal'; // ✅ YENİ: Mutfak siparişleri modalı
import EndOfDayPrintModal from '../../reports/components/EndOfDayPrintModal'; // ✅ YENİ: Kasiyere özel Gün Sonu yazdırma modalı
import KioskProductsModal from './components/modals/KioskProductsModal'; // 🖥️ Plan 2 — Kiosk Urun Gorunurluk Modal
import CashChangeModal from './components/modals/CashChangeModal'; // ✅ YENİ: Para üstü modalı
import type { SelectedOption } from './types/product.types'; // ✅ YENİ: Opsiyon tipi
import AutoLockCountdownModal from './components/modals/AutoLockCountdownModal';
import { useAutoLock } from '../../../hooks/useAutoLock';
import { useWeightBarcodePrefixes } from '../../../hooks/useWeightBarcodePrefixes';

// ═══════════════════════════════════════════════════════════
// 📦 TYPE IMPORTS
// ═══════════════════════════════════════════════════════════
import type {
  Customer,
  FilteredProduct,
  CartProduct,
  CartItem,
  InventoryItem,
  InventoryCategory,
  Warehouse,
  QuickSaleScreenData,
  UserData,
  StockVisibilitySettings,
  ScreenSettings,
  Personnel,
  QuickSaleInvoiceItem,
  QuickSaleInvoice,
  PykScreenData,
} from './types';

import type { FiscalDeviceStatus } from './components/QuickSaleAppBar';

// ═══════════════════════════════════════════════════════════
// 🔧 CONSTANTS IMPORTS
// ═══════════════════════════════════════════════════════════
import {
  STORAGE_KEYS,
  DEFAULT_CUSTOMER,
  TRANSACTION_TYPES,
  DEBUG_MODE,
  LAYOUT,
} from './constants';

// Re-export types for backward compatibility with other components
// ✅ Customer tipi artık types/customer.types.ts'den geliyor
export type { Customer } from './types/customer.types';
export type { FilteredProduct, CartProduct, CartItem, InventoryItem, InventoryCategory, Warehouse, QuickSaleScreenData, ScreenSettings }

// ═══════════════════════════════════════════════════════════
// 🛠️ UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Conditional logging function for debug purposes
 * Only logs when DEBUG_MODE is enabled
 */
const debugLog = (message: string, ...data: any[]) => {
  if (DEBUG_MODE) {
    console.log(message, ...data);
  }
};

// Türkiye saatini ISO formatında döndüren helper fonksiyon
const getTurkeyTimeISO = (): string => {
  const now = new Date();
  // Türkiye saat diliminde tarihi al
  const turkeyDateStr = now.toLocaleString('sv-SE', { 
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).replace(' ', 'T');
  
  // Milisaniye ekle - Z kaldırıldı (Z = UTC anlamına gelir, biz local time gönderiyoruz)
  const ms = now.getMilliseconds().toString().padStart(3, '0');
  return `${turkeyDateStr}.${ms}`;
};

// ✅ YENİ: Paket Modu Props Interface
interface PackageCustomer {
  id: number;
  name: string;
  phone: string;
  addressId: number;
  address: string;
}

interface QuickSaleScreenProps {
  screenId?: string; // Props olarak verilirse route yerine bu kullanılır
  isPackageMode?: boolean; // Paket sipariş modu
  packageCustomer?: PackageCustomer; // Paket müşteri bilgileri
  /**
   * Sipariş tamamlandığında callback.
   *
   * @param orderId Tamamlanan/güncellenen paket sipariş ID'si.
   * @param meta İlave sipariş akışında yeni eklenen item ID'leri ve
   *   "ilave mi" bayrağı. Yeni sipariş oluşturma akışında `undefined`.
   *   Mutfak fişinin SADECE yeni eklenen kalemleri basabilmesi için
   *   parent bu bilgiyi alır.
   */
  onPackageOrderComplete?: (
    orderId: number,
    meta?: { addedItemIds?: number[]; isAdditionalOrder?: boolean }
  ) => void;
  onClose?: () => void; // Modal kapatma callback
  onSettingsReady?: (openSettings: () => void) => void; // ✅ YENİ: Ayarlar butonunu parent'a expose et
  onProductSortReady?: (openProductSort: () => void) => void; // ✅ YENİ: Ürün Kartı Düzeni butonunu parent'a expose et (paket modu)
  packageCashierInfo?: CashierInfo | null; // ✅ YENİ: Paket modunda dışarıdan gelen kasiyer bilgisi
  addOnOrderId?: number; // ✅ YENİ: İlave sipariş modu - varsa mevcut siparişe ürün eklenir
}

const INGENICO_FIS_LIMIT_TL = 12000;
const BEKO_FIS_LIMIT_TL_DEFAULT = 9000;
const BEKO_KALEM_LIMIT = 150;
// ✅ Cihazdan bağımsız sepet kalem limiti — kullanıcı isteği:
// Hangi yazarkasa entegrasyonu seçilirse seçilsin (veya hiç seçilmesin),
// sepete en fazla GLOBAL_KALEM_LIMIT (100) farklı kalem (ürün) eklenebilir.
// Beko aktifken bu kontrol atlanır ve Beko'nun kendi limiti (BEKO_KALEM_LIMIT = 150)
// uygulanır.
const GLOBAL_KALEM_LIMIT = 100;

const getBekoFisLimitTL = (): number => {
  const limits = bekoTokenDevice.getDeviceLimits?.();
  if (limits?.receiptLimit && limits.receiptLimit > 0) {
    return limits.receiptLimit / 100;
  }
  return BEKO_FIS_LIMIT_TL_DEFAULT;
};

/**
 * Ürün eklerken ilgili kısmın limitini kontrol eder.
 * Limit aşılıyorsa Türkçe hata mesajı döner, yoksa null döner.
 * Kısım limiti kuruş cinsinden (limit alanı). Eşleşme KDV oranına göre yapılır.
 */
const checkBekoSectionLimit = (product: any, quantityToAdd: number, cart: any[]): string | null => {
  const limits = bekoTokenDevice.getDeviceLimits?.();
  if (!limits || limits.sectionLimits.length === 0) return null;

  const productVatRate = product.vatRate ?? product.taxRate ?? 0;
  const productTaxPercent = productVatRate <= 1 ? productVatRate * 100 : productVatRate;

  const matchingSection = limits.sectionLimits.find(s => 
    Math.abs(s.taxPercent - productTaxPercent) < 0.01
  );

  if (!matchingSection) return null;

  const limitTL = matchingSection.limit / 100;
  const productPrice = product.price || product.salePrice || 0;
  
  let sectionTotalInCart = 0;
  for (const item of cart) {
    const itemVatRate = item.vatRate ?? item.taxRate ?? 0;
    const itemTaxPercent = itemVatRate <= 1 ? itemVatRate * 100 : itemVatRate;
    if (Math.abs(itemTaxPercent - productTaxPercent) < 0.01) {
      sectionTotalInCart += (item.price || item.salePrice || 0) * (item.quantity || 1);
    }
  }
  
  const newSectionTotal = sectionTotalInCart + (productPrice * quantityToAdd);
  
  if (newSectionTotal > limitTL) {
    return `"${matchingSection.name}" kısmının satış limiti ${limitTL.toLocaleString('tr-TR')} TL'dir.\n\nBu ürünü ekledikten sonra bu kısımdaki toplam ${newSectionTotal.toFixed(2)} TL olacak ve kısım limitini aşacaktır.\n\nBu kısımda daha fazla ürün satışı yapılamaz.`;
  }
  
  return null;
};

const QuickSaleScreen: React.FC<QuickSaleScreenProps> = ({
  screenId: propsScreenId,
  isPackageMode = false,
  packageCustomer,
  onPackageOrderComplete,
  onClose,
  onSettingsReady,
  onProductSortReady, // ✅ YENİ: Paket modunda Ürün Kartı Düzeni butonu için
  packageCashierInfo, // ✅ YENİ: Paket modunda kasiyer bilgisi
  addOnOrderId, // ✅ YENİ: İlave sipariş modu için mevcut sipariş ID'si
}) => {
  const { t } = useTranslation('salesSystem');
  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 1: CORE HOOKS (Auth & Router)
  // ═══════════════════════════════════════════════════════════
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ YENİ: Restaurant modu için state okuma
  const { screenId: routeScreenId } = useParams<{ screenId: string }>();
  
  // ✅ YENİ: Ekran ID'si - props varsa onu kullan, yoksa route'tan al
  const screenId = propsScreenId || routeScreenId;
  
  // ✅ YENİ: Restaurant Modu Tespiti (location.state'ten)
  const restaurantState = location.state as {
    isRestaurantMode?: boolean;
    restaurantScreenId?: number;
    restaurantBranchId?: string | null; // ✅ YENİ: Şube ID
    tableId?: string;
    tableName?: string;
    useKitchenScreen?: boolean; // ✅ YENİ: Mutfak ekranı ayarı
    // ✅ YENİ: Garson veya Kasiyer bilgisi (RestaurantScreen'den)
    loginInfo?: {
      id: number;
      name: string;
      loginType: 'waiter' | 'cashier';
      canCloseSale: boolean;
      canCloseTable: boolean;
      canAddProduct: boolean;
      canRemoveProduct: boolean;
      canClearCart: boolean;
      canPrintToKitchen: boolean;
      canDeletePrintedItems: boolean;
      canDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
      canTransferTable: boolean;
      canPosRefund?: boolean;
      canCancelReceipt?: boolean; // Adisyon iptal yetkisi
      canCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
      canPrintReceipt?: boolean; // Adisyon yazdırma yetkisi
      canAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
      canUsePavoP2P?: boolean;
      // Kasiyer ek yetkileri
      canChangePrice?: boolean;
      canApplyDiscount?: boolean;
      canCancelSale?: boolean;
      canViewReports?: boolean;
      canViewEndOfDay?: boolean;
      canWarehouseOperations?: boolean;
      canViewAccountBalance?: boolean;
      canListAccounts?: boolean;
      canQuickAddItem?: boolean;
      canAddAccount?: boolean;
    };
    // ✅ Geriye uyumluluk için
    waiterInfo?: {
      waiterId: number;
      waiterName: string;
      canCloseSale: boolean;
      canCloseTable: boolean;
      canAddProduct: boolean;
      canRemoveProduct: boolean;
      canClearCart: boolean;
      canPrintToKitchen: boolean;
      canDeletePrintedItems: boolean;
      canDeleteUnprintedItems?: boolean; // Yazdırılmamış ürün silme yetkisi
      canTransferTable: boolean;
      canPosRefund?: boolean;
      canCancelReceipt?: boolean; // Adisyon iptal yetkisi
      canCancelUnprintedReceipt?: boolean; // Yazdırılmamış adisyon iptal yetkisi
      canPrintReceipt?: boolean; // Adisyon yazdırma yetkisi
      canAddProductAfterReceiptPrint?: boolean; // Adisyon sonrası ürün ekleme
      canUsePavoP2P?: boolean;
    };
  } | null;
  
  const isRestaurantMode = restaurantState?.isRestaurantMode === true;
  const tableName = restaurantState?.tableName || '';
  // ✅ YENİ: loginInfo varsa oradan, yoksa waiterInfo'dan al
  const loginInfo = restaurantState?.loginInfo;
  const waiterName = loginInfo?.name || restaurantState?.waiterInfo?.waiterName || '';
  const loginType = loginInfo?.loginType || 'waiter'; // 'waiter' | 'cashier'
  const restaurantScreenId = restaurantState?.restaurantScreenId;
  const restaurantBranchId = restaurantState?.restaurantBranchId || null; // ✅ YENİ: Şube ID
  const tableId = restaurantState?.tableId;
  const useKitchenScreen = restaurantState?.useKitchenScreen ?? false;
  
  // ✅ TÜM GARSON/KASİYER YETKİLERİ (Restoran modu için)
  // loginInfo varsa oradan, yoksa waiterInfo'dan al
  const waiterPermissions = {
    canCloseSale: loginInfo?.canCloseSale ?? restaurantState?.waiterInfo?.canCloseSale ?? true,
    canCloseTable: loginInfo?.canCloseTable ?? restaurantState?.waiterInfo?.canCloseTable ?? true,
    canAddProduct: loginInfo?.canAddProduct ?? restaurantState?.waiterInfo?.canAddProduct ?? true,
    canRemoveProduct: loginInfo?.canRemoveProduct ?? restaurantState?.waiterInfo?.canRemoveProduct ?? true,
    canClearCart: loginInfo?.canClearCart ?? restaurantState?.waiterInfo?.canClearCart ?? true,
    canPrintToKitchen: loginInfo?.canPrintToKitchen ?? restaurantState?.waiterInfo?.canPrintToKitchen ?? true,
    canDeletePrintedItems: loginInfo?.canDeletePrintedItems ?? restaurantState?.waiterInfo?.canDeletePrintedItems ?? false,
    canDeleteUnprintedItems: loginInfo?.canDeleteUnprintedItems ?? restaurantState?.waiterInfo?.canDeleteUnprintedItems ?? false, // Yazdırılmamış ürün silme (varsayılan KAPALI)
    canTransferTable: loginInfo?.canTransferTable ?? restaurantState?.waiterInfo?.canTransferTable ?? true,
    canCancelReceipt: loginInfo?.canCancelReceipt ?? restaurantState?.waiterInfo?.canCancelReceipt ?? false, // Adisyon iptal yetkisi (varsayılan KAPALI)
    canCancelUnprintedReceipt: loginInfo?.canCancelUnprintedReceipt ?? restaurantState?.waiterInfo?.canCancelUnprintedReceipt ?? false, // Yazdırılmamış adisyon iptal (varsayılan KAPALI)
    canPrintReceipt: loginInfo?.canPrintReceipt ?? restaurantState?.waiterInfo?.canPrintReceipt ?? false, // Adisyon yazdırma yetkisi (varsayılan KAPALI)
    canAddProductAfterReceiptPrint: loginInfo?.canAddProductAfterReceiptPrint ?? restaurantState?.waiterInfo?.canAddProductAfterReceiptPrint ?? false, // Adisyon sonrası ürün ekleme (varsayılan KAPALI)
    canUsePavoP2P: loginInfo?.canUsePavoP2P ?? restaurantState?.waiterInfo?.canUsePavoP2P ?? false,
  };
  
  // 🐛 DEBUG: Yetkileri konsola yazdır (DETAYLI)
  // Bu blok her render'da çalışıyor; Electron kabuğunda renderer console'u dosyaya
  // yansıtıldığı için üretimde ciddi yavaşlamaya yol açıyordu → DEBUG_MODE'a alındı.
  if (isRestaurantMode && DEBUG_MODE) {
    console.log('┌══════════════════════════════════════════════════════════┐');
    console.log('║ 🔐 GARSON/KASİYER YETKİLERİ - TÜM DETAYLAR             ║');
    console.log('└══════════════════════════════════════════════════════════┘');
    console.log('👤 waiterName:', waiterName);
    console.log('👤 loginType:', loginType);
    console.log('📋 loginInfo:', loginInfo ? JSON.stringify({
      id: loginInfo.id,
      name: loginInfo.name,
      loginType: loginInfo.loginType,
      canRemoveProduct: loginInfo.canRemoveProduct,
      canDeletePrintedItems: loginInfo.canDeletePrintedItems,
      canAddProduct: loginInfo.canAddProduct,
      canCloseSale: loginInfo.canCloseSale,
    }) : 'NULL');
    console.log('📋 waiterInfo:', restaurantState?.waiterInfo ? JSON.stringify({
      waiterId: restaurantState.waiterInfo.waiterId,
      waiterName: restaurantState.waiterInfo.waiterName,
      canRemoveProduct: restaurantState.waiterInfo.canRemoveProduct,
      canDeletePrintedItems: restaurantState.waiterInfo.canDeletePrintedItems,
    }) : 'NULL');
    console.log('🔐 [SONUÇ waiterPermissions]:', JSON.stringify(waiterPermissions));
  }
  
  debugLog("[QuickSaleScreen.tsx] Component loaded!", {
    screenId,
    isRestaurantMode,
    isPackageMode,
    tableName,
    waiterName,
    restaurantState
  });
  
  // Mobil görünüm kontrolü
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md (960px) altında mobil

  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 2: LOCAL STATE (Component-specific state)
  // ═══════════════════════════════════════════════════════════
  
  /**
   * ✅ PERFORMANS OPTİMİZASYONU: searchTerm artık ProductSearchBar'da local state olarak tutuluyor
   * Bu sayede her karakter için QuickSaleScreen yeniden render olmuyor
   * Numpad için kullanılan değer:
   */
  const [numpadSearchValue, setNumpadSearchValue] = useState<string>('');
  
  /**
   * Currently selected customer for the sale
   * @reason Shared between hooks as parameter, managed at component level
   */
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  /** ✅ YENİ: Müşteri notu - sepet için geçerli, fiş yazdırıldığında temizlenir */
  const [customerNote, setCustomerNote] = useState<string>('');
  const [isCustomerNoteModalOpen, setIsCustomerNoteModalOpen] = useState(false);

  /** Ön ödemeli kart sistemi state'leri */
  const [prepaidCard, setPrepaidCard] = useState<any>(null);
  const [selectedPrepaidWalletId, setSelectedPrepaidWalletId] = useState<number | null>(null);
  const [isPrepaidCardSearchOpen, setIsPrepaidCardSearchOpen] = useState(false);
  /** Karta tanımlı ön ödemeli ürün hakları (entitlement) + ürün detay map'i */
  const [prepaidEntitlements, setPrepaidEntitlements] = useState<any[]>([]);
  const [entitledProductDetailMap, setEntitledProductDetailMap] = useState<Record<string, any>>({});

  // Satın Alınan Ürünler + İade (refund) state'leri → useProductRefund hook'una taşındı

  /** Quantity mode activation state */
  const [isQuantityModeActive, setIsQuantityModeActive] = useState(false);
  
  /** Current quantity input value */
  const [quantityInputValue, setQuantityInputValue] = useState('');

  /** Payment waiting modal states */
  const [paymentWaitingModalOpen, setPaymentWaitingModalOpen] = useState(false);
  const [paymentWaitingStatus, setPaymentWaitingStatus] = useState<'waiting' | 'success' | 'error'>('waiting');
  const [paymentWaitingMessage, setPaymentWaitingMessage] = useState('');

  /** Settings refresh state (passed to hooks) */
  const [refreshingSettings, setRefreshingSettings] = useState(false);
  
  /** ✅ YENİ: Adisyon açma onay modalı (Restaurant modu) */
  const [confirmOpenTableModalOpen, setConfirmOpenTableModalOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState<any | null>(null); // any - runtime'da tüm product properties'ine erişmek için
  const [pendingQuantity, setPendingQuantity] = useState(1);
  const [pendingMultiplier, setPendingMultiplier] = useState(1);
  const [isOpeningTable, setIsOpeningTable] = useState(false); // ✅ YENİ: Adisyon açma loading state
  
  /** ✅ YENİ: Numpad modalı (Restaurant modu - miktar girişi) */
  const [numpadModalOpen, setNumpadModalOpen] = useState(false);
  const [numpadProduct, setNumpadProduct] = useState<any | null>(null);

  const [transferTableModalOpen, setTransferTableModalOpen] = useState(false);
  const [isTransferringTable, setIsTransferringTable] = useState(false);
  const [transferItemModalOpen, setTransferItemModalOpen] = useState(false);
  
  /** ✅ YENİ: Masa birleştirme modalı (Restaurant modu) */
  const [mergeTablesModalOpen, setMergeTablesModalOpen] = useState(false);
  const [mergeTargetTableId, setMergeTargetTableId] = useState<number | null>(null);
  const [mergeTargetTableName, setMergeTargetTableName] = useState<string>('');
  const [mergeSourceItems, setMergeSourceItems] = useState<any[]>([]);
  const [mergeTargetItems, setMergeTargetItems] = useState<any[]>([]);
  const [isMergingTables, setIsMergingTables] = useState(false);

  /** User menu anchor element */
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<null | HTMLElement>(null);

  /** Ürün kartı sıralama düzeni */
  const [isProductSortPinDialogOpen, setIsProductSortPinDialogOpen] = useState(false);
  const [productSortPinInput, setProductSortPinInput] = useState('');
  const [productSortPinError, setProductSortPinError] = useState(false);
  const [isProductSortEditMode, setIsProductSortEditMode] = useState(false);
  const [customProductOrder, setCustomProductOrder] = useState<Record<string, string[]>>({});

  /** User permissions state */
  const [currentUserPermissions, setCurrentUserPermissions] = useState<PersonnelPermissions | null>(null);
  const [loadingUserPermissions, setLoadingUserPermissions] = useState<boolean>(true);

  /**
   * Weight input modal state
   * @reason Temporary UI state for KG products
   * @todo Consider moving to useWeightInput hook
   */
  const [isWeightInputModalOpen, setIsWeightInputModalOpen] = useState(false);
  const [tempSelectedProduct, setTempSelectedProduct] = useState<{
    productInfo: {
      id: string;
      name: string;
      price: number;
      vatRate: number;
      unit: string;
    },
    itemCanBeNegative: boolean;
    itemStockQuantity: number;
    currentQuantity?: number;
  } | null>(null);

  /**
   * ✅ YENİ: Menu Option Selection Modal state
   * Opsiyonlu menü ürünleri için
   */
  const [isMenuOptionModalOpen, setIsMenuOptionModalOpen] = useState(false);
  const [pendingMenuProduct, setPendingMenuProduct] = useState<{
    productId: string;
    productName: string;
    productPrice: number;
    productVatRate: number;
    productVatStatus: string;
    productUnit: string;
    itemCanBeNegative: boolean;
    itemStockQuantity: number;
    hasRecipe: boolean;
    hasMenu: boolean;
    categoryId?: string;
  } | null>(null);
  // ✅ YENİ: Düzenleme modu için sepet item'ı
  const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);

  /** Snackbar states (passed to hooks) */
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  /** Settings refresh tracking */
  const [lastSettingsRefresh, setLastSettingsRefresh] = useState<string | null>(null);

  /** ✅ YENİ: POS İade Modal state'leri */
  const [isPosRefundModalOpen, setIsPosRefundModalOpen] = useState(false);
  const [hasPendingPosRefunds, setHasPendingPosRefunds] = useState(false);
  
  /** ✅ YENİ: PavoPay İade Modal state'i */
  const [isPavoPayRefundModalOpen, setIsPavoPayRefundModalOpen] = useState(false);

  /** PavoPay Cari Sorgu Modal ve Geçici Cari */
  const [isCustomerQueryModalOpen, setIsCustomerQueryModalOpen] = useState(false);
  const [pavoPayTempCustomerName, setPavoPayTempCustomerName] = useState<string | null>(null);
  const [pavoPayTempCustomerParty, setPavoPayTempCustomerParty] = useState<PavoPayCustomerParty | null>(null);
  const [pavoPayIsEInvoice, setPavoPayIsEInvoice] = useState(false);

  /** Receipt number states */
  const [currentReceiptNumber, setCurrentReceiptNumber] = useState<number>(1);
  const [showReceiptNumber, setShowReceiptNumber] = useState<boolean>(false);

  /** ✅ YENİ: Yazarkasa (ÖKC) Bağlantı Durumu */
  const [fiscalDeviceStatus, setFiscalDeviceStatus] = useState<FiscalDeviceStatus>('not_configured');
  const [deptVatSyncResult, setDeptVatSyncResult] = useState<DepartmentVatSyncResult | null>(null);
  const [showDeptVatMismatchModal, setShowDeptVatMismatchModal] = useState(false);

  /** Ingenico acik fis kurtarma */
  const [isIngenicoRecoveryModalOpen, setIsIngenicoRecoveryModalOpen] = useState(false);
  const [ingenicoRecoveryData, setIngenicoRecoveryData] = useState<IngenicoOpenTransactionData | null>(null);
  const [isIngenicoRecoveryProcessing, setIsIngenicoRecoveryProcessing] = useState(false);

  /** ✅ YENİ: Kasiyer Sistemi State'leri */
  const [isCashierLoginModalOpen, setIsCashierLoginModalOpen] = useState(false);
  /** ✅ YENİ: Mutfak Siparişleri Modalı */
  const [isKitchenOrdersModalOpen, setIsKitchenOrdersModalOpen] = useState(false);
  const [endOfDayPrintModalOpen, setEndOfDayPrintModalOpen] = useState(false);
  // 🖥️ Plan 2 — Kiosk Urun Gorunurluk modali
  const [isKioskProductsModalOpen, setIsKioskProductsModalOpen] = useState(false);
  /** ✅ YENİ: Kasiyer giriş yapmadan ürün eklenmeye çalışıldığında bekleyen ürün bilgisi */
  const [pendingProductForCashierLogin, setPendingProductForCashierLogin] = useState<any | null>(null);
  const [pendingQuantityForCashierLogin, setPendingQuantityForCashierLogin] = useState<number>(1);
  const [currentCashier, setCurrentCashier] = useState<CashierInfo | null>(() => {
    // ✅ YENİ: Paket modunda dışarıdan kasiyer bilgisi geliyorsa onu kullan
    if (isPackageMode && packageCashierInfo) {
      console.log('✅ [QuickSaleScreen] Paket modunda kasiyer bilgisi alındı:', packageCashierInfo.cashierName);
      return packageCashierInfo;
    }
    
    // ✅ Restoran modunda kasiyer bilgisi geliyorsa otomatik set et
    if (isRestaurantMode && loginInfo?.loginType === 'cashier') {
      console.log('✅ [QuickSaleScreen] Restoran modunda kasiyer bilgisi alındı:', loginInfo.name);
      return {
        cashierId: loginInfo.id,
        cashierName: loginInfo.name,
        permissions: {
          // Normal Hızlı Satış Yetkileri
          canChangePrice: loginInfo.canChangePrice ?? false,
          canApplyDiscount: loginInfo.canApplyDiscount ?? false,
          canCancelSale: loginInfo.canCancelSale ?? false,
          canViewReports: loginInfo.canViewReports ?? false,
          canViewEndOfDay: loginInfo.canViewEndOfDay ?? false,
          canWarehouseOperations: loginInfo.canWarehouseOperations ?? false,
          canViewAccountBalance: loginInfo.canViewAccountBalance ?? false,
          canListAccounts: loginInfo.canListAccounts ?? false,
          canQuickAddItem: loginInfo.canQuickAddItem ?? false,
          canAddAccount: loginInfo.canAddAccount ?? false,
          // Restoran Yetkileri
          canCloseSale: loginInfo.canCloseSale ?? false,
          canCloseTable: loginInfo.canCloseTable ?? false,
          canAddProduct: loginInfo.canAddProduct ?? false,
          canRemoveProduct: loginInfo.canRemoveProduct ?? false,
          canClearCart: loginInfo.canClearCart ?? false,
          canPrintToKitchen: loginInfo.canPrintToKitchen ?? false,
          canDeletePrintedItems: loginInfo.canDeletePrintedItems ?? false,
          canDeleteUnprintedItems: loginInfo.canDeleteUnprintedItems ?? false, // Yazdırılmamış ürün silme yetkisi
          canTransferTable: loginInfo.canTransferTable ?? false,
          canPosRefund: loginInfo.canPosRefund ?? false,
          canCancelReceipt: loginInfo.canCancelReceipt ?? false, // Adisyon iptal yetkisi
          canCancelUnprintedReceipt: loginInfo.canCancelUnprintedReceipt ?? false, // Yazdırılmamış adisyon iptal yetkisi
          canPrintReceipt: loginInfo.canPrintReceipt ?? false, // Adisyon yazdırma yetkisi
          canAddProductAfterReceiptPrint: loginInfo.canAddProductAfterReceiptPrint ?? false, // Adisyon sonrası ürün ekleme
          canUsePavoP2P: loginInfo.canUsePavoP2P ?? false,
        }
      };
    }
    return null;
  });

  // ✅ YENİ: Paket sipariş ödeme yöntemi seçim modalı
  const [packagePaymentModalOpen, setPackagePaymentModalOpen] = useState(false);
  const [pendingPackageOrderData, setPendingPackageOrderData] = useState<{
    orderDto: CreatePackageOrderDto;
    customer: any;
  } | null>(null);

  /** KG product cart update states */
  const [isCartUpdateMode, setIsCartUpdateMode] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<CartItem | null>(null);

  /** Search bar ref for focus management */
  const searchBarRef = useRef<ProductSearchBarRef>(null);

  /** ✅ YENİ: Restaurant modu - Masayı kapat loading state */
  const [isClosingTable, setIsClosingTable] = useState(false);

  /** ✅ YENİ: Restaurant modu - Yazdırılmamış ürün uyarı modalı state'leri */
  const [unprintedItemsWarningOpen, setUnprintedItemsWarningOpen] = useState(false);

  /** Mutfak yazdırma bilgi modalı */
  const [kitchenPrintInfoOpen, setKitchenPrintInfoOpen] = useState(false);
  const [kitchenPrintInfoData, setKitchenPrintInfoData] = useState<{
    printedItems: { name: string; quantity: number }[];
    unprintedItems: { name: string; quantity: number }[];
    /** ✅ YENİ: Kategorisinde yazıcı atanmadığı için yazdırılamayan ürünler */
    skippedItems?: { name: string; quantity: number; categoryName?: string }[];
  }>({ printedItems: [], unprintedItems: [], skippedItems: undefined });

  /** ✅ YENİ: Kategori bazlı modda — sepetteki TÜM ürünlerin kategorisinde
   *  yazıcı atanmamışsa gösterilen uyarı modalı. Tek bir tamam butonu ile kapanır. */
  const [noCategoryPrinterModalOpen, setNoCategoryPrinterModalOpen] = useState(false);
  const [noCategoryPrinterModalData, setNoCategoryPrinterModalData] = useState<{
    categoryNames: string[];
    skippedItems: { name: string; quantity: number; categoryName?: string }[];
  }>({ categoryNames: [], skippedItems: [] });

  /** Mutfağa yazdırma sonrası masalar ekranına otomatik dönüş geri sayımı */
  const [kitchenReturnCountdownOpen, setKitchenReturnCountdownOpen] = useState(false);
  
  /** ✅ YENİ: Adisyon yazdırıldı - Yetki yok modalı state'leri */
  const [receiptPrintedNoPermissionOpen, setReceiptPrintedNoPermissionOpen] = useState(false);
  const [isReceiptPrintedForCurrentTable, setIsReceiptPrintedForCurrentTable] = useState(false);
  const [receiptPrintedInfo, setReceiptPrintedInfo] = useState<{
    receiptPrintedAt?: string;
    receiptPrintedBy?: string;
  }>({});

  /**
   * ✅ YENİ: Adisyon fişine yazılacak garson adı (override).
   * Restoran ekran toolbar'ındaki "Adisyonda İlk Açan Garson" toggle'ı açıkken
   * masayı ilk açan garsonun adı ile dolar; toggle kapalıysa veya kayıt yoksa null kalır
   * (hook fallback olarak login yapan garson adını kullanır).
   * Sadece adisyon fişini etkiler — mutfak yazdırma vb. akışlar değişmez.
   */
  const [receiptWaiterName, setReceiptWaiterName] = useState<string | null>(null);
  
  /** ✅ YENİ: Garson işlemleri geçmişi (Masa Geçmişi modalı için) */
  const [waiterActionsJson, setWaiterActionsJson] = useState<string | null>(null);
  
  /** ✅ YENİ: Adisyon yazdırıldı - Onay modalı state'leri (yetkili garsonlar için) */
  const [receiptPrintedConfirmOpen, setReceiptPrintedConfirmOpen] = useState(false);
  
  /** Ürün silme nedeni modalı (restoran modu) */
  const [deleteReasonModalOpen, setDeleteReasonModalOpen] = useState(false);

  /** Ürün açıklaması/notu modalı (restoran modu) */
  const [itemNoteModalOpen, setItemNoteModalOpen] = useState(false);
  const [itemNoteCurrentNote, setItemNoteCurrentNote] = useState('');

  /** ✅ YENİ: Sepet bölme modalı state'i */
  const [splitCartModalOpen, setSplitCartModalOpen] = useState(false);
  const [splitRightCart, setSplitRightCart] = useState<CartItem[]>([]); // Bölünmüş sağ sepet
  const [splitRightInvoiceNumber, setSplitRightInvoiceNumber] = useState<string>(''); // Sağ sepet fiş numarası
  const [selectedCartView, setSelectedCartView] = useState<'cart1' | 'cart2'>('cart1'); // ✅ YENİ: Hangi sepet görüntüleniyor?
  const [splitBIndex, setSplitBIndex] = useState<number>(2); // ✅ YENİ: Sepet bölme index'i (B2, B3, B4...)
  const prevReservedLongCodeRef = useRef<string | null>(null); // ✅ YENİ: Önceki fiş numarasını takip
  
  /** ✅ YENİ: Sepet bölme parçalı ödeme bilgisi */
  const [splitCartPartialPaymentInfo, setSplitCartPartialPaymentInfo] = useState<{
    cartType: 'left' | 'right';
    cartItems: CartItem[];
    total: number;
    invoiceNumber: string;
    newCartAfterPayment: CartItem[];
  } | null>(null);
  
  /** ✅ YENİ: SplitCartModal refresh key - parçalı ödeme sonrası modal'ı yenilemek için */
  const [splitCartRefreshKey, setSplitCartRefreshKey] = useState(0);
  
  /** ✅ YENİ: Sepet bölme ödemesi için callback state'i */
  const [splitPaymentSuccessCallback, setSplitPaymentSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const [isSplitPaymentMode, setIsSplitPaymentMode] = useState(false);
  
  /** ✅ YENİ: Sepet bölme için pending ödeme bilgisi */
  const [pendingSplitPayment, setPendingSplitPayment] = useState<{
    paymentType: 'cash' | 'pos' | 'partial';
    posOption?: any;
    partialPaymentDetails?: PaymentDetails; // Parçalı ödeme için
  } | null>(null);
  
  /** ✅ YENİ: Sepet bölme ödemesi için kullanılacak sepet (cart'ı değiştirmemek için) */
  const [pendingSplitCartItems, setPendingSplitCartItems] = useState<CartItem[]>([]);
  /** ✅ YENİ: Restoran modu + sepet bölme — hangi ActiveSale kapatılacak (sol=mainSaleId, sağ=splitSaleId) */
  const [pendingSplitActiveSaleId, setPendingSplitActiveSaleId] = useState<number | null>(null);
  const [pendingProductForReceiptConfirm, setPendingProductForReceiptConfirm] = useState<any | null>(null);
  const [pendingQuantityForReceiptConfirm, setPendingQuantityForReceiptConfirm] = useState<number>(1);
  const [pendingActionTypeForReceiptConfirm, setPendingActionTypeForReceiptConfirm] = useState<'addToCart' | 'numpadConfirm'>('addToCart');
  const [unprintedItemsList, setUnprintedItemsList] = useState<{
    id: string;
    name: string;
    price: number;
    vatRate?: number;
    quantity: number;
    printedQuantity: number;
    unprintedQuantity: number;
  }[]>([]);
  
  /** ✅ YENİ: Restaurant modu - İptal edilen ürünler listesi */
  const [cancelledItemsList, setCancelledItemsList] = useState<{
    name: string;
    previousQuantity: number;
    currentQuantity: number;
    cancelledQuantity: number;
  }[]>([]);

  /** Action Controls Panel daraltılmış durumu */
  const [isActionPanelCollapsed, setIsActionPanelCollapsed] = useState(() => {
    const saved = localStorage.getItem('gts_action_panel_collapsed');
    return saved === 'true';
  });

  // Product Display Area daraltma özelliği kaldırıldı - artık her zaman genişletilmiş

  /** ✅ YENİ: Şirket adı cache (Fatura numarası için) */
  const [companyNameCache, setCompanyNameCache] = useState<string | null>(null);

  /** ✅ YENİ: Cari hesap ekleme modalı */
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);

  /** ✅ YENİ: Diğer ödeme yöntemleri modalı */
  const [otherPaymentModalOpen, setOtherPaymentModalOpen] = useState(false);

  /** ✅ YENİ: Online ödeme modalı */
  const [onlinePaymentModalOpen, setOnlinePaymentModalOpen] = useState(false);

  /** ✅ YENİ: Para üstü modalı */
  const [cashChangeModalOpen, setCashChangeModalOpen] = useState(false);

  /**
   * ✅ YENİ: Para üstü modalında girilen alınan para / para üstü (fişe basılır).
   * Modal onayı ödemeyi aynı tick içinde başlattığı için state yerine ref kullanılır;
   * aksi halde usePayment closure'ı bayat değer görür. Fiş basıldıktan sonra temizlenir.
   */
  const cashChangeInfoRef = useRef<{ receivedAmount: number; changeAmount: number } | null>(null);

  /** Modal her açılışında önceki (ör. yarıda kalmış) para üstü bilgisi temizlenir */
  const openCashChangeModal = () => {
    cashChangeInfoRef.current = null;
    setCashChangeModalOpen(true);
  };

  /** ✅ YENİ: Sepeti temizleme onay modalı (ActionControlsPanel ve CartSection paylaşıyor) */
  const [clearCartConfirmOpen, setClearCartConfirmOpen] = useState(false);

  /** ✅ YENİ: Rezerve edilmiş sipariş numaraları (Ekran açıldığında API'den alınır) */
  const [reservedLongCode, setReservedLongCode] = useState<string | null>(null);
  const [reservedShortCode, setReservedShortCode] = useState<string | null>(null);

  // ✅ YENİ: Ana fiş numarası değiştiğinde sepet bölme index'ini sıfırla
  useEffect(() => {
    // İlk yükleme değilse ve numara değiştiyse index'i sıfırla
    if (prevReservedLongCodeRef.current !== null && 
        prevReservedLongCodeRef.current !== reservedLongCode) {
      console.log(`🔢 [SPLIT INDEX] Ana fiş numarası değişti, splitBIndex sıfırlanıyor (${splitBIndex} → 2)`);
      setSplitBIndex(2);
    }
    prevReservedLongCodeRef.current = reservedLongCode;
  }, [reservedLongCode, splitBIndex]);

  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 3: CUSTOM HOOKS (Ordered by dependency)
  // ═══════════════════════════════════════════════════════════
  
  // ─────────────────────────────────────────────────────────
  // 3.1 - Cart Management (no external dependencies)
  // ─────────────────────────────────────────────────────────
  // Hem restoran modu hem paket hızlı satış modu, sepete eklenen aynı ürünün
  // ayrı kalem olarak listelenmesini gerektirir (restoran: her siparişin
  // bireysel takibi; paket: PackageOrderScreen'a ayrı kalemler olarak aktarım).
  const cartHook = useQuickSaleCart(isRestaurantMode || isPackageMode);
  const {
    cart,
    setCart,
    selectedCartItemId,
    setSelectedCartItemId,
    addToCart: addToCartHook,
    updateQuantity,
    removeFromCart,
    clearCart: clearCartHook,
    handleSelectItem,
    handleIncreaseSelected,
    handleDecreaseSelected,
    handleDeleteSelected,
    calculateTotal: calculateTotalHook,
  } = cartHook;

  const prevCartLengthRef = useRef(cart.length);
  useEffect(() => {
    const wasNonEmpty = prevCartLengthRef.current > 0;
    prevCartLengthRef.current = cart.length;
    if (wasNonEmpty && cart.length === 0 && pavoPayTempCustomerName) {
      console.log('[PavoPay TempCustomer] Sepet boşaldı, geçici cari temizleniyor');
      setPavoPayTempCustomerName(null);
      setPavoPayTempCustomerParty(null);
      setPavoPayIsEInvoice(false);
      clearPendingCustomerParty();
    }
  }, [cart.length, pavoPayTempCustomerName]);

  // ─────────────────────────────────────────────────────────
  // 3.2 - Data Layer (depends on auth & router)
  // ─────────────────────────────────────────────────────────
  const dataHook = useQuickSaleData({
    currentUser,
    routeScreenId: screenId, // Props veya route'tan gelen screenId
    navigate,
    refreshingSettings,
    selectedCustomer,
    setSelectedCustomer,
  });
  
  const {
    companyId,
    quickSaleUserData,
    currentScreen,
    permittedScreens,
    permittedPykScreens,
    allScreens,
    screenSettings,
    prepaidSystemEnabled: prepaidSystemEnabledRaw,
    showProductImages,
    // ✅ PR2 (Invoice Seller Snapshot Refactor): Companies vs Branches.Firm* snapshot
    seller,
    allInventoryItems,
    allCategoriesData,
    allWarehouses,
    allPersonnel,
    cashPaymentLabel,
    cashPaymentCashboxId,
    cashPaymentCashboxName, // ✅ YENİ: Kasa adı (gün sonu raporu için)
    posOptions,
    singlePosOption,
    openAccountPaymentOption,
    loadingScreens,
    loadingScreenSettings,
    loadingInventory,
    loadingWarehouses,
    loadingCategories,
    screenError,
    screenSettingsError,
    inventoryError,
    refreshScreenSettings: refreshScreenSettingsFromHook,
    setCompanyId,
    setCurrentScreen,
    setScreenSettings,
  } = dataHook;

  // Kulüp Girişi (Club Entry) state
  const [clubEntrySettings, setClubEntrySettings] = useState<any>(null);
  const isClubEntryMode = clubEntrySettings?.isEnabled === true;

  // ✨ Duyuru sistemi: Ekran için aktif duyurular (header marquee'sinde gösterilir)
  // Logic → useScreenAnnouncements hook'una taşındı (hooks/useScreenAnnouncements.ts)
  const { activeAnnouncements } = useScreenAnnouncements({
    companyId,
    screenId: currentScreen?.id,
    screenBranchId: currentScreen?.branchId,
    userBranchesId: quickSaleUserData?.branchesId,
  });

  // PYK ve Kulüp Girişi birlikte kullanılamaz - Kulüp Girişi öncelikli
  const prepaidSystemEnabled = isClubEntryMode ? false : prepaidSystemEnabledRaw;

  // Kulüp girişi ayarlarını yükle
  useEffect(() => {
    const loadClubEntrySettings = async () => {
      if (!currentScreen?.id) return;
      try {
        const { clubEntryService } = await import('../../../services/clubEntryService');
        const settings = await clubEntryService.getSettings(parseInt(currentScreen.id, 10));
        setClubEntrySettings(settings);
      } catch (err) {
        console.error('[QuickSaleScreen] Club entry settings yüklenemedi:', err);
      }
    };
    loadClubEntrySettings();
  }, [currentScreen?.id]);

  // Kulüp girişi modunda kart modalleri state
  const [showPostSaleCardModal, setShowPostSaleCardModal] = useState(false);
  const [showActiveEntriesModal, setShowActiveEntriesModal] = useState(false);
  const [showDirectTriggerModal, setShowDirectTriggerModal] = useState(false);
  const [showCardReturnModal, setShowCardReturnModal] = useState(false);
  // ✅ YENİ: Kulüp girişi rezervasyon listele modalı (Phase 1: sadece görüntüleme)
  const [showClubReservationsModal, setShowClubReservationsModal] = useState(false);
  const [lastSaleReceiptId, setLastSaleReceiptId] = useState<number | null>(null);
  const [lastPaymentMethod, setLastPaymentMethod] = useState<string>('CASH');
  const [lastPavoPaySaleNumber, setLastPavoPaySaleNumber] = useState<string | undefined>();
  const [lastReferenceNumber, setLastReferenceNumber] = useState<string | undefined>();
  const [lastServiceItems, setLastServiceItems] = useState<any[]>([]);
  // ✅ KULÜP GİRİŞİ: ödeme anında sepetten çıkarılan kart slotları (entitlement dahil) - PostSale modalı için
  const [lastCardSlots, setLastCardSlots] = useState<any[]>([]);

  // Kulüp girişi: ödeme öncesi kart okutma (deposito aktifken)
  const [showPrePaymentCardModal, setShowPrePaymentCardModal] = useState(false);
  // 'OTHER_TICKET': dış bilet platformu (FIRSAT ME / BU BİLET) — kart okutma sonrası
  //   sepet gerçek fiyatıyla platform kasasına satılır, ardından depozito tahsil edilir.
  const [pendingPaymentType, setPendingPaymentType] = useState<'CASH' | 'POS' | 'PARTIAL' | 'OTHER_TICKET' | null>(null);
  const [pendingPosArgs, setPendingPosArgs] = useState<{ bankAccountId: number; bankSubAccountId: number | null; bankAccountName: string; useFiscalDevice: boolean } | null>(null);
  const [prePaymentScannedCards, setPrePaymentScannedCards] = useState<any[]>([]);
  const prePaymentScannedCardsRef = useRef<any[]>([]);
  const depositPaymentMethodRef = useRef<'CASH' | 'POS'>('CASH');
  // Kulüp girişi: bu işlemde kasiyer "Kartsız Devam Et" dedi mi? (kart/depozito atlanır,
  // normal hızlı satış gibi tamamlanır). Her ödeme tetiklendiğinde sıfırlanır.
  const skipCardForCurrentSaleRef = useRef<boolean>(false);

  // ✅ KULÜP GİRİŞİ — AYRI DEPOZİTO TAHSİLATI (2 adımlı akış):
  //   1) Önce ana (giriş/hizmet) ödemesi alınır.
  //   2) Ardından depozito AYRI bir satış olarak tahsil edilir (kart 2. kez okutulur).
  //   Çıkışta depozito, kendi satış no'su üzerinden TAM İPTAL (CancelSale) ile iade edilir.
  // `clubEntryDepositPending`: ana ödeme alındı, "Depozito tahsilatı bekleniyor" ekranı açık.
  const [clubEntryDepositPending, setClubEntryDepositPending] = useState(false);
  // Ana ödeme + depozito tahsilatı arasındaki köprü bilgisi.
  const [pendingDepositInfo, setPendingDepositInfo] = useState<{
    cards: any[];
    depositMethod: 'CASH' | 'POS';
    totalDeposit: number;
    mainReceiptId: number;
    mainReferenceNumber: string;
    // 'OTHER': dış bilet platformu (FIRSAT ME / BU BİLET) ile ödenmiş satış.
    mainPaymentMethod: 'CASH' | 'POS' | 'PARTIAL' | 'OTHER';
    // Dış bilet için gerçek platform etiketi (assignCards.paymentMethod'a taşınır).
    mainPaymentLabel?: string;
    mainPavoPaySaleNumber?: string;
    mainAmount: number;
  } | null>(null);
  const pendingDepositInfoRef = useRef<typeof pendingDepositInfo>(null);
  // Depozito tahsilatı / iptali sürerken modal butonlarını kilitlemek için.
  const [clubEntryDepositBusy, setClubEntryDepositBusy] = useState(false);
  // ✅ KULÜP GİRİŞİ — seçilen dış bilet platformu (FIRSAT ME / BU BİLET) bilgisi.
  //   Kart okutma onaylanınca bu kasa/etikete satış (processOtherPaymentWithInvoice) yapılır.
  const externalTicketOptionRef = useRef<any>(null);

  // ✅ YENİ: Mutfak fişi yazdırma servisi (Restaurant modu için)
  const { printKitchenReceipt, printKitchenReceiptByCategory } = useKitchenPrint({
    companyId,
    screenId: currentScreen?.id || null,
    screenSettings,
    onPrintError: (error) => {
      console.error('[QuickSaleScreen] Mutfak fişi yazdırma hatası:', error);
      showSnackbar(error, 'error');
    },
    onPrintSuccess: (message) => {
      console.log('[QuickSaleScreen] Mutfak fişi yazdırıldı:', message);
      // Başarı mesajı zaten handlePrintToKitchen'da gösteriliyor
    },
  });

  // ✅ YENİ: Component unmount olduğunda parçalı ödeme geçmişini temizle
  useEffect(() => {
    return () => {
      // Cleanup: Ekran kapandığında localStorage temizle
      if (companyId && currentScreen?.id) {
        const partialPaymentKey = `partialPayment_${companyId}_${currentScreen.id}`;
        localStorage.removeItem(partialPaymentKey);
        console.log('[QuickSaleScreen] Component unmount - Parçalı ödeme geçmişi temizlendi');
      }
    };
  }, [companyId, currentScreen?.id]);

  // ✅ F5: Rest-P2P aktiflik kontrolu (modal acma kosullari icin)
  const isRestP2PEnabled = Boolean(
    (screenSettings as any)?.useRestP2PForPayments && (screenSettings as any)?.pavoRestP2PDeviceScreenCode
  );

  // Kartli odeme aracisini (Pavo PaymentMediatorId) global store'a yaz.
  // pavopay.ts helper'lari kart bacagini bu degerle gonderir; ayar yoksa
  // BKM/TechPOS varsayilani (2) kullanilir. Albaraka BKM'de olmadigi icin
  // Albaraka POS'lu sirketler ekran ayarindan AlbarakaPOS (38) secer.
  useEffect(() => {
    setPavoCardMediator(
      (screenSettings as any)?.pavoPaymentMediatorId ?? DEFAULT_PAVO_CARD_MEDIATOR
    );
  }, [(screenSettings as any)?.pavoPaymentMediatorId]);

  // ✅ F5: Rest-P2P config'i global store'a yaz; pavopay.ts helper'lari
  //     bu config'i okuyarak iade/iptal/returnable sale cagrilarini
  //     local-service yerine Rest-P2P dalina yonlendirecek.
  useEffect(() => {
    const enabled = Boolean(
      (screenSettings as any)?.useRestP2PForPayments && (screenSettings as any)?.pavoRestP2PDeviceScreenCode
    );
    if (enabled) {
      setRestP2PConfig({
        enabled: true,
        deviceCode: (screenSettings as any)?.pavoRestP2PDeviceScreenCode ?? null,
        companyId: companyId ?? null,
        branchId: ((screenSettings as any)?.branchId ?? null) as string | null,
      });
    } else {
      clearRestP2PConfig();
    }
  }, [
    (screenSettings as any)?.useRestP2PForPayments,
    (screenSettings as any)?.pavoRestP2PDeviceScreenCode,
    (screenSettings as any)?.branchId,
    companyId,
  ]);

  // ─────────────────────────────────────────────────────────
  // 3.3 - UI & Discount Hooks (depends on cart)
  // ─────────────────────────────────────────────────────────
  
  /** UI Settings Hook */
  const {
    productCardSize,
    productCardFontSize,
    productCategoryTabSize,
    productImageSize,
    controlPanelPosition,
    showProductImages: showProductImagesUI, // ✅ YENİ: Renamed to avoid conflict
    showProductPrices: showProductPricesUI, // ✅ YENİ: Renamed to avoid conflict
    cartColumnWidth: cartColumnWidthSetting, // ✅ YENİ: Sepet sütunu genişlik ayarı (hook'tan)
    showAllCategory, // ✅ YENİ: "Tümü" kategorisi gösterilsin mi
    showSearchBar, // ✅ YENİ: Arama bölümü gösterilsin mi
    categoryLayout, // ✅ YENİ: Kategori düzeni (yatay/dikey)
    categoryPosition, // ✅ YENİ: Dikey modda kategori pozisyonu
    categoryFontSize, // ✅ YENİ: Kategori yazı boyutu
    isUISettingsModalOpen,
    setIsUISettingsModalOpen,
    handleOpenUISettingsModal,
    handleSaveUISettings,
  } = useQuickSaleUI();

  // ✅ YENİ: Paket modunda ayarlar fonksiyonunu parent'a expose et
  useEffect(() => {
    if (isPackageMode && onSettingsReady) {
      onSettingsReady(handleOpenUISettingsModal);
    }
  }, [isPackageMode, onSettingsReady, handleOpenUISettingsModal]);

  // Sütun genişliklerini hesapla (controlPanelPosition tanımlandıktan sonra)
  const actionPanelWidth = isActionPanelCollapsed ? 0.37 : 1.8; // Daraltılmış: 0.37 (44px), Genişletilmiş: 1.8
  
  // ✅ Sepet sütunu genişliği: Kullanıcı ayarından al (varsayılan 5)
  const cartColumnWidth = cartColumnWidthSetting || 5;
  
  // Kulüp girişi: Canlı kart izleme paneli görünürlüğü (ayar varsayılan AÇIK) ve genişliği
  const clubEntryMonitoringPanelEnabled = isClubEntryMode && clubEntrySettings?.cardMonitoringPanelEnabled !== false;
  const accessControlPanelWidth = clubEntryMonitoringPanelEnabled ? 1.8 : 0;
  
  // Ürünler sütunu genişliği: Sepet ve butonlar hariç kalan alan (daraltma kaldırıldı)
  const productsColumnWidth = 12 - cartColumnWidth - actionPanelWidth - accessControlPanelWidth;

  // Kulüp girişi: Geçiş hakkı sorgu sonuçları (kart izleme panelinde gösterilir)
  const [accessControlEntryResults] = useState<Record<string, CardScanEntryResult>>({});

  // Local-service'e otomatik geçiş kontrolü modunu etkinleştir
  useEffect(() => {
    const lsUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';
    const entryMode = clubEntrySettings?.entryMode || 'SINGLE_ENTRY';
    const screenId = currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined;

    if (!isClubEntryMode) {
      fetch(`${lsUrl}/api/club-entry/disable-auto`, { method: 'POST' }).catch(() => {});
      return;
    }

    const enableAuto = async () => {
      try {
        const jwtToken = await ownAuthSession.getAccessToken();
        if (!jwtToken) return;

        const res = await fetch(`${lsUrl}/api/club-entry/enable-auto`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            salesApiUrl: SALESAPI_URL,
            accountingApiUrl: ACCOUNTINGAPI_URL,
            jwtToken,
            companyId,
            entryMode,
            screenId,
          }),
        });
        const data = await res.json();
        console.log('[ERİŞİM KONTROL] Local-service auto mode:', data);
      } catch (err) {
        console.warn('[ERİŞİM KONTROL] Auto mode enable hatası:', err);
      }
    };

    enableAuto();

    // Periyodik kontrol: local-service yeniden başlatıldıysa enable-auto'yu tekrar gönder + JWT yenile
    const healthInterval = setInterval(async () => {
      try {
        const statusRes = await fetch(`${lsUrl}/api/club-entry/status`);
        const statusData = await statusRes.json();
        if (!statusData.enabled || statusData.entryMode !== entryMode || !statusData.hasToken) {
          console.log('[ERİŞİM KONTROL] Local-service state eksik/uyumsuz, yeniden enable-auto gönderiliyor...', statusData);
          await enableAuto();
          return;
        }
        // JWT token yenile (1 saatte expire olur)
        const jwtToken = await ownAuthSession.getAccessToken();
        if (!jwtToken) return;
        await fetch(`${lsUrl}/api/club-entry/update-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jwtToken, companyId, entryMode }),
        });
      } catch {
        // Local-service kapalı olabilir, sessizce devam et
      }
    }, 30 * 1000);

    return () => {
      clearInterval(healthInterval);
      fetch(`${lsUrl}/api/club-entry/disable-auto`, { method: 'POST' }).catch(() => {});
    };
  }, [isClubEntryMode, clubEntrySettings?.entryMode, currentScreen?.id, screenSettings?.localServiceUrl, companyId]);

  // Geçiş kontrolü artık local-service tarafında otomatik yapılıyor.
  // Bu callback sadece frontend'de yeni kart olayını loglar.
  // Sonuçlar local-service'den entryResults olarak gelir ve AccessControlLivePanel'de gösterilir.
  const handleAccessControlCardScan = useCallback(async (event: CardScanEvent) => {
    console.log('[ERİŞİM KONTROL] 🎫 Kart okutuldu (local-service işleyecek):', {
      cardSN: event.cardSN,
      doorId: event.doorId,
      passTime: event.passTime,
    });
  }, []);

  /** Snackbar helper (passed to other hooks) */
  const showSnackbar = (message: string, severity: AlertColor = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // ─────────────────────────────────────────────────────────
  // 3.4 - Modal Management (depends on cart)
  // ─────────────────────────────────────────────────────────
  const modalsHook = useQuickSaleModals({
    clearCart: clearCartHook
  });
  const {
    alertModalOpen,
    alertModalTitle,
    alertModalMessage,
    isCriticalError,
    showQuickAddButton, // ✅ YENİ: Hızlı stok kaydı butonu kontrolü
    alertModalRetryCallback,
    showAlertModal,
    showCriticalErrorModal,
    handleCloseAlertModal,
    handleCriticalErrorClearCart,
    isQuickAddItemModalOpen,
    setIsQuickAddItemModalOpen,
    handleOpenQuickAddItemModal,
    handleCloseQuickAddItemModal,
    isBulkAddAccountModalOpen,
    setIsBulkAddAccountModalOpen,
    handleOpenBulkAddAccountModal,
    handleCloseBulkAddAccountModal,
    isPartialPaymentModalOpen,
    setIsPartialPaymentModalOpen,
    handleOpenPartialPaymentModal,
    handleClosePartialPaymentModal: handleClosePartialPaymentModalBase,
  } = modalsHook;

  // ─────────────────────────────────────────────────────────
  // 3.4.1 - Okutulduğu hâlde sepete eklenemeyen ürünlerin uyarıları
  // Engelleyici modal yerine ses + sepetteki kalıcı şerit; böylece kasiyer
  // ekrana bakmadan okutmaya devam edebilir ve sonraki ürünler sepete girer.
  // ─────────────────────────────────────────────────────────
  const {
    alerts: scanAlerts,
    pushScanAlert,
    dismissAlert: dismissScanAlert,
    clearAlerts: clearScanAlerts,
    isListOpen: isScanAlertListOpen,
    openList: openScanAlertList,
    closeList: closeScanAlertListBase,
    quickAddBarcode,
    setQuickAddBarcode,
  } = useScanAlerts();

  // ─────────────────────────────────────────────────────────
  // ✅ PAKET MODU: Ödeme yöntemi seçildikten sonra sipariş oluştur veya ilave et
  // ─────────────────────────────────────────────────────────
  const handlePackagePaymentSelect = async (paymentMethod: string) => {
    if (!pendingPackageOrderData) {
      showAlertModal('Hata', 'Sipariş bilgisi bulunamadı.');
      return;
    }

    const { orderDto } = pendingPackageOrderData;
    
    // PaymentMethod'u DTO'ya ekle
    orderDto.PaymentMethod = paymentMethod;
    
    try {
      // ✅ İLAVE SİPARİŞ MODU: addOnOrderId varsa mevcut siparişe ürün ekle
      if (addOnOrderId && companyId) {
        console.log('📦 [İLAVE SİPARİŞ] Mevcut siparişe ürün ekleniyor:', { 
          orderId: addOnOrderId, 
          itemCount: orderDto.Items.length,
          items: orderDto.Items.map(i => ({ name: i.ProductName, qty: i.Quantity, price: i.UnitPrice, subTotal: i.SubTotal, totalAmount: i.TotalAmount, taxRate: i.TaxRate, taxAmount: i.TaxAmount })),
          orderDtoTotalAmount: orderDto.TotalAmount
        });
        
        const result = await addItemsToPackageOrder(
          addOnOrderId,
          orderDto.Items,
          quickSaleUserData?.uid,
          quickSaleUserData?.email || currentCashier?.cashierName
        );
        
        if (result.Success) {
          console.log('✅ [İLAVE SİPARİŞ] Ürünler başarıyla eklendi:', result);
          showAlertModal('Başarılı', `İlave ürünler eklendi!\nSipariş No: ${result.OrderNumber}\nEklenen Ürün: ${orderDto.Items.length} adet`);
          
          // Sepeti temizle
          setCart([]);
          setSelectedCartItemId(null);
          
          // Modal'ı kapat ve pending data'yı temizle
          setPackagePaymentModalOpen(false);
          setPendingPackageOrderData(null);
          
          // onPackageOrderComplete callback'ini çağır (varsa) — ilave modu
          // olduğunu ve hangi item'ların YENİ eklendiğini parent'a iletiyoruz
          // ki mutfak fişine sadece bu kalemler basılsın (delta print).
          if (onPackageOrderComplete && result.OrderId) {
            onPackageOrderComplete(result.OrderId, {
              addedItemIds: result.AddedItemIds,
              isAdditionalOrder: true,
            });
          }
        } else {
          console.error('❌ [İLAVE SİPARİŞ] Ürünler eklenemedi:', result.Message);
          showAlertModal('Hata', result.Message || 'Ürünler eklenemedi.');
        }
      } else {
        // ✅ NORMAL PAKET SİPARİŞ MODU: Yeni sipariş oluştur
        console.log('📦 Sipariş oluşturuluyor (Ödeme: ' + paymentMethod + '):', orderDto);
        
        const result = await createPackageOrder(orderDto);
        
        if (result.Success) {
          console.log('✅ Sipariş başarıyla oluşturuldu:', result);
          showAlertModal('Başarılı', `Sipariş oluşturuldu!\nSipariş No: ${result.OrderNumber}\nKısa Kod: ${result.ShortCode}\nÖdeme: ${paymentMethod === 'CASH' ? 'Nakit' : paymentMethod === 'POS' ? 'POS' : paymentMethod}`);
          
          // Sepeti temizle
          setCart([]);
          setSelectedCartItemId(null);
          
          // Modal'ı kapat ve pending data'yı temizle
          setPackagePaymentModalOpen(false);
          setPendingPackageOrderData(null);
          
          // onPackageOrderComplete callback'ini çağır (varsa)
          if (onPackageOrderComplete && result.OrderId) {
            onPackageOrderComplete(result.OrderId);
          }
        } else {
          console.error('❌ Sipariş oluşturulamadı:', result.Message);
          showAlertModal('Hata', result.Message || 'Sipariş oluşturulamadı.');
        }
      }
    } catch (error: any) {
      console.error('❌ Sipariş hatası:', error);
      showAlertModal('Hata', error.message || 'Beklenmeyen bir hata oluştu.');
    }
  };

  // ─────────────────────────────────────────────────────────
  // 3.5 - Customer Management (depends on data & modals)
  // ─────────────────────────────────────────────────────────
  const customersHook = useQuickSaleCustomers({
    companyId,
    selectedCustomer,
    setSelectedCustomer,
    showAlertModal,
    isPackageMode, // ✅ YENİ: Paket modunda "Genel Cari" otomatik seçimi yapma
  });
  const {
    customers,
    generalCariRef,
    loadingCustomers,
    customerError,
    calculateBalance,
    refreshCustomers,
    isCustomerSelectionModalOpen,
    setIsCustomerSelectionModalOpen,
    handleOpenCustomerSelectionModal,
    handleCloseCustomerSelectionModal,
    handleSelectCustomerFromModal,
    allowAccountCreationOnSale,
    setAllowAccountCreationOnSale
  } = customersHook;

  // ═══════════════════════════════════════════════════════════
  // 🔹 PAKET MODU: Müşteri otomatik seçimi
  // ═══════════════════════════════════════════════════════════
  
  /**
   * ✅ PAKET MODU: packageCustomer varsa cariyi otomatik seç
   * PackageOrderScreen'den gelen müşteri bilgisi ile cari hesap seçilir
   */
  useEffect(() => {
    const loadPackageCustomer = async () => {
      if (!isPackageMode || !packageCustomer || !companyId) {
        return;
      }
      
      // packageCustomer.id ile cariyi çek
      if (packageCustomer.id && packageCustomer.id > 0) {
        try {
          console.log('📦 [PAKET MODU] Cari hesap yükleniyor... ID:', packageCustomer.id);
          const accountData = await accountingService.getAccountById(packageCustomer.id);
          
          console.log('🔍 [PAKET MODU] API\'den gelen accountData:', accountData);
          console.log('🔍 [PAKET MODU] accountData keys:', accountData ? Object.keys(accountData) : 'null');
          
          if (accountData) {
            // Customer tipine dönüştür
            const rawAccount = accountData as any;
            
            // ID alanını bul - farklı naming convention'lar için kontrol
            const accountId = rawAccount.Id || rawAccount.id || rawAccount.ID || packageCustomer.id;
            console.log('🔍 [PAKET MODU] ID kontrolü:', { 
              'rawAccount.Id': rawAccount.Id, 
              'rawAccount.id': rawAccount.id,
              'rawAccount.ID': rawAccount.ID,
              'packageCustomer.id': packageCustomer.id,
              'kullanılacak accountId': accountId
            });
            
            const packageCustomerAccount: Customer = {
              id: accountId?.toString() || '',
              companyName: accountData.CompanyName || rawAccount.companyName || packageCustomer.name,
              balance: accountData.CreditBalance || rawAccount.creditBalance || undefined,
              debitBalance: accountData.DebitBalance || rawAccount.debitBalance || 0,
              creditBalance: accountData.CreditBalance || rawAccount.creditBalance || 0,
              mobilePhone: accountData.MobilePhone || rawAccount.mobilePhone || packageCustomer.phone,
              email: accountData.Email || rawAccount.email || '',
              isActive: (accountData.IsActive !== false) && (rawAccount.isActive !== false),
              taxNumber: accountData.TaxNumber || rawAccount.taxNumber || undefined,
              taxOffice: accountData.TaxOffice || rawAccount.taxOffice || undefined,
              address: packageCustomer.address, // Adres paket müşterisinden gelir
            };
            
            setSelectedCustomer(packageCustomerAccount);
            console.log('✅ [PAKET MODU] Cari hesap seçildi:', packageCustomerAccount.companyName);
          } else {
            // Cari bulunamadıysa packageCustomer bilgileriyle oluştur
            console.warn('⚠️ [PAKET MODU] Cari hesap bulunamadı, packageCustomer bilgileri kullanılacak');
            const fallbackCustomer: Customer = {
              id: packageCustomer.id.toString(),
              companyName: packageCustomer.name,
              mobilePhone: packageCustomer.phone,
              address: packageCustomer.address,
            };
            setSelectedCustomer(fallbackCustomer);
          }
        } catch (error) {
          console.error('❌ [PAKET MODU] Cari hesap yüklenirken hata:', error);
          // Hata durumunda packageCustomer bilgileriyle devam et
          const fallbackCustomer: Customer = {
            id: packageCustomer.id.toString(),
            companyName: packageCustomer.name,
            mobilePhone: packageCustomer.phone,
            address: packageCustomer.address,
          };
          setSelectedCustomer(fallbackCustomer);
        }
      } else {
        // ID yok, packageCustomer bilgileriyle cari oluştur
        console.log('📦 [PAKET MODU] Müşteri ID yok, packageCustomer bilgileri kullanılıyor');
        const newCustomer: Customer = {
          id: '0',
          companyName: packageCustomer.name || 'Paket Müşterisi',
          mobilePhone: packageCustomer.phone,
          address: packageCustomer.address,
        };
        setSelectedCustomer(newCustomer);
      }
    };
    
    loadPackageCustomer();
  }, [isPackageMode, packageCustomer, companyId, setSelectedCustomer]);

  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 4: DERIVED VALUES & HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════
  
  /**
   * Calculate cart total
   * Wrapper around cart hook's calculateTotal
   */
  const calculateTotal = () => {
    return calculateTotalHook();
  };

  /**
   * Get user full name for display
   */
  const userFullName = quickSaleUserData
    ? `${quickSaleUserData.firstName} ${quickSaleUserData.lastName}`.trim()
    : 'Kullanıcı';

  /**
   * ✅ YENİ: Diğer ödeme seçeneklerini screenSettings'ten al
   */
  const otherPaymentOptions = useMemo(() => {
    if (!screenSettings?.paymentOptions) {
      console.log('🔴 [OTHER PAYMENT] screenSettings.paymentOptions yok');
      return [];
    }
    const paymentOptionsObj = screenSettings.paymentOptions as Record<string, any>;
    const result = Object.entries(paymentOptionsObj)
      .filter(([, opt]) => opt.type === 'other' && opt.enabled)
      .map(([id, opt]) => {
        const label = opt.label?.toUpperCase() === 'SODEXO' ? 'PLUXEE' : opt.label;
        return { id, ...opt, label };
      });
    console.log('🟢 [OTHER PAYMENT] Bulunan diğer ödeme sayısı:', result.length, result);
    return result;
  }, [screenSettings?.paymentOptions]);

  /** ✅ YENİ: Online ödeme yöntemlerini filtrele */
  const onlinePaymentOptions = useMemo(() => {
    if (!screenSettings?.paymentOptions) {
      console.log('🔴 [ONLINE PAYMENT] screenSettings.paymentOptions yok');
      return [];
    }
    const paymentOptionsObj = screenSettings.paymentOptions as Record<string, any>;
    const result = Object.entries(paymentOptionsObj)
      .filter(([, opt]) => opt.type === 'online' && opt.enabled)
      .map(([id, opt]) => ({ id, ...opt }));
    console.log('🟢 [ONLINE PAYMENT] Bulunan online ödeme sayısı:', result.length, result);
    return result;
  }, [screenSettings?.paymentOptions]);

  /**
   * Calculate cart item count
   */
  const totalCartItems = cart.reduce((total, item) => total + item.quantity, 0);

  // ═══════════════════════════════════════════════════════════════════════════════
  // MÜŞTERİ EKRANI (CUSTOMER DISPLAY) - BroadcastChannel Entegrasyonu
  // Logic → useCustomerDisplay hook'una taşındı (hooks/useCustomerDisplay.ts)
  // ═══════════════════════════════════════════════════════════════════════════════
  // Not: Dönen customerDisplayChannelRef / notifyCustomerDisplaySaleComplete ekranda
  // kullanılmıyordu (orijinalde de çağrılmıyordu); effect'ler hook içinde çalışıyor.
  useCustomerDisplay({
    currentScreenId: currentScreen?.id,
    companyId,
    isPackageMode,
    isRestaurantMode,
    cart,
    calculateTotalHook,
  });

  /**
   * Format currency for display
   */
  const formatCurrency = (amount: number) => {
    const absoluteAmount = Math.abs(amount);
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absoluteAmount);
  };

  /**
   * ✅ YENİ: Barkod arama bölümüne fokusla (Sadece normal hızlı satış modunda)
   * Restaurant modunda fokus yok çünkü arama bölümü yok
   */
  const focusSearchBar = useCallback(() => {
    console.log('🎯🎯🎯 [FOCUS] focusSearchBar çağrıldı!', {
      isRestaurantMode,
      searchBarRefExists: !!searchBarRef.current
    });
    
    if (!isRestaurantMode && searchBarRef.current) {
      setTimeout(() => {
        console.log('🎯🎯🎯 [FOCUS] setTimeout içinde, fokuslanıyor...');
        searchBarRef.current?.focus();
        console.log('🎯🎯🎯 [FOCUS] Barkod arama bölümüne fokuslandı!');
      }, 100);
    } else {
      console.log('🎯🎯🎯 [FOCUS] Fokus YAPILMADI! Sebep:', {
        isRestaurantMode: isRestaurantMode ? 'Restaurant modu aktif' : 'Restaurant modu değil',
        searchBarRef: searchBarRef.current ? 'Var' : 'YOK!'
      });
    }
  }, [isRestaurantMode]);

  /**
   * Uyarı listesi kapanınca odak barkod kutusuna döner; kasiyer listeyi
   * kapattığı anda okutmaya devam edebilsin.
   */
  const closeScanAlertList = useCallback(() => {
    closeScanAlertListBase();
    focusSearchBar();
  }, [closeScanAlertListBase, focusSearchBar]);

  // ─────────────────────────────────────────────────────────
  // 3.6 - Discount Management (depends on cart & modals & focusSearchBar)
  // ─────────────────────────────────────────────────────────
  const discountHook = useQuickSaleDiscount({
    cart,
    setCart,
    showAlertModal,
    focusSearchBar, // ✅ YENİ: Modal kapandığında fokuslanma
  });
  const {
    isDiscountModalOpen,
    discountInputPercentage,
    discountInputAmount,
    lastAppliedDiscountAmount,
    appliedDiscountType,
    setDiscountInputPercentage,
    setDiscountInputAmount,
    handleOpenDiscountModal,
    handleCloseDiscountModal,
    handleApplyDiscount,
    handleApplyQuickDiscount,
    handleRemoveDiscount,
  } = discountHook;

  // ─────────────────────────────────────────────────────────
  // 3.7 - Product Operations (depends on data, discount, snackbar)
  // ─────────────────────────────────────────────────────────
  // ✅ ŞUBE ID: Restoran modunda restaurantBranchId, normal modda ekranın branchId'si
  // Öncelik: 1) Restoran şubesi 2) Ekran şubesi 3) Kullanıcı şubesi
  const activeBranchId = isRestaurantMode 
    ? restaurantBranchId 
    : (currentScreen?.branchId || quickSaleUserData?.branchesId || null);

  // ✅ Şubenin deposu: allWarehouses içinde branchesId eşleşen ilk depo
  // null ise mevcut davranış korunur (stockVisibility ayarlarından depo belirlenir)
  const branchWarehouseId = useMemo(() => {
    if (!activeBranchId) return null;
    return allWarehouses.find(w => w.branchesId === activeBranchId)?.id ?? null;
  }, [activeBranchId, allWarehouses]);

  const productsHook = useQuickSaleProducts({
    allInventoryItems,
    allCategoriesData,
    allWarehouses,
    screenSettings,
    companyId,
    showProductImages,
    handleRemoveDiscount,
    showSnackbar,
    setTempSelectedProduct,
    setIsWeightInputModalOpen,
    setCart,
    focusSearchBar, // ✅ YENİ: Kategori değiştirilince fokuslan
    branchId: activeBranchId, // ✅ YENİ: Şube bazlı fiyatlandırma için şube ID
    // ✅ YENİ: Menü opsiyon modalı için
    setPendingMenuProduct,
    setIsMenuOptionModalOpen,
    showAllCategory, // ✅ YENİ: "Tümü" kategorisi gösterilsin mi
    isPackageMode, // ✅ YENİ: Paket modu için packagePrice kullanılacak
    // ✅ YENİ: Hızlı Satış "Şube Paket Fiyatı Kullan" ayarı (StockVisibilityJson.usePackagePrices)
    usePackagePrices: screenSettings?.stockVisibility?.usePackagePrices === true,
    // ✅ YENİ: Kulüp girişi paket ürünleri
    screenId: currentScreen?.id ? parseInt(currentScreen.id, 10) : null,
    isClubEntryMode,
  });
  const {
    selectedCategory,
    setSelectedCategory,
    displayableCategories,
    filteredProducts,
    addToCart,
    addToCartWithOptions, // ✅ YENİ: Opsiyonlu menü için
    productImagesCache,
    selectedWarehouseIds,
    setSelectedWarehouseIds,
    activeDisplayWarehouseId,
    setActiveDisplayWarehouseId,
    handleCategoryChange,
    loadAndCacheProductImages,
    sqlInventoryItems,
    getScreenCategoryId,
    resolveBranchPrice, // ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix)
  } = productsHook;

  // ─────────────────────────────────────────────────────────
  // 3.8 - Pending Sales (depends on cart & customer)
  // ─────────────────────────────────────────────────────────
  const pendingHook = useQuickSalePending({
    cart,
    selectedCustomer,
    clearCart: clearCartHook,
    setCart,
    setSelectedCustomer,
    showSnackbar,
  });
  const {
    pendingSales,
    isPendingSalesModalOpen,
    setIsPendingSalesModalOpen,
    handleHoldSale,
    handleOpenPendingSalesModal,
    handleRestoreSale,
    handleDeleteSale,
  } = pendingHook;

  /**
   * Clear cart wrapper - also removes discount and resets customer
   * Must be defined before usePayment hook
   */
  const clearCart = () => {
    handleRemoveDiscount();
    clearCartHook();

    // Kulüp girişi: sepet temizlendiğinde kart okutma verilerini sıfırla
    // Böylece yeni ödeme denemesinde kart okutma modalı tekrar açılır
    if (prePaymentScannedCards.length > 0 || prePaymentScannedCardsRef.current.length > 0) {
      setPrePaymentScannedCards([]);
      prePaymentScannedCardsRef.current = [];
      depositPaymentMethodRef.current = 'CASH';
      setPendingPaymentType(null);
      setPendingPosArgs(null);
      console.log('[KULÜP GİRİŞİ] Sepet temizlendi, kart okutma verileri sıfırlandı');
    }

    // ✅ YENİ: Fiş notu temizle (ödeme sonrası)
    setCustomerNote('');

    // ✅ YENİ: Parçalı ödeme geçmişini temizle (localStorage)
    if (companyId && currentScreen?.id) {
      const partialPaymentKey = `partialPayment_${companyId}_${currentScreen.id}`;
      localStorage.removeItem(partialPaymentKey);
      console.log('[QuickSaleScreen] Parçalı ödeme geçmişi temizlendi');
    }

    // Reset customer to general customer after sale
    if (selectedCustomer && selectedCustomer.id !== DEFAULT_CUSTOMER.ID && generalCariRef) {
      setSelectedCustomer(generalCariRef);
      debugLog("Satış sonrası müşteri Genel Cari'ye döndürüldü.");
    }

    if (prepaidSystemEnabled) {
      setPrepaidCard(null);
      setSelectedPrepaidWalletId(null);
      const cashierRequired = !isRestaurantMode && screenSettings?.useCashierSystem;
      if (!cashierRequired || currentCashier) {
        setTimeout(() => {
          setIsPrepaidCardSearchOpen(true);
        }, LAYOUT.TIMEOUTS.MODAL_DELAY);
      }
    } else {
      focusSearchBar();
    }
  };

  // ─────────────────────────────────────────────────────────
  // 3.8.1 - Restaurant Cart Operations Hook (REFACTORED)
  // ─────────────────────────────────────────────────────────
  // Sentinel/nonce: hook'tan "son ürün silindi" sinyali geldiğinde CartSection
  // içindeki iptal nedeni modalını dışarıdan tetiklemek için. Her artırma yeni
  // bir referans değeri üretir, useEffect karşılığı modal'ı açar (ilk render'da
  // 0 olduğu için tetiklenmez).
  const [cancelReasonTriggerNonce, setCancelReasonTriggerNonce] = useState(0);
  const requestCloseTableForLastItemRemoval = useCallback(() => {
    setCancelReasonTriggerNonce(n => n + 1);
  }, []);

  // Ref pattern: Cart hook (line ~2076), Kitchen hook'tan (line ~2220) ÖNCE
  // çağrıldığı için Kitchen hook'un `printCancellationForRemovedItem`
  // fonksiyonu Cart hook'a doğrudan geçilemez (henüz tanımlı değil). Bu
  // wrapper stable kalır; ref aşağıdaki useEffect ile Kitchen hook çağrısından
  // sonra senkronize edilir. Cart hook silme/azaltma anında bu wrapper'ı
  // çağırır → ref aracılığıyla Kitchen hook'taki gerçek fonksiyona ulaşır.
  const printCancellationRef = useRef<((params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => Promise<void>) | null>(null);
  const printCancellationForRemovedItemViaRef = useCallback(
    (params: { item: CartItem; cancelledQuantity: number; previousQuantity: number; currentQuantity: number; }) => {
      return printCancellationRef.current?.(params) ?? Promise.resolve();
    },
    []
  );

  const {
    removeFromCartWithRestaurant,
    updateQuantityWithRestaurant,
    clearCartWithRestaurant,
    handleIncreaseSelectedWithRestaurant,
    handleDecreaseSelectedWithRestaurant,
    handleDeleteSelectedWithRestaurant,
    addWaiterAction,
  } = useRestaurantCartOperations({
    isRestaurantMode,
    tableId: tableId || null,
    restaurantScreenId: restaurantScreenId || null,
    companyId,
    cart,
    setCart,
    showSnackbar,
    waiterName: waiterName || null,
    focusSearchBar,
    clearCart,
    selectedCartItemId,
    canDeletePrintedItems: waiterPermissions.canDeletePrintedItems,
    canRemoveProduct: waiterPermissions.canRemoveProduct,
    canDeleteUnprintedItems: waiterPermissions.canDeleteUnprintedItems,
    canCloseTable: waiterPermissions.canCloseTable,
    canCancelUnprintedReceipt: waiterPermissions.canCancelUnprintedReceipt,
    requestCloseTableForLastItemRemoval,
    printCancellationForRemovedItem: printCancellationForRemovedItemViaRef,
  });

  const handleDeleteWithReasonModal = useCallback(() => {
    console.log('┌══════════════════════════════════════════════════════════┐');
    console.log('║ 🗑️ handleDeleteWithReasonModal ÇAĞRILDI                  ║');
    console.log('└══════════════════════════════════════════════════════════┘');
    console.log('📋 [DEBUG] selectedCartItemId:', selectedCartItemId);
    console.log('📋 [DEBUG] cart.length:', cart.length);
    console.log('📋 [DEBUG] waiterPermissions:', JSON.stringify({
      canRemoveProduct: waiterPermissions.canRemoveProduct,
      canDeletePrintedItems: waiterPermissions.canDeletePrintedItems,
    }));
    console.log('📋 [DEBUG] waiterName:', waiterName);
    console.log('📋 [DEBUG] loginType:', loginType);

    if (selectedCartItemId) {
      const selectedItem = cart.find(item => item.cartItemId === selectedCartItemId);
      console.log('📋 [DEBUG] selectedItem bulundu mu?', !!selectedItem);
      if (selectedItem) {
        console.log('📋 [DEBUG] selectedItem detay:', JSON.stringify({
          cartItemId: selectedItem.cartItemId,
          id: selectedItem.id,
          name: selectedItem.name,
          sqlItemId: selectedItem.sqlItemId,
          quantity: selectedItem.quantity,
          printedQuantity: selectedItem.printedQuantity,
          price: selectedItem.price,
        }));
      }
      
      // Tüm sepeti logla (karşılaştırma için)
      console.log('📋 [DEBUG] Tüm sepet cartItemId listesi:', cart.map(item => ({
        cartItemId: item.cartItemId,
        name: item.name,
        sqlItemId: item.sqlItemId,
        printedQty: item.printedQuantity || 0,
      })));

      if (selectedItem && (selectedItem.printedQuantity || 0) > 0 && !waiterPermissions.canDeletePrintedItems) {
        console.warn('⛔ [SİLME YETKİ KONTROL] Yazdırılmış ürün silme ENGELLENDİ:', selectedItem.name);
        console.warn('⛔ [DEBUG] printedQuantity:', selectedItem.printedQuantity, '| canDeletePrintedItems:', waiterPermissions.canDeletePrintedItems);
        showSnackbar('Yetkiniz yok! Lütfen yetkisi olan personel ile oturum açın.', 'error');
        return;
      }
      // ✅ YENİ: Yazdırılmamış ürün — canRemoveProduct VEYA canDeleteUnprintedItems yeterli.
      // (Buton canDeleteUnprintedItems ile de aktifleştiği için burada savunma kontrolü.)
      if (
        selectedItem &&
        (selectedItem.printedQuantity || 0) === 0 &&
        !waiterPermissions.canRemoveProduct &&
        !waiterPermissions.canDeleteUnprintedItems
      ) {
        console.warn('⛔ [SİLME YETKİ KONTROL] Yazdırılmamış ürün silme ENGELLENDİ (yetki yok):', selectedItem.name);
        showSnackbar('Yetkiniz yok! Lütfen yetkisi olan personel ile oturum açın.', 'error');
        return;
      }
      console.log('✅ [DEBUG] Yetki kontrolü GEÇTİ, DeleteReasonModal açılıyor...');
      setDeleteReasonModalOpen(true);
    } else {
      console.warn('⚠️ [DEBUG] selectedCartItemId YOK, işlem iptal!');
    }
  }, [selectedCartItemId, cart, waiterPermissions.canDeletePrintedItems, waiterPermissions.canRemoveProduct, waiterPermissions.canDeleteUnprintedItems, showSnackbar, waiterName, loginType]);

  const handleDeleteReasonConfirm = useCallback((reason: string) => {
    console.log('🗑️ [DEBUG] handleDeleteReasonConfirm ÇAĞRILDI:', { selectedCartItemId, reason });
    if (selectedCartItemId) {
      console.log('🗑️ [DEBUG] removeFromCartWithRestaurant çağrılıyor:', selectedCartItemId);
      removeFromCartWithRestaurant(selectedCartItemId, reason);
      setDeleteReasonModalOpen(false);
    } else {
      console.warn('⚠️ [DEBUG] handleDeleteReasonConfirm: selectedCartItemId YOK!');
    }
  }, [selectedCartItemId, removeFromCartWithRestaurant]);

  const handleOpenItemNoteModal = useCallback(() => {
    if (!selectedCartItemId) return;
    const selectedItem = cart.find(item => item.cartItemId === selectedCartItemId);
    if (!selectedItem) return;
    setItemNoteCurrentNote(selectedItem.note || '');
    setItemNoteModalOpen(true);
  }, [selectedCartItemId, cart]);

  const handleItemNoteConfirm = useCallback(async (note: string) => {
    if (!selectedCartItemId) return;
    setCart(prev => prev.map(item =>
      item.cartItemId === selectedCartItemId
        ? { ...item, note: note || undefined }
        : item
    ));
    setItemNoteModalOpen(false);

    const selectedItem = cart.find(item => item.cartItemId === selectedCartItemId);
    if (selectedItem?.sqlItemId) {
      try {
        const { restaurantActiveSaleItemApi } = await import('../../../services/restaurantApiService');
        await restaurantActiveSaleItemApi.update(selectedItem.sqlItemId, { Notes: note || '' });
        console.log('✅ [AÇIKLAMA] Ürün notu güncellendi:', { sqlItemId: selectedItem.sqlItemId, note });
      } catch (err) {
        console.error('❌ [AÇIKLAMA] Not güncellenirken hata:', err);
        showSnackbar('Açıklama kaydedilirken hata oluştu.', 'error');
      }
    }
  }, [selectedCartItemId, cart, setCart, showSnackbar]);

  const scheduleRestaurantReturnAfterKitchenPrint = useCallback(() => {
    if (!isRestaurantMode || !restaurantScreenId) return;
    setKitchenReturnCountdownOpen(true);
  }, [isRestaurantMode, restaurantScreenId]);

  const handleKitchenReturnCountdownDismiss = useCallback(() => {
    setKitchenReturnCountdownOpen(false);
  }, []);

  const handleKitchenReturnCountdownTimeout = useCallback(() => {
    setKitchenReturnCountdownOpen(false);
    if (restaurantScreenId) {
      navigate(`/restaurant-screen/${restaurantScreenId}`);
    }
  }, [navigate, restaurantScreenId]);

  // ─────────────────────────────────────────────────────────
  // 3.8.2 - Restaurant Kitchen Operations Hook (REFACTORED)
  // ─────────────────────────────────────────────────────────
  const {
    handlePrintToKitchen,
    handlePrintCheck,
    handleBackButtonClick,
    handleContinueWithoutPrinting,
    handlePrintToKitchenAndGoBack,
    handlePrintCancellationAndGoBack,
    handlePrintBothAndGoBack,
    handleCloseTable,
    printCancellationForRemovedItem: printCancellationForRemovedItemFromHook,
  } = useRestaurantKitchenOperations({
    isRestaurantMode,
    useKitchenScreen,
    tableId: tableId || null,
    tableName: tableName || null,
    restaurantScreenId: restaurantScreenId || null,
    companyId,
    waiterName: waiterName || null,
    receiptWaiterName, // ✅ Adisyon fişine "masayı ilk açan garson" yazılması için override
    cart,
    clearCart,
    calculateTotal,
    sqlInventoryItems,
    allCategoriesData,
    screenSettings,
    currentScreen,
    quickSaleUserData,
    reservedLongCode,
    reservedShortCode,
    navigate,
    showSnackbar,
    setIsClosingTable,
    setUnprintedItemsList,
    setCancelledItemsList,
    setUnprintedItemsWarningOpen,
    setIsReceiptPrintedForCurrentTable,
    showKitchenPrintInfoModal: (printedItems, unprintedItems, skippedItems) => {
      setKitchenPrintInfoData({ printedItems, unprintedItems, skippedItems });
      setKitchenPrintInfoOpen(true);
      // Atlanan ürün varsa modali biraz daha uzun göster (kullanıcı görsün)
      const closeAfterMs = (skippedItems && skippedItems.length > 0) ? 5000 : 3000;
      setTimeout(() => setKitchenPrintInfoOpen(false), closeAfterMs);
    },
    showNoCategoryPrinterModal: (categoryNames, skippedItems) => {
      setNoCategoryPrinterModalData({ categoryNames, skippedItems });
      setNoCategoryPrinterModalOpen(true);
      // OTOMATİK kapanmaz — kullanıcı "Tamam" butonuna basmalı
    },
    cancelledItemsList,
    unprintedItemsList,
    addWaiterAction,
    printKitchenReceiptByCategory,
    lastAppliedDiscountAmount,
    appliedDiscountType,
    discountInputPercentage,
    salesService,
    setCart,
    onAfterSuccessfulKitchenPrint: scheduleRestaurantReturnAfterKitchenPrint,
    onAfterSuccessfulCheckPrint: scheduleRestaurantReturnAfterKitchenPrint,
    selectedCustomerName: selectedCustomer?.companyName || null,
  });

  // Ref senkronizasyonu: Kitchen hook çağrısından sonra `printCancellationRef`
  // güncel `printCancellationForRemovedItem` fonksiyonuna işaret eder. Cart
  // hook silme/azaltma anında stable wrapper üzerinden bu ref'e ulaşır.
  useEffect(() => {
    printCancellationRef.current = printCancellationForRemovedItemFromHook;
  }, [printCancellationForRemovedItemFromHook]);

  // ─────────────────────────────────────────────────────────
  // 3.8.3 - Restaurant Table Operations Hook
  // ─────────────────────────────────────────────────────────
  const {
    handleConfirmOpenTable,
    handleCancelOpenTable,
    handleOpenTransferTableModal,
    handleTransferTable,
    handleRequestMerge,
    handleConfirmMergeTables,
    handleCloseMergeTablesModal,
    handleCloseTransferTableModal,
  } = useRestaurantTableOperations({
    isRestaurantMode,
    tableId: tableId || null,
    tableName: tableName || null,
    restaurantScreenId: restaurantScreenId || null,
    restaurantBranchId: restaurantBranchId || null,
    companyId,
    waiterName: waiterName || null,
    pendingProduct,
    pendingQuantity,
    pendingMultiplier,
    mergeTargetTableId,
    mergeTargetTableName,
    mergeSourceItems,
    mergeTargetItems,
    isMergingTables,
    isTransferringTable,
    setCart,
    waiterPermissions,
    navigate,
    showSnackbar,
    showAlertModal,
    setIsOpeningTable,
    setConfirmOpenTableModalOpen,
    setPendingProduct,
    setPendingQuantity,
    setPendingMultiplier,
    setReservedLongCode,
    setReservedShortCode,
    setTransferTableModalOpen,
    setIsTransferringTable,
    setMergeTargetTableId,
    setMergeTargetTableName,
    setMergeSourceItems,
    setMergeTargetItems,
    setMergeTablesModalOpen,
    setIsMergingTables,
    addWaiterAction,
    resolveBranchPrice, // ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix)
  });

  // ─────────────────────────────────────────────────────────
  // 3.8.4 - Restaurant Numpad Operations Hook
  // ─────────────────────────────────────────────────────────
  const {
    handleNumpadConfirm,
    handleNumpadCancel,
    handleReceiptPrintedConfirm,
    handleReceiptPrintedConfirmCancel,
  } = useRestaurantNumpadOperations({
    isRestaurantMode,
    tableId: tableId || null,
    restaurantScreenId: restaurantScreenId || null,
    companyId,
    waiterName: waiterName || null,
    numpadProduct,
    setNumpadProduct,
    setNumpadModalOpen,
    isReceiptPrintedForCurrentTable,
    setIsReceiptPrintedForCurrentTable,
    receiptPrintedConfirmOpen,
    setReceiptPrintedConfirmOpen,
    setReceiptPrintedNoPermissionOpen,
    pendingProductForReceiptConfirm,
    setPendingProductForReceiptConfirm,
    pendingQuantityForReceiptConfirm,
    setPendingQuantityForReceiptConfirm,
    pendingActionTypeForReceiptConfirm,
    setPendingActionTypeForReceiptConfirm,
    setPendingProduct,
    setPendingQuantity,
    setPendingMultiplier,
    setConfirmOpenTableModalOpen,
    setCart,
    waiterPermissions,
    showSnackbar,
    addWaiterAction,
    resolveBranchPrice, // ✅ YENİ: Şube fiyatı garanti çözücü (race-condition fix)
  });

  // ─────────────────────────────────────────────────────────
  // 3.9 - Payment Hook (MUST BE LAST - depends on all above)
  // ─────────────────────────────────────────────────────────
  
  // ✅ FIX: useMemo ile optimize et - sonsuz re-render'ı önle
  const selectedFiscalDevice = useMemo(() => {
    return (screenSettings?.selectedFiscalDevice as FiscalDeviceType) || 'hugin_t300';
  }, [screenSettings?.selectedFiscalDevice]);

  const mealCardFiscalData = useMemo(() => {
    if (selectedFiscalDevice !== 'pavopay' || cart.length === 0) {
      return { products: [] as FiscalProductItem[], total: 0 };
    }
    const products: FiscalProductItem[] = cart.map((cartItem) => {
      // Ürün listesi sqlInventoryItems'tır; allInventoryItems kalıcı olarak boş kaldığı
      // için burada envanterden KDV/departman hiç çözülemiyor, fiş KDV0 çıkıyordu.
      const inv = sqlInventoryItems?.find((item: any) => item.id === cartItem.id);
      // Oran çözülemezse 0 yazmak fişi geçerli ama yanlış (KDV'siz) kestirir.
      // NaN bırakılıyor: render'da bir şey bozmuyor, ödeme anında yemek kartı
      // isteği kurulurken ürün adıyla birlikte hataya dönüşüyor ve fiş kesilmiyor.
      const vatRate = parseVatRate(inv?.vatRate ?? cartItem.vatRate) ?? NaN;
      const isVatIncluded = cartItem.vatStatus === 'included' || isRestaurantMode;
      const fiscalPrice = isVatIncluded ? cartItem.price : cartItem.price * (1 + vatRate / 100);
      return {
        id: cartItem.id,
        ...toPackageFiscalLine(cartItem, { name: cartItem.name, quantity: cartItem.quantity, price: fiscalPrice }),
        vatRate,
        departmentNo: inv?.departmentNo || 1,
        barcode: inv?.barcode || '',
      };
    });
    const total = parseFloat(calculateTotal().toFixed(2));
    return { products, total };
  }, [selectedFiscalDevice, cart, sqlInventoryItems, isRestaurantMode, calculateTotal]);

  /**
   * ✅ YENİ: Yeni sipariş numarası al (ödeme sonrası çağrılır)
   * usePayment hook'undan önce tanımlanmalı!
   */
  const refreshOrderNumber = useCallback(async () => {
    console.log(`🎫 [NUMARA] refreshOrderNumber çağrıldı - companyId: ${companyId}, isRestaurantMode: ${isRestaurantMode}, branchId: ${activeBranchId}`);
    
    if (!companyId || isRestaurantMode || !activeBranchId) {
      console.log(`🎫 [NUMARA] Erken dönüş - companyId yok, restoran modu veya şube henüz hazır değil`);
      return;
    }

    // ✅ DAYANIKLILIK: API hatasında 2 kez daha dene (500ms / 1500ms bekleme).
    // reserveOrderNumber throw etmez; hata durumunda success=false + fallback
    // numara döndürür. Fallback numara sıralı DEĞİLDİR (rastgele), bu yüzden
    // yalnızca tüm denemeler başarısız olursa son çare olarak kullanılır ve
    // kullanıcı snackbar ile uyarılır — numara sessizce takılı kalmaz.
    const RETRY_DELAYS_MS = [0, 500, 1500];
    let lastResponse: Awaited<ReturnType<typeof salesService.reserveOrderNumber>> | null = null;

    for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
      if (RETRY_DELAYS_MS[attempt] > 0) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
      }
      try {
        console.log(`🎫 [NUMARA] API'ye istek gönderiliyor... (BranchId: ${activeBranchId}, deneme: ${attempt + 1}/${RETRY_DELAYS_MS.length})`);
        const response = await salesService.reserveOrderNumber('QUICK_SALE', activeBranchId);
        console.log('🎫 [NUMARA] API yanıtı:', response);
        lastResponse = response;
        if (response.success !== false) {
          setReservedLongCode(response.longCode);
          setReservedShortCode(response.shortCode);
          console.log(`🎫 [NUMARA] ✅ Yeni numara set edildi: ${response.shortCode}`);
          return;
        }
        console.warn(`❌ [NUMARA] Deneme ${attempt + 1} başarısız (success=false, fallback numara döndü)`);
      } catch (err) {
        console.error(`❌ [NUMARA] Deneme ${attempt + 1} hata:`, err);
      }
    }

    // Tüm denemeler başarısız — son çare: fallback numarayı kullan + kullanıcıyı uyar
    if (lastResponse) {
      setReservedLongCode(lastResponse.longCode);
      setReservedShortCode(lastResponse.shortCode);
      console.warn(`⚠️ [NUMARA] Tüm denemeler başarısız — fallback numara kullanılıyor: ${lastResponse.shortCode}`);
    }
    showSnackbar('Yeni sipariş numarası alınamadı. Bağlantınızı kontrol edin — geçici bir numara kullanılacak.', 'warning');
  }, [companyId, isRestaurantMode, activeBranchId]);

  const {
    handleCashPaymentForQuickSale: hookHandleCashPayment,
    handlePosPaymentForQuickSale: hookHandlePosPayment,
    handleOpenAccountPayment: hookHandleOpenAccountPayment,
    handlePavoP2pPayment: hookHandlePavoP2pPayment,
    openPavoP2pSelect: hookOpenPavoP2pSelect,
    handlePavoP2pDeviceSelected: hookHandlePavoP2pDeviceSelected,
    pavoP2pSelectOpen,
    setPavoP2pSelectOpen,
    processPaymentAfterPartialModal: hookProcessPaymentAfterPartial,
    isProcessingPayment,
    pavoP2pPaymentPending,
    activePavoPayment,
    cancelActivePavoPayment,
    isProcessingPosPayment, // Sadece POS için modal
    setIsProcessingPosPayment, // ✅ Parçalı ödeme modalı için
    // POS hata modalı
    posPaymentError,
    isPosErrorModalOpen,
    posRetryCallback,
    posContinueCallback,
    handleClosePosErrorModal,
    handleClosePosErrorModalAndClearCart,
    // ✅ YENİ: Yazarkasa bağlı değil onay modalı (POS)
    isFiscalDisconnectedConfirmOpen,
    handleFiscalDisconnectedConfirm,
    handleFiscalDisconnectedCancel,
    // ✅ YENİ (Ingenico): Yazarkasa hatası "Yine de Devam Et" onayı (NAKİT)
    isFiscalErrorContinueOpen,
    fiscalErrorContinueMessage,
    handleFiscalErrorContinueConfirm,
    handleFiscalErrorContinueCancel,
    // ✅ YENİ: Yazarkasa bağlı değil onay modalı (KARMA ÖDEME)
    isFiscalDisconnectedConfirmOpenPartial,
    handleFiscalDisconnectedConfirmPartial,
    handleFiscalDisconnectedCancelPartial,
    // ✅ YENİ: Fiş yazdırma (Online ve Diğer ödemeler için)
    printReceiptAfterPayment,
    // ✅ Rest-P2P (pavo_rest_p2p) bekleme modalı
    restP2PModalOpen,
    restP2PStatus,
    restP2PMode,
    restP2PTotal,
    restP2PParts,
    restP2PProgress,
    restP2PMessage,
    restP2PErrorCode,
    restP2PDeviceName,
    handleRestP2PCancel,
    handleRestP2PClose,
    handleRestP2PContinueWithoutDevice,
    runRestP2PFlow,
    getLastRestP2PSaleNumber,
  } = usePayment({
    cart,
    companyId,
    companyName: companyNameCache, // ✅ YENİ: Cache'lenmiş şirket adı
    seller, // ✅ PR2: Backend'in çözdüğü satıcı snapshot (fiş başlığı için)
    selectedCustomer,
    allInventoryItems: sqlInventoryItems,  // ✅ SQL'den çekilen ürünleri gönder
    userDetails: quickSaleUserData,
    currentUserDisplayName: userFullName,
    cashPaymentCashboxId,
    cashPaymentCashboxName: cashPaymentCashboxName || cashPaymentLabel, // ✅ YENİ: Kasa adı (gün sonu raporu için)
    posOptions,
    showAlertModal,
    showCriticalErrorModal,
    showSnackbar,
    clearCart,
    appliedDiscountType,
    lastAppliedDiscountAmount,
    calculateTotal,
    formatCurrency,
    screenId: currentScreen?.id || null,
    screenSettings,  // ✅ SQL'den çekilen ekran ayarları
    useFiscalDeviceForCash: screenSettings?.useFiscalDeviceForCash || false,
    useBothFiscalAndReceiptPrinter: screenSettings?.useBothFiscalAndReceiptPrinter || false, // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
    warnIfFiscalNotConnected: screenSettings?.warnIfFiscalNotConnected !== undefined ? screenSettings.warnIfFiscalNotConnected : true, // ✅ YENİ: Yazarkasa bağlantı uyarısı (varsayılan açık)
    prepaidSystemEnabled: screenSettings?.prepaidSystemEnabled || false,
    selectedFiscalDevice: selectedFiscalDevice, // ✅ FIX: useMemo ile optimize edilmiş değer
    setPaymentWaitingModalOpen,
    setPaymentWaitingStatus,
    setPaymentWaitingMessage,
    // ✅ YENİ: Restoran modu prop'ları - Ödeme sonrası adisyon kapatma için
    isRestaurantMode,
    tableId: tableId || null,
    tableName: tableName || null,
    waiterName: waiterName || null,
    loginType: loginType,
    restaurantScreenId: restaurantScreenId || null,
    navigateToRestaurantScreen: () => navigate(`/restaurant-screen/${restaurantScreenId}`),
    // ✅ YENİ: Sipariş numarası rezervasyonu
    reservedLongCode,
    reservedShortCode, // ✅ YENİ: Fiş yazdırmada kullanılır
    onPaymentComplete: refreshOrderNumber,
    // 💰 Para çekmecesi tetiği (FALLBACK):
    // Ön-tetik (butona basıldığında) zaten atılmış olur; buradaki callback
    // yalnızca beklenmedik bir şekilde ön-tetik atlandıysa devreye girer.
    // `triggerCashDrawer` içindeki dedup sayesinde çift tetik olmaz.
    onCashPaymentSuccess: (paidByCash: number) => {
      console.log(`💰 [CashDrawer@onSuccess:fallback] paidByCash=${paidByCash}`);
      if (paidByCash > 0) {
        triggerCashDrawer('success-fallback');
      }
    },
    // ✅ YENİ: Sepet bölme için parametreler
    skipNavigationAfterPayment: isSplitPaymentMode,
    onPaymentSuccessOverride: splitPaymentSuccessCallback,
    splitCartOverride: pendingSplitCartItems.length > 0 ? pendingSplitCartItems : undefined, // ✅ YENİ: Sepet bölme ödemesi için cart
    // ✅ YENİ: Restoran + sepet bölme fix — hangi ActiveSale kapatılacak (sol=main, sağ=splitChild)
    restaurantActiveSaleIdOverride: pendingSplitActiveSaleId,
    // ✅ YENİ: Kasiyer bilgileri (gün sonu raporu için)
    currentCashierId: currentCashier?.cashierId ?? null,
    currentCashierName: currentCashier?.cashierName ?? null,
    // ✅ YENİ: Müşteri notu (fişe yazdırılır)
    customerNote,
    // ✅ YENİ: Para üstü modalı bilgisi (alınan para / para üstü fişe yazdırılır)
    cashChangeInfoRef,
    // ✅ YENİ: Şube ID (Branch bazında benzersiz sipariş numarası için)
    activeBranchId,
    // ✅ YENİ: Şubenin deposu (varsa tüm ürünler bu depodan düşülür)
    branchWarehouseId,
    // ✅ Ingenico departman-KDV uyumsuzluğu tespit edildiğinde modal göster (nakit veya karma)
    onIngenicoDeptVatMismatch: (syncResult) => {
      setDeptVatSyncResult(syncResult);
      setShowDeptVatMismatchModal(true);
    },
    // Kulüp girişi: ödeme sonrası callback
    onClubEntryPaymentComplete: isClubEntryMode ? async (data) => {
      // Kart akışı KAPALI ise (ayar) ya da bu işlemde "Kartsız Devam Et" seçildiyse:
      // kart atama/depozito/PostSale modalı YOK. Normal hızlı satış gibi bitir.
      if (!clubEntryCardFlowEnabled || skipCardForCurrentSaleRef.current) {
        skipCardForCurrentSaleRef.current = false;
        setPrePaymentScannedCards([]);
        prePaymentScannedCardsRef.current = [];
        depositPaymentMethodRef.current = 'CASH';
        clearCart();
        refreshOrderNumber();
        return;
      }

      const cardsFromRef = prePaymentScannedCardsRef.current;
      const depPayMethod = depositPaymentMethodRef.current;
      console.log('[KULÜP GİRİŞİ] Ödeme tamamlandı (Step 1 - CompleteSale)', {
        receiptId: data.receiptId,
        paymentMethod: data.paymentMethod,
        pavoPaySaleNumber: data.pavoPaySaleNumber,
        prePaymentScannedCards: cardsFromRef.length,
        depositActive: clubEntryDepositActive,
        depositPaymentMethod: depPayMethod,
      });

      // Deposito aktif ve kartlar önceden okutulduysa
      if (clubEntryDepositActive && cardsFromRef.length > 0) {
        const totalDepositAmount = cardsFromRef.reduce((sum: number, sc: any) => sum + (sc.depositAmount || 0), 0);

        // (B) Depozito var → ana (giriş/hizmet) ödemesi alındı; depozitoyu AYRI
        //     bir tahsilat olarak almak için "Depozito tahsilatı bekleniyor"
        //     ekranını aç. Kart ataması, depozito tahsil edilince yapılacak.
        if (totalDepositAmount > 0) {
          const mainAmount = (data.cart || []).reduce((s: number, ci: any) => s + ((ci.price || 0) * (ci.quantity || 1)), 0);
          const info = {
            cards: cardsFromRef,
            depositMethod: depPayMethod,
            totalDeposit: totalDepositAmount,
            mainReceiptId: data.receiptId,
            mainReferenceNumber: data.referenceNumber || '',
            mainPaymentMethod: data.paymentMethod,
            mainPavoPaySaleNumber: data.pavoPaySaleNumber,
            mainAmount,
          };
          pendingDepositInfoRef.current = info;
          setPendingDepositInfo(info);
          setClubEntryDepositPending(true);
          clearCart();
          refreshOrderNumber();
          showSnackbar('Giriş ödemesi alındı. Şimdi depozito tahsilatını yapın.', 'info');
          console.log('[KULÜP GİRİŞİ] Ana ödeme alındı; depozito tahsilatı bekleniyor:', totalDepositAmount, 'TL', depPayMethod, 'ana tutar:', mainAmount);
          return;
        }

        try {
          const { clubEntryService } = await import('../../../services/clubEntryService');
          const cards = cardsFromRef.map((sc: any) => ({
            cardUID: sc.cardUID,
            cardLabel: sc.cardLabel,
            depositAmount: sc.depositAmount || 0,
            serviceProductId: sc.assignedServiceItem?.id,
            serviceProductName: sc.assignedServiceItem?.name,
            entryMode: clubEntrySettings?.entryMode || 'SINGLE_ENTRY',
            totalEntryRights: sc.entryRights || 1,
            // ✅ Karta tanımlı ön ödemeli ürün hakları (paket grubundan)
            entitlements: (sc.entitlements || []).map((e: any) => ({
              productId: e.productId,
              productName: e.productName,
              quantity: e.quantity || 1,
              unitPricePaid: e.unitPricePaid || 0,
            })),
          }));

          await clubEntryService.assignCards({
            saleReceiptId: data.receiptId,
            saleReceiptNumber: data.referenceNumber,
            pavoPaySaleNumber: data.pavoPaySaleNumber,
            depositPavoPaySaleNumber: depPayMethod === 'POS' ? data.pavoPaySaleNumber : undefined,
            // Depozito tahsilat yöntemi açıkça gönderilir → aktif girişlerde
            // "POS"/"Nakit" etiketi PavoPay satış no akmasa bile doğru gösterilir.
            depositPaymentMethod: depPayMethod,
            screenId: currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined,
            paymentMethod: data.paymentMethod,
            cards,
          });

          console.log('[KULÜP GİRİŞİ] Kartlar başarıyla atandı!');
          showSnackbar(`${cards.length} kart başarıyla atandı`, 'success');

          // İade/iptal listesinde kulüp giriş satışı kart numarasıyla görünsün/aransın.
          // Satış no biliniyorsa (POS) kesin; bilinmiyorsa (Rest-P2P fire-and-forget
          // nakit) requestId üzerinden eşleştirilir.
          if (companyId) {
            linkClubEntryCard(
              companyId,
              cards.map((c: any) => c.cardUID).filter(Boolean).join(', '),
              { pavoPaySaleNumber: data.pavoPaySaleNumber }
            );
          }

          for (const sc of cardsFromRef) {
            const cardId = sc.cardId;
            if (!cardId) continue;
            try {
              await cardManagementService.openSession({
                CardId: cardId,
                AccountId: selectedCustomer?.id || customersHook.generalCariRef?.id || '0',
                AccountName: selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari',
                UserId: currentUser?.email || '',
                ScreenId: currentScreen?.id?.toString() || '',
              });
              console.log('[KULÜP GİRİŞİ] Oturum başlatıldı:', sc.cardUID);
            } catch (sessionErr: any) {
              const backendMsg = sessionErr.response?.data?.message || sessionErr.response?.data?.title || sessionErr.message;
              console.warn('[KULÜP GİRİŞİ] Oturum başlatma hatası:', sc.cardUID, backendMsg, sessionErr.response?.data);
            }
          }
        } catch (err: any) {
          console.error('[KULÜP GİRİŞİ] Kart atama hatası:', err);
          showSnackbar(`Kart atama hatası: ${err.message}`, 'error');
        }

        setPrePaymentScannedCards([]);
        prePaymentScannedCardsRef.current = [];
        depositPaymentMethodRef.current = 'CASH';
        clearCart();
        refreshOrderNumber();
        return;
      }

      // Deposito kapalı: mevcut akış - PostSaleCardAssignmentModal aç
      const regularCartItems = data.cart.filter((item: any) => !item.id?.startsWith('DEPOSIT-'));
      const serviceItemsInCart = regularCartItems.filter((item: any) => {
        const inv = sqlInventoryItems.find((i: any) => i.id === item.id);
        return inv?.itemType === 'SERVICE';
      });
      const itemsForModal = serviceItemsInCart.length > 0 ? serviceItemsInCart : regularCartItems;
      setLastServiceItems(itemsForModal.map((item: any) => ({
        id: item.id,
        productId: item.id,
        name: item.name,
        salePrice: item.price,
        quantity: item.quantity,
        itemType: sqlInventoryItems.find((i: any) => i.id === item.id)?.itemType || 'PRODUCT',
      })));
      // ✅ Kart slotlarını (entitlement dahil) ödeme anındaki sepetten üret; PostSale modalı kullanır
      setLastCardSlots(buildClubEntryCardSlots(data.cart));
      setLastSaleReceiptId(data.receiptId);
      setLastReferenceNumber(data.referenceNumber);
      setLastPaymentMethod(data.paymentMethod);
      setLastPavoPaySaleNumber(data.pavoPaySaleNumber);
      setShowPostSaleCardModal(true);
    } : undefined,
    skipClearCartAfterPayment: isClubEntryMode,
  });

  // ✅ KULÜP GİRİŞİ — Depozito tahsil edildikten SONRA kartları ata.
  //   Depozito ana satıştan AYRI tahsil edildiği için `collectDepositSeparately:true`
  //   ile gönderilir; backend muhasebeyi (POS→banka, Nakit→kasa) DepositAsync ile
  //   kendisi oluşturur. `depositSaleNumber`: POS depozitonun Pavo satış no'su —
  //   karta yazılır, çıkışta CancelSale (tam iptal) bununla yapılır.
  const finalizeClubEntryWithDeposit = useCallback(async (opts: {
    depositSaleNumber?: string;
    depositBankAccountId?: number;
    depositCashboxId?: number;
  }) => {
    const info = pendingDepositInfoRef.current;
    if (!info) {
      console.warn('[KULÜP GİRİŞİ] finalize çağrıldı ama pendingDepositInfo yok');
      return;
    }
    try {
      const { clubEntryService } = await import('../../../services/clubEntryService');
      const cards = info.cards.map((sc: any) => ({
        cardUID: sc.cardUID,
        cardLabel: sc.cardLabel,
        depositAmount: sc.depositAmount || 0,
        serviceProductId: sc.assignedServiceItem?.id,
        serviceProductName: sc.assignedServiceItem?.name,
        entryMode: clubEntrySettings?.entryMode || 'SINGLE_ENTRY',
        totalEntryRights: sc.entryRights || 1,
        entitlements: (sc.entitlements || []).map((e: any) => ({
          productId: e.productId,
          productName: e.productName,
          quantity: e.quantity || 1,
          unitPricePaid: e.unitPricePaid || 0,
        })),
      }));

      await clubEntryService.assignCards({
        saleReceiptId: info.mainReceiptId,
        saleReceiptNumber: info.mainReferenceNumber,
        pavoPaySaleNumber: info.mainPavoPaySaleNumber,
        // Depozitonun KENDİ tahsilatının Pavo satış no'su → çıkışta CancelSale (tam iptal).
        depositPavoPaySaleNumber: info.depositMethod === 'POS' ? opts.depositSaleNumber : undefined,
        depositPaymentMethod: info.depositMethod,
        // Depozito ayrı tahsil edildi → backend DepositAsync ile muhasebe kaydı yapsın.
        collectDepositSeparately: true,
        depositBankAccountId: info.depositMethod === 'POS' ? opts.depositBankAccountId : undefined,
        depositCashboxId: info.depositMethod === 'CASH' ? opts.depositCashboxId : undefined,
        screenId: currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined,
        // Dış bilet platformlarında gerçek platform etiketi (FIRSAT ME / BU BİLET) gönderilir.
        paymentMethod: info.mainPaymentLabel ?? info.mainPaymentMethod,
        cards,
      });

      console.log('[KULÜP GİRİŞİ] Kartlar atandı (depozito ayrı tahsilat). Depozito satış no:', opts.depositSaleNumber || '(nakit)');
      showSnackbar(`${cards.length} kart başarıyla atandı`, 'success');

      // İade/iptal listesinde hem ana giriş satışı hem (varsa) POS depozito satışı
      // kart numarasıyla görünsün/aransın.
      if (companyId) {
        const cardUidStr = cards.map((c: any) => c.cardUID).filter(Boolean).join(', ');
        linkClubEntryCard(companyId, cardUidStr, { pavoPaySaleNumber: info.mainPavoPaySaleNumber });
        if (opts.depositSaleNumber) {
          // Depozito ayrı bir POS satışıdır; satış no kesindir.
          linkClubEntryCard(companyId, cardUidStr, { pavoPaySaleNumber: opts.depositSaleNumber });
        }
      }

      for (const sc of info.cards) {
        const cardId = sc.cardId;
        if (!cardId) continue;
        try {
          await cardManagementService.openSession({
            CardId: cardId,
            AccountId: selectedCustomer?.id || customersHook.generalCariRef?.id || '0',
            AccountName: selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari',
            UserId: currentUser?.email || '',
            ScreenId: currentScreen?.id?.toString() || '',
          });
        } catch (sessionErr: any) {
          const backendMsg = sessionErr.response?.data?.message || sessionErr.response?.data?.title || sessionErr.message;
          console.warn('[KULÜP GİRİŞİ] Oturum başlatma hatası:', sc.cardUID, backendMsg);
        }
      }
    } catch (err: any) {
      console.error('[KULÜP GİRİŞİ] Depozito sonrası kart atama hatası:', err);
      showSnackbar(`Kart atama hatası: ${err.message}`, 'error');
    } finally {
      pendingDepositInfoRef.current = null;
      setPendingDepositInfo(null);
      setClubEntryDepositPending(false);
      setPrePaymentScannedCards([]);
      prePaymentScannedCardsRef.current = [];
      depositPaymentMethodRef.current = 'CASH';
      clearCart();
      refreshOrderNumber();
    }
  }, [clubEntrySettings, currentScreen, selectedCustomer, customersHook, currentUser, showSnackbar, clearCart, refreshOrderNumber]);

  // ✅ KULÜP GİRİŞİ — "Depozitoyu Tahsil Et": depozitoyu AYRI tahsil eder.
  //   POS  → standalone PavoPay (Rest-P2P) tahsilatı; QuickSale/processPartialWithInvoice
  //          akışına GİRMEZ (kalemsiz satış 400 verir). Başarıda satış no yakalanır.
  //   Nakit → cihaz yok; doğrudan finalize (backend kasa DepositAsync).
  const handleCollectDeposit = useCallback(async () => {
    const info = pendingDepositInfoRef.current;
    if (!info) {
      showSnackbar('Bekleyen depozito bilgisi yok', 'warning');
      return;
    }
    if (clubEntryDepositBusy) return;
    setClubEntryDepositBusy(true);
    try {
      if (info.depositMethod === 'CASH') {
        const cashboxId = cashPaymentCashboxId ? parseInt(String(cashPaymentCashboxId), 10) : undefined;
        if (!cashboxId) {
          showSnackbar('Nakit kasa atanmamış. Ekran ayarlarından kasa seçin.', 'error');
          return;
        }
        await finalizeClubEntryWithDeposit({ depositCashboxId: cashboxId });
      } else {
        const posOption: any = posOptions.find((p: any) => p.bankAccountId);
        if (!posOption) {
          showSnackbar('POS ödeme seçeneği tanımlı değil. Ayarlardan POS ekleyin.', 'error');
          return;
        }
        // Standalone PavoPay tahsilatı (Rest-P2P). Rest-P2P kapalıysa 'failed' döner.
        const outcome = await runRestP2PFlow(
          'pos',
          [{ method: 'pos', amount: info.totalDeposit, label: 'Kart Depozito', order: 0 }],
          info.totalDeposit,
        );
        if (outcome !== 'completed' && outcome !== 'continue_without_fiscal') {
          showSnackbar('Depozito POS tahsilatı tamamlanmadı. Tekrar deneyin, "Nakitle Al" seçin veya satışı iptal edin.', 'warning');
          return;
        }
        const depositSaleNumber = getLastRestP2PSaleNumber() || undefined;
        await finalizeClubEntryWithDeposit({
          depositSaleNumber,
          depositBankAccountId: parseInt(String(posOption.bankAccountId), 10),
        });
      }
    } finally {
      setClubEntryDepositBusy(false);
    }
  }, [clubEntryDepositBusy, cashPaymentCashboxId, posOptions, runRestP2PFlow, getLastRestP2PSaleNumber, finalizeClubEntryWithDeposit, showSnackbar]);

  // ✅ KULÜP GİRİŞİ — "Nakitle Al": POS depozito alınamadıysa yöntemi NAKİT'e çevir.
  const handleSwitchDepositToCash = useCallback(() => {
    const info = pendingDepositInfoRef.current;
    if (!info) return;
    const updated = { ...info, depositMethod: 'CASH' as const };
    pendingDepositInfoRef.current = updated;
    setPendingDepositInfo(updated);
    depositPaymentMethodRef.current = 'CASH';
    showSnackbar('Depozito NAKİT olarak alınacak. "Depozitoyu Tahsil Et"e basın.', 'info');
  }, [showSnackbar]);

  // ✅ KULÜP GİRİŞİ — "Satışı İptal Et": depozito alınamadıysa ANA satışı da geri al.
  //   POS ana ödeme  → PavoPay CancelSale + backend POS iadesi (para döner).
  //   Nakit/karma ana → Pavo fişi iptal edilir; nakit kısmı için kasiyer elle iade eder.
  //   Kart ataması YAPILMAZ.
  const handleCancelClubEntrySale = useCallback(async () => {
    const info = pendingDepositInfoRef.current;
    if (!info) return;
    if (clubEntryDepositBusy) return;
    setClubEntryDepositBusy(true);
    try {
      const { cancelPavoPaySale } = await import('./hooks/fiscalDevices/pavopay');
      if (info.mainPavoPaySaleNumber) {
        // POS ana ödemede tutarla birlikte (banka iadesi). Nakit/karma'da Pavo
        // fişini iptal et (tutar 0 → banka iadesi yapılmaz, kasiyer elle iade eder).
        const refundAmount = info.mainPaymentMethod === 'POS' ? info.mainAmount : 0;
        const res = await cancelPavoPaySale(info.mainPavoPaySaleNumber, companyId || '', refundAmount, currentUser?.email || undefined);
        if (!res.success) {
          showSnackbar(`Ana satış iptali başarısız: ${res.errorMessage || 'bilinmeyen hata'}`, 'error');
          return;
        }
        if (info.mainPaymentMethod === 'POS') {
          showSnackbar('Ana satış iptal edildi, ödeme iade edildi.', 'success');
        } else {
          showSnackbar('Ana satış fişi iptal edildi. NAKİT/karma kısmı için iadeyi elle yapın.', 'warning');
        }
      } else {
        showSnackbar('Ana satışta Pavo satış no yok; kart ataması iptal edildi (gerekirse iadeyi Satış Geçmişi\'nden yapın).', 'warning');
      }
      pendingDepositInfoRef.current = null;
      setPendingDepositInfo(null);
      setClubEntryDepositPending(false);
      setPrePaymentScannedCards([]);
      prePaymentScannedCardsRef.current = [];
      depositPaymentMethodRef.current = 'CASH';
      clearCart();
      refreshOrderNumber();
    } catch (e: any) {
      console.error('[KULÜP GİRİŞİ] Satış iptali hatası:', e);
      showSnackbar(`Satış iptali hatası: ${e.message}`, 'error');
    } finally {
      setClubEntryDepositBusy(false);
    }
  }, [clubEntryDepositBusy, companyId, currentUser, showSnackbar, clearCart, refreshOrderNumber]);

  // ✅ YENİ: Para çekmecesi tetikleyici hook (nakit / karma ödemede çekmece aç)
  const openCashDrawer = useOpenCashDrawer(screenSettings?.localServiceUrl);

  // 💰 Çekmece tetik dedup'u:
  // Aynı ödeme akışında birden fazla tetik atmayı engeller. Ön-tetik (butona
  // basılır basılmaz) ile callback-tetik (SQL başarısından sonra) fallback
  // olarak birlikte kalabilsin diye son tetikten itibaren kısa bir süre (3 sn)
  // geçmediyse yeni tetik atlanır.
  const lastDrawerTriggerAtRef = useRef<number>(0);
  const DRAWER_DEDUP_MS = 3000;
  const triggerCashDrawer = useCallback(
    (reason: string) => {
      const drawerPrinter = (screenSettings as any)?.cashDrawerPrinter;
      if (!drawerPrinter?.name) {
        console.log(`💰 [CashDrawer:${reason}] yazıcı tanımlı değil, tetik atlandı.`);
        return;
      }
      const now = performance.now();
      const elapsed = now - lastDrawerTriggerAtRef.current;
      if (lastDrawerTriggerAtRef.current > 0 && elapsed < DRAWER_DEDUP_MS) {
        console.log(
          `💰 [CashDrawer:${reason}] dedup (${Math.round(elapsed)}ms < ${DRAWER_DEDUP_MS}ms), tetik atlandı.`
        );
        return;
      }
      lastDrawerTriggerAtRef.current = now;
      const pin = drawerPrinter.pin || 'auto';
      console.log(
        `%c💰 [CashDrawer:${reason}] ÖN-TETİK → ${drawerPrinter.name} [pin=${pin}]`,
        'color:#2e7d32;font-weight:bold;'
      );
      // Fire-and-forget: ödeme akışını BLOKLAMA, paralel gitsin.
      // Kullanıcının ayarlardan kaydettiği pin (auto / pin2 / pin5) gönderilir;
      // 'auto' ise backend her ikisini sırayla dener (geriye uyumluluk).
      openCashDrawer(drawerPrinter.name, { pin });
    },
    [screenSettings, openCashDrawer]
  );

  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 5: EFFECTS (Side effects in logical order)
  // ═══════════════════════════════════════════════════════════

  /**
   * ✅ YENİ EFFECT: Şirket adını bir kez yükle ve cache'le
   * @trigger companyId changes
   * @purpose Her satışta Firebase'e gitmemek için şirket adını bir kez yükle
   */
  useEffect(() => {
    async function loadCompanyName() {
      if (!companyId) {
        setCompanyNameCache(null);
        return;
      }

      try {
        const companyInfo = await getCompanyInfo(companyId);
        if (companyInfo?.companyName) {
          setCompanyNameCache(companyInfo.companyName);
          console.log('🏢 [ŞİRKET CACHE] companyDataService\'den alındı:', companyInfo.companyName);
        } else {
          setCompanyNameCache(null);
          console.warn('⚠️ [ŞİRKET CACHE] companyName alanı yok');
        }
      } catch (err) {
        console.error('❌ [ŞİRKET CACHE] Şirket adı alınırken hata:', err);
        setCompanyNameCache(null);
      }
    }

    loadCompanyName();
  }, [companyId]);

  /**
   * ✅ YENİ EFFECT: Sipariş numarası rezervasyonu
   * @trigger companyId, isRestaurantMode changes
   * @purpose Ekran açıldığında API'den sipariş numarası al ve sağ üstte göster
   */
  useEffect(() => {
    async function reserveOrderNumber() {
      // ✅ Restoran modunda numara masa açılırken alınacak, burada almıyoruz
      if (isRestaurantMode) {
        console.log('🎫 [NUMARA] Restoran modunda - numara masa açılırken alınacak');
        return;
      }

      if (!companyId) {
        setReservedLongCode(null);
        setReservedShortCode(null);
        return;
      }

      // ✅ activeBranchId hazır olana kadar numara alma - gereksiz rezervasyonu önle
      if (!activeBranchId) {
        console.log('🎫 [NUMARA] Şube bilgisi henüz hazır değil, bekleniyor...');
        return;
      }

      try {
        console.log(`🎫 [NUMARA] Sipariş numarası rezerve ediliyor... (BranchId: ${activeBranchId})`);
        const response = await salesService.reserveOrderNumber('QUICK_SALE', activeBranchId);
        
        if (response.success) {
          setReservedLongCode(response.longCode);
          setReservedShortCode(response.shortCode);
          console.log(`🎫 [NUMARA] Rezerve edildi: ${response.shortCode} (${response.longCode}) BranchId: ${activeBranchId}`);
        } else {
          // Fallback - API hatası durumunda frontend'de üretilen numara kullanılır
          setReservedLongCode(response.longCode);
          setReservedShortCode(response.shortCode);
          console.warn(`⚠️ [NUMARA] API hatası, fallback numara: ${response.shortCode}`);
        }
      } catch (err) {
        console.error('❌ [NUMARA] Sipariş numarası alınırken hata:', err);
      }
    }

    reserveOrderNumber();
  }, [companyId, isRestaurantMode, activeBranchId]);

  /**
   * EFFECT 1: Initialize receipt number settings
   * @trigger screenSettings changes
   * @purpose Show/hide receipt number based on settings
   */
  useEffect(() => {
    if (screenSettings?.receiptDisplaySettings?.showReceiptNumber) {
      setShowReceiptNumber(true);
      const storedReceiptNumber = localStorage.getItem(STORAGE_KEYS.CURRENT_RECEIPT_NUMBER);
      if (storedReceiptNumber) {
        setCurrentReceiptNumber(parseInt(storedReceiptNumber, 10));
      }
    } else {
      setShowReceiptNumber(false);
    }
  }, [screenSettings]);

  /**
   * ✅ YENİ EFFECT: Yazarkasa (ÖKC) Bağlantı Durumu Kontrolü
   * @trigger selectedFiscalDevice, screenSettings?.useFiscalDeviceForCash changes
   * @purpose Her 1 dakikada bir local-service'e gidip yazarkasa bağlantısını kontrol et
   */
  useEffect(() => {
    // ÖKC kullanımı kapalıysa kontrol yapma
    if (!screenSettings?.useFiscalDeviceForCash) {
      setFiscalDeviceStatus('not_configured');
      return;
    }

    // Seçili cihaz yoksa kontrol yapma
    if (!selectedFiscalDevice) {
      setFiscalDeviceStatus('not_configured');
      return;
    }

    // ✅ KONTROL: Inpos veya Beko seçiliyse bağlantı kontrolü yap
    const isInposSelected = selectedFiscalDevice?.startsWith('inpos');
    const isBekoSelected = selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
    
    if (!isInposSelected && !isBekoSelected) {
      console.log('🖨️ [YAZARKASA] Inpos/Beko seçili değil, bağlantı kontrolü yapılmıyor. Seçili cihaz:', selectedFiscalDevice);
      setFiscalDeviceStatus('not_configured');
      return;
    }

    // localServiceUrl henüz yüklenmediyse beklemeye devam et
    if (!screenSettings?.localServiceUrl) {
      console.log('🖨️ [YAZARKASA] localServiceUrl henüz yüklenmedi, bağlantı kontrolü erteleniyor...');
      setFiscalDeviceStatus('checking');
      return;
    }

    console.log('🖨️ [YAZARKASA] Bağlantı kontrolü yapılacak:', selectedFiscalDevice, '| URL:', screenSettings.localServiceUrl);

    // URL'yi bağlantı kontrolünden ÖNCE set et
    const url = screenSettings.localServiceUrl;
    setPavoPayLocalServiceUrl(url);
    setHuginLocalServiceUrl(url);
    setInposLocalServiceUrl(url);
    setBekoTokenLocalServiceUrl(url);
    setBekoTargetDevice(screenSettings.selectedFiscalDevice || '');
    setIngenicoLocalServiceUrl(url);

    const checkFiscalDeviceConnection = async () => {
      setFiscalDeviceStatus('checking');

      try {
        const fiscalDevice = getFiscalDevice(selectedFiscalDevice);
        
        if (fiscalDevice.checkConnection) {
          const isConnected = await fiscalDevice.checkConnection();
          
          if (isConnected) {
            setFiscalDeviceStatus('connected');
            console.log(`🖨️ [YAZARKASA] ${selectedFiscalDevice.toUpperCase()} bağlı`);

            if (selectedFiscalDevice === 'ingenico_5000f' && allInventoryItems?.length > 0) {
              try {
                const products = allInventoryItems
                  .filter((item: any) => item.departmentNo && item.departmentNo > 0)
                  .map((item: any) => ({
                    departmentNo: item.departmentNo,
                    vatRate: item.vatRate ?? 0,
                    name: item.name || '',
                  }));
                if (products.length > 0) {
                  const syncResult = await syncDepartmentVatMapping(products);
                  setDeptVatSyncResult(syncResult);
                  if (syncResult.hasMismatches) {
                    console.warn('[Ingenico 5000F] Departman-KDV uyumsuzlugu tespit edildi!', syncResult.mismatches);
                    setShowDeptVatMismatchModal(true);
                  }
                }
              } catch (syncErr) {
                console.warn('[Ingenico 5000F] Departman-KDV senkronizasyon hatası:', syncErr);
              }
            }
          } else {
            setFiscalDeviceStatus('disconnected');
            console.warn(`⚠️ [YAZARKASA] ${selectedFiscalDevice.toUpperCase()} bağlı değil`);
          }
        } else {
          setFiscalDeviceStatus('connected');
          console.log(`🖨️ [YAZARKASA] ${selectedFiscalDevice.toUpperCase()} - bağlantı kontrolü desteklenmiyor, varsayılan: bağlı`);
        }
      } catch (error: any) {
        setFiscalDeviceStatus('disconnected');
        console.log('🖨️ [YAZARKASA] Bağlantı kontrolü başarısız:', error.message || error);
      }
    };

    // Beko USB: cached health state API ile hizli polling (5sn)
    // Diger cihazlar: dogrudan cihaz kontrolu (60sn)
    const pollCachedHealthState = async () => {
      try {
        const resp = await fetch(`${url}/api/yazarkasa/device-health-status?deviceType=${selectedFiscalDevice}`, {
          signal: AbortSignal.timeout(3000)
        });
        const result = await resp.json();
        if (result.success && result.data) {
          const newStatus = result.data.isConnected ? 'connected' : 'disconnected';
          setFiscalDeviceStatus(prev => {
            if (prev !== newStatus) {
              console.log(`🖨️ [YAZARKASA] ${selectedFiscalDevice.toUpperCase()} durum değişti: ${prev} → ${newStatus}`);
            }
            return newStatus;
          });
        }
      } catch {
        // cached health API erisilemezse sessizce devam et
      }
    };

    checkFiscalDeviceConnection();

    const timers: ReturnType<typeof setInterval>[] = [];

    if (isBekoSelected) {
      // Beko: cached health API ile 5 saniyede bir hızlı kontrol
      timers.push(setInterval(pollCachedHealthState, 5000));
      // Tam kontrol her 60 saniyede bir (yedek)
      timers.push(setInterval(checkFiscalDeviceConnection, 60000));
    } else {
      // Inpos ve diğerleri: 60 saniyede bir tam kontrol
      timers.push(setInterval(checkFiscalDeviceConnection, 60000));
    }

    return () => {
      timers.forEach(t => clearInterval(t));
    };
  }, [selectedFiscalDevice, screenSettings?.useFiscalDeviceForCash, screenSettings?.localServiceUrl, allInventoryItems?.length]);

  // Ingenico acik fis kontrolu - ekran acildiginda bir kez calisir
  useEffect(() => {
    if (selectedFiscalDevice !== 'ingenico_5000f') return;
    if (!screenSettings?.useFiscalDeviceForCash) return;
    if (!screenSettings?.localServiceUrl) return;

    const checkForOpenTransaction = async () => {
      try {
        console.log('[QuickSale] Ingenico acik fis kontrolu yapiliyor...');
        const data = await checkIngenicoOpenTransaction();
        if (data && data.hasOpenTransaction) {
          console.log('[QuickSale] Ingenico cihazda acik fis bulundu:', {
            totalAmount: data.totalAmountTL,
            totalPayment: data.totalPaymentTL,
            remaining: data.remainingAmountTL,
            paymentsComplete: data.paymentsComplete,
          });
          setIngenicoRecoveryData(data);
          setIsIngenicoRecoveryModalOpen(true);
        } else {
          console.log('[QuickSale] Ingenico cihazda acik fis yok.');
        }
      } catch (err: any) {
        console.warn('[QuickSale] Ingenico acik fis kontrolu hatasi:', err.message);
      }
    };

    const timer = setTimeout(checkForOpenTransaction, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiscalDevice, screenSettings?.useFiscalDeviceForCash, screenSettings?.localServiceUrl]);

  // ═══════════════════════════════════════════════════════════
  // 🔹 SECTION 6: EVENT HANDLERS & BUSINESS LOGIC
  // ═══════════════════════════════════════════════════════════

  // ─────────────────────────────────────────────────────────
  // 6.1 - User Menu Handlers
  // ─────────────────────────────────────────────────────────
  
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserMenu(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUserMenu(null);
  };

  // Ürün kartı sıralama düzeni handler'ları
  const productSortStorageKey = useCallback((categoryId: string) => {
    return `qs_product_order_${currentScreen?.id || 'default'}_${categoryId}`;
  }, [currentScreen?.id]);

  useEffect(() => {
    if (!currentScreen?.id) return;
    try {
      const allKeys: Record<string, string[]> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`qs_product_order_${currentScreen.id}_`)) {
          const val = localStorage.getItem(key);
          if (val) {
            const catId = key.replace(`qs_product_order_${currentScreen.id}_`, '');
            allKeys[catId] = JSON.parse(val);
          }
        }
      }
      if (Object.keys(allKeys).length > 0) {
        setCustomProductOrder(allKeys);
      }
    } catch (e) {
      console.warn('Ürün sıralama verisi okunamadı:', e);
    }
  }, [currentScreen?.id]);

  // useCallback: paket modunda parent'a expose edildiği için referansın stabil
  // kalması gerekiyor (aksi halde her render'da parent state'i güncellenir).
  const handleOpenProductSortSettings = useCallback(() => {
    setIsProductSortPinDialogOpen(true);
    setProductSortPinInput('');
    setProductSortPinError(false);
  }, []);

  // ✅ YENİ: Paket modunda "Ürün Kartı Düzeni" açma fonksiyonunu parent'a expose et
  // (normal moddaki menüdeki seçeneğin paket hızlı satış başlığındaki karşılığı)
  useEffect(() => {
    if (isPackageMode && onProductSortReady) {
      onProductSortReady(handleOpenProductSortSettings);
    }
  }, [isPackageMode, onProductSortReady, handleOpenProductSortSettings]);

  const handleProductSortPinDigit = (digit: string) => {
    if (productSortPinInput.length < 5) {
      const newPin = productSortPinInput + digit;
      setProductSortPinInput(newPin);
      setProductSortPinError(false);
      if (newPin.length === 5) {
        if (newPin === '13579') {
          setIsProductSortPinDialogOpen(false);
          setProductSortPinInput('');
          setIsProductSortEditMode(true);
        } else {
          setProductSortPinError(true);
          setTimeout(() => { setProductSortPinInput(''); setProductSortPinError(false); }, 800);
        }
      }
    }
  };

  const handleProductSortPinBackspace = () => {
    setProductSortPinInput(prev => prev.slice(0, -1));
    setProductSortPinError(false);
  };

  const handleProductOrderChange = useCallback((categoryId: string, orderedIds: string[]) => {
    setCustomProductOrder(prev => ({ ...prev, [categoryId]: orderedIds }));
  }, []);

  const handleSaveProductOrder = useCallback(() => {
    try {
      Object.entries(customProductOrder).forEach(([catId, ids]) => {
        localStorage.setItem(productSortStorageKey(catId), JSON.stringify(ids));
      });
      setIsProductSortEditMode(false);
    } catch (e) {
      console.error('Ürün sıralaması kaydedilemedi:', e);
    }
  }, [customProductOrder, productSortStorageKey]);

  const handleResetProductOrder = useCallback(() => {
    try {
      const key = productSortStorageKey(selectedCategory);
      localStorage.removeItem(key);
      setCustomProductOrder(prev => {
        const next = { ...prev };
        delete next[selectedCategory];
        return next;
      });
    } catch (e) {
      console.error('Ürün sıralaması sıfırlanamadı:', e);
    }
  }, [productSortStorageKey, selectedCategory]);

  const handleExitProductSortEdit = useCallback(() => {
    setIsProductSortEditMode(false);
    // Kaydedilmemiş değişiklikleri geri al - localStorage'dan tekrar oku
    if (currentScreen?.id) {
      try {
        const allKeys: Record<string, string[]> = {};
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(`qs_product_order_${currentScreen.id}_`)) {
            const val = localStorage.getItem(k);
            if (val) {
              const catId = k.replace(`qs_product_order_${currentScreen.id}_`, '');
              allKeys[catId] = JSON.parse(val);
            }
          }
        }
        setCustomProductOrder(allKeys);
      } catch (e) {
        console.warn('Ürün sıralama verisi okunamadı:', e);
      }
    }
  }, [currentScreen?.id]);

  const handleLogout = async () => {
    try {
      // Hedef rota çıkıştan ÖNCE hesaplanır: terminal oturumu tespiti oturumdaki
      // kimliğe bakıyor ve logout onu siliyor.
      const target = getPostLogoutRoute();
      await logout();
      handleCloseUserMenu();
      navigate(target);
    } catch (error) {
      console.error("QuickSale'den çıkış yapılırken hata oluştu:", error);
    }
  };

  const handleGoToTaskBoard = () => {
    navigate('/personnel/tasks');
  };

  // ─────────────────────────────────────────────────────────
  // 6.2 - Search & Product Selection Handlers
  // ─────────────────────────────────────────────────────────
  
  // ✅ PERFORMANS OPTİMİZASYONU: handleSearchChange kaldırıldı
  // Artık ProductSearchBar kendi local state'ini tutuyor

  /**
   * ✅ YENİ: Ana container'a tıklandığında barkod arama bölümüne otomatik fokuslan
   * Restaurant modunda çalışmaz (arama bölümü yok)
   */
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // Sadece ana container'a direkt tıklandığında fokusla
    // Input, button vs. gibi etkileşimli elemanlara tıklandığında fokuslanma
    const target = e.target as HTMLElement;
    
    // Etkileşimli elementleri kontrol et
    const isInteractiveElement = 
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'TEXTAREA' ||
      target.closest('button') !== null ||
      target.closest('input') !== null ||
      target.closest('[role="button"]') !== null;
    
    // Restaurant modunda veya etkileşimli elemente tıklandıysa fokuslanma
    if (isRestaurantMode || isInteractiveElement) {
      return;
    }
    
    // Boş alana tıklandı, fokusla
    focusSearchBar();
  }, [isRestaurantMode, focusSearchBar]);

  // ─────────────────────────────────────────────────────────
  // 6.3 - Numpad Handlers (Refactored into smaller functions)
  // ─────────────────────────────────────────────────────────

  /** Handle numpad input when search bar is focused */
  const handleNumpadForSearch = (value: string) => {
    // ✅ PERFORMANS OPTİMİZASYONU: Numpad değerini ProductSearchBar'a ilet
    // ProductSearchBar kendi local state'ini güncelleyecek
    setNumpadSearchValue(value);
  };

  /** Apply quantity input value to selected cart item */
  const applyQuantityToSelectedItem = () => {
    if (selectedCartItemId !== null && quantityInputValue !== '') {
      const quantity = parseFloat(quantityInputValue);
      if (!isNaN(quantity) && quantity > 0) {
        setCart(prevCart => {
          return prevCart.map(item => {
            if (item.cartItemId === selectedCartItemId) { // ✅ cartItemId kullan
              return { ...item, quantity };
            }
            return item;
          });
        });
        setIsQuantityModeActive(false);
        setQuantityInputValue('');
      }
    }
  };

  /** Handle numpad input when quantity mode is active */
  const handleNumpadForQuantity = (value: string) => {
    if (value === 'C') {
      setQuantityInputValue('');
    } else if (value === '<') {
      setQuantityInputValue(prev => prev.slice(0, -1));
    } else if (value === 'Enter') {
      applyQuantityToSelectedItem();
    } else if (value === '.' || value === ',') {
      if (!quantityInputValue.includes('.')) {
        setQuantityInputValue(prev => prev + '.');
      }
    } else if (/[0-9]/.test(value)) {
      setQuantityInputValue(prev => prev + value);
    }
  };

  /** Activate quantity mode with specified value */
  const activateQuantityModeWithValue = (value: string) => {
    setIsQuantityModeActive(true);
    setQuantityInputValue(value === '.' ? '0.' : value);
  };

  /** Main numpad click handler (orchestrator) */
  const handleNumpadClick = (value: string) => {
    if (document.activeElement === searchBarRef.current?.inputElement) {
      handleNumpadForSearch(value);
      return;
    }
    
    if (isQuantityModeActive) {
      handleNumpadForQuantity(value);
      return;
    }
    
    if (value === '.') {
      activateQuantityModeWithValue('.');
    } else if (/[0-9]/.test(value)) {
      activateQuantityModeWithValue(value);
    }
  };

  // ─────────────────────────────────────────────────────────
  // 6.4 - Cart Management Handlers
  // ─────────────────────────────────────────────────────────

  /** Handle clear cart button click */
  const handleClearCart = () => {
    if (cart.length === 0) {
      return;
    }
    // ✅ Restaurant modunda SQL'den de sil
    clearCartWithRestaurant();
    // ✅ YENİ: Sepet temizlendiğinde notu da temizle
    setCustomerNote('');
  };

  /** ✅ YENİ: Sepeti temizleme onay modalını aç (ActionControlsPanel için) */
  const handleOpenClearCartConfirm = () => {
    if (cart.length === 0) return;
    setClearCartConfirmOpen(true);
  };

  /** ✅ YENİ: Sepeti temizleme onay modalını kapat */
  const handleCloseClearCartConfirm = () => {
    setClearCartConfirmOpen(false);
  };

  /** ✅ YENİ: Sepeti temizlemeyi onayla */
  const handleConfirmClearCart = () => {
    handleClearCart();
    setCustomerNote(''); // ✅ YENİ: Sepet temizlendiğinde notu da temizle
    setClearCartConfirmOpen(false);
  };

  /**
   * Local service'ten gelen hızlı sepet temizleme sinyali.
   *
   * Tuş ataması local service'in "Seçenekler > Ayarlar" sayfasında yapılır ve
   * tuş orada yakalanır; buraya yalnızca "temizle" komutu düşer, hangi tuşun
   * atandığını bilmemize gerek yok. Onay sorulmaz — kısayolun amacı bu.
   */
  const clearCartOnSignalRef = useRef<() => void>(() => {});
  clearCartOnSignalRef.current = () => {
    if (cart.length === 0) return;
    handleClearCart();
    showSnackbar('Sepet temizlendi', 'info');
  };

  /**
   * ✅ YENİ: Local service'ten gelen diğer kısayolların gövdesi.
   *
   * Burada yalnızca ref tanımlanır; içi, ilgili handler'ların tümü tanımlandıktan
   * sonra (handlePartialPayment'ın altında) doldurulur. Çağrılan handler'lar bu
   * satırdan sonra `const` ile tanımlandığı için burada atanamaz.
   */
  const signalHandlersRef = useRef<Record<string, (payload?: any) => void>>({});

  /** POS butonunun kendi tıklama yolu; CartSection kaydeder. */
  const posButtonClickRef = useRef<() => void>(() => {});
  const registerPosButtonClick = useCallback((fn: () => void) => {
    posButtonClickRef.current = fn;
  }, []);

  useEffect(() => {
    const baseUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      try {
        const source = new EventSource(`${baseUrl}/api/shortcuts/events`);
        eventSource = source;

        source.addEventListener('clear-cart', () => {
          clearCartOnSignalRef.current();
        });

        // ✅ YENİ: Atanabilir komutlar ve sabit tuşlar. Hangi tuşun atandığını
        // bilmemize gerek yok; local service yalnızca komut adını gönderir.
        (['cash-payment', 'pos-payment', 'partial-payment', 'discount', 'hold-sale', 'split-cart',
          'cart-increase', 'cart-decrease'] as const).forEach(eventName => {
          source.addEventListener(eventName, () => {
            signalHandlersRef.current[eventName]?.();
          });
        });

        // Sayısal tuş takımı: değer taşıdığı için payload okunur
        source.addEventListener('numpad-key', (event: Event) => {
          try {
            // Local service sarmalı: { eventType, data: { value }, timestamp }
            const payload = JSON.parse((event as MessageEvent).data || '{}');
            const value = payload?.data?.value;
            if (value) signalHandlersRef.current['numpad-key']?.(value);
          } catch {
            // Bozuk payload'da sessiz kal; kasiyerin işini kesmeyelim
          }
        });

        source.onerror = () => {
          // Local service kapalıyken EventSource sürekli deneyip konsolu
          // dolduruyor; bağlantıyı kapatıp sabit aralıkla yeniden kuruyoruz.
          source.close();
          if (eventSource === source) eventSource = null;
          if (!cancelled) reconnectTimer = setTimeout(connect, 10000);
        };
      } catch {
        if (!cancelled) reconnectTimer = setTimeout(connect, 10000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSource) eventSource.close();
    };
  }, [screenSettings?.localServiceUrl]);

  /**
   * ✅ YENİ: Sabit tuşların (sayısal tuş takımı, NumPad +/-) yakalanıp
   * yakalanmayacağını local service'e bildirir.
   *
   * Zorunlu: local service tuş yakalamayı her sekmeye ve ana pencereye bağlıyor.
   * Bu bildirim olmazsa veritabanı ayarları gibi sayfalarda sayısal tuşlarla
   * rakam yazmak imkansız hale gelir. Nabız, sekme çökerse bayrağın açık
   * takılı kalmaması için.
   */
  useEffect(() => {
    const baseUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';

    const postState = (quickSaleActive: boolean, quantityMode: boolean) => {
      // Local service kapalıysa hata sessizce yutulur; web tarayıcıda da çalışıyor.
      fetch(`${baseUrl}/api/shortcuts/state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quickSaleActive, quantityMode }),
      }).catch(() => {});
    };

    postState(true, isQuantityModeActive);

    const heartbeat = setInterval(() => {
      postState(true, isQuantityModeActive);
    }, 30000);

    const handleBeforeUnload = () => postState(false, false);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      postState(false, false);
    };
  }, [screenSettings?.localServiceUrl, isQuantityModeActive]);

  // ─────────────────────────────────────────────────────────
  // 6.5 - Quantity & Weight Handlers
  // ─────────────────────────────────────────────────────────

  /** Toggle quantity mode */
  const handleToggleQuantityMode = () => {
    const nextState = !isQuantityModeActive;
    setIsQuantityModeActive(nextState);
    setQuantityInputValue('');
    
    // Apply current quantity if mode is being deactivated
    if (!nextState && selectedCartItemId !== null && quantityInputValue !== '') {
      const quantity = parseFloat(quantityInputValue);
      if (!isNaN(quantity) && quantity > 0) {
        setCart(prevCart => {
          return prevCart.map(item => {
            if (item.id === selectedCartItemId) {
              return { ...item, quantity };
            }
            return item;
          });
        });
      }
    }
  };
  
  /** Handle quantity button click */
  const handleQuantityButton = () => {
    if (selectedCartItemId === null) return;
    
    handleRemoveDiscount();
    
    const selectedItem = cart.find(item => item.id === selectedCartItemId);
    if (!selectedItem) return;
    
    // Open weight input modal for KG products
    if (selectedItem.unit?.toLowerCase() === 'kg') {
      const originalInventoryItem = sqlInventoryItems.find((invItem: InventoryItem) => invItem.id === selectedItem.id);
      if (!originalInventoryItem) return;
      
      setTempSelectedProduct({
        productInfo: {
          id: selectedItem.id,
          name: selectedItem.name,
          price: selectedItem.price,
          vatRate: selectedItem.vatRate || 0,
          unit: selectedItem.unit
        },
        itemCanBeNegative: originalInventoryItem.canBeNegative === true,
        itemStockQuantity: originalInventoryItem.quantity !== undefined ? originalInventoryItem.quantity : 0
      });
      
      setIsWeightInputModalOpen(true);
      return;
    }
    
    // For non-KG products, activate quantity mode
    setIsQuantityModeActive(true);
    if (selectedItem) {
      setQuantityInputValue(selectedItem.quantity.toString());
    } else {
      setQuantityInputValue('');
    }
  };

  /**  Handle quantity click on cart item */
  const handleQuantityClick = (item: CartItem) => {
    const inventoryItem = sqlInventoryItems.find((invItem: InventoryItem) => invItem.id === item.id);
    if (!inventoryItem) {
      showSnackbar('Ürün bilgileri bulunamadı.', 'error');
      return;
    }

    setSelectedCartItem(item);
    // Ambalaj satırı: pencere ambalaj adedi ve ambalaj fiyatıyla açılır (2 KOLİ); onayda × içerik.
    const pkgFactor = !isRestaurantMode && isPackageLine(item) ? (item.packageFactor as number) : 1;
    const stockQty = inventoryItem.quantity !== undefined ? inventoryItem.quantity : 0;
    setTempSelectedProduct({
      productInfo: {
        id: item.id,
        name: pkgFactor > 1 ? packageLineName(item) : item.name,
        price: item.price * pkgFactor,
        vatRate: item.vatRate || 0,
        unit: pkgFactor > 1 ? (item.packageName as string) : (item.unit || 'adet')
      },
      itemCanBeNegative: inventoryItem.canBeNegative === true,
      itemStockQuantity: stockQty / pkgFactor,
      currentQuantity: item.quantity / pkgFactor
    });
    
    setIsCartUpdateMode(true);
    
    setTimeout(() => {
      setIsWeightInputModalOpen(true);
    }, 0);
  };

  /** ✅ YENİ: Handle menu option edit - sepetteki menü ürününün opsiyonlarını düzenle */
  const handleMenuOptionEdit = (item: CartItem) => {
    if (!item.hasMenu) {
      return;
    }
    
    const inventoryItem = sqlInventoryItems.find((invItem: InventoryItem) => invItem.id === item.id);
    if (!inventoryItem) {
      showSnackbar('Ürün bilgileri bulunamadı.', 'error');
      return;
    }

    // Düzenleme için sepet item'ını kaydet
    setEditingCartItem(item);
    
    // ✅ KRİTİK: BASE fiyatı hesapla (ekstra opsiyonlar HARİÇ)
    // Sepetteki fiyat = base fiyat + totalPriceDifference
    // Dolayısıyla base fiyat = sepetteki fiyat - totalPriceDifference
    const basePrice = item.price - (item.totalPriceDifference || 0);
    
    // Menü ürün bilgilerini ayarla
    setPendingMenuProduct({
      productId: item.id,
      productName: item.name,
      productPrice: basePrice, // ✅ BASE fiyat (ekstra opsiyonlar HARİÇ)
      productVatRate: item.vatRate || 0,
      productVatStatus: item.vatStatus || 'excluded',
      productUnit: item.unit || 'adet',
      itemCanBeNegative: inventoryItem.canBeNegative === true,
      itemStockQuantity: inventoryItem.quantity !== undefined ? inventoryItem.quantity : 0,
      hasRecipe: item.hasRecipe || false,
      hasMenu: true
    });
    
    // Modalı aç
    setIsMenuOptionModalOpen(true);
  };

  /** Handle quantity update confirmation from weight modal */
  const handleQuantityUpdateConfirm = (quantity: number, mode: 'add' | 'update') => {
    if (!selectedCartItem) {
      showSnackbar('Ürün seçilmedi.', 'error');
      setIsWeightInputModalOpen(false);
      setIsCartUpdateMode(false);
      return;
    }

    // ✅ RESTORAN MODU: SQL senkronlu yola delege ET. Modal eskiden sadece
    // yerel sepeti güncelliyordu; SQL'deki Quantity 1'de kalırken markAsPrinted
    // PrintedQuantity'yi yeni miktara yazınca kayıt bozuluyordu (Quantity=1,
    // PrintedQuantity=2). Masaya tekrar girişte sepet SQL'den yüklendiği için
    // artış kayboluyor ve "Tüm ürünler zaten mutfağa yazdırılmış!" hatası
    // doğuyordu. updateQuantityWithRestaurant SQL Quantity/TotalPrice günceller,
    // garson işlemini kaydeder, azalışta yetki + otomatik iptal fişini yönetir.
    if (isRestaurantMode && selectedCartItem.cartItemId) {
      const currentItem = cart.find(i => i.cartItemId === selectedCartItem.cartItemId);
      const currentQty = currentItem?.quantity ?? selectedCartItem.quantity;
      const change = mode === 'update' ? quantity - currentQty : quantity;

      if (change !== 0) {
        updateQuantityWithRestaurant(selectedCartItem.cartItemId, change);
      }

      setIsWeightInputModalOpen(false);
      setIsCartUpdateMode(false);
      setSelectedCartItem(null);

      if (change !== 0) {
        showSnackbar(
          mode === 'update'
            ? `Ürün miktarı ${quantity} olarak güncelleniyor.`
            : `Ürün miktarına ${quantity} ekleniyor.`,
          'success'
        );
      }
      return;
    }

    setCart(prevCart => {
      return prevCart.map(item => {
        // cartItemId satıra özgüdür; item.id ürün kimliği olduğundan aynı üründen
        // birden fazla satır varsa hepsini birden günceller (bug). Eski sepet
        // kayıtlarında cartItemId olmayabilir — o durumda id'ye düşülür.
        const isSameLine = selectedCartItem.cartItemId
          ? item.cartItemId === selectedCartItem.cartItemId
          : item.id === selectedCartItem.id;
        if (isSameLine) {
          // Ambalaj satırında girilen sayı ambalaj adedidir.
          const multiplier = isPackageLine(item) ? (item.packageFactor as number) : 1;
          const newQuantity = mode === 'update'
            ? quantity * multiplier
            : item.quantity + quantity * multiplier;
          
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
    });

    setIsWeightInputModalOpen(false);
    setIsCartUpdateMode(false);
    setSelectedCartItem(null);
    
    showSnackbar(
      mode === 'update' 
        ? `Ürün miktarı ${quantity} olarak güncellendi.` 
        : `Ürün miktarına ${quantity} eklendi.`,
      'success'
    );
  };

  // 6.6 - Prepaid kart iadesi -> useProductRefund hook
  const {
    isPurchaseItemsOpen,
    setIsPurchaseItemsOpen,
    purchaseItems,
    purchaseItemsLoading,
    purchaseItemsGrandTotal,
    selectedRefundItem,
    setSelectedRefundItem,
    refundLoading,
    refundConfirmOpen,
    setRefundConfirmOpen,
    handleOpenPurchaseItems,
    handleRefundItem,
  } = useProductRefund({
    companyId,
    prepaidCard,
    setPrepaidCard,
    selectedPrepaidWalletId,
    currentCashier,
    showAlertModal,
  });

  // ✅ HOOK'TAN GELİYOR: handleOpenDiscountModal, handleCloseDiscountModal, handleApplyDiscount, handleRemoveDiscount
  // Bu fonksiyonlar yukarıda useQuickSaleDiscount hook'undan geliyor (580-584 satırları)
  // setIsDiscountModalOpen, setDiscountInputPercentage, setDiscountInputAmount ve ilgili setter'lar da hook'tan geliyor

  // Ingenico acik fis kurtarma - iptal et
  const handleIngenicoRecoveryCancel = async () => {
    setIsIngenicoRecoveryProcessing(true);
    try {
      console.log('[QuickSale] Ingenico acik fis iptal ediliyor...');
      const result = await cancelIngenicoOpenTransaction();
      if (result.success) {
        if (result.action === 'payments_complete_close') {
          showAlertModal(
            'Fiş Kapatıldı (İptal Değil!)',
            `Dikkat: Fişteki ödemeler TAMAMLANMIŞ (${(result.totalPaymentTL ?? 0).toFixed(2)} TL) olduğu için fiş iptal edilemedi, yazdırılıp KAPATILDI. Bu satış cihazda kayıtlıdır — gerekiyorsa satışı sisteme de kaydedin.`
          );
        } else {
          showAlertModal('Fiş İptal Edildi', 'Cihazda açık kalan fiş başarıyla iptal edildi. Yeni bir işlem başlatabilirsiniz.');
        }
      } else if (result.action === 'partial_payment_pending') {
        showAlertModal(
          'Kısmi Ödeme Var',
          `Fişte kısmi ödeme var (${(result.totalPaymentTL ?? 0).toFixed(2)} / ${(result.totalAmountTL ?? 0).toFixed(2)} TL), iptal edilemiyor. Kalan ödemeyi cihazdan tamamlayın veya cihazdan iptal edin.`
        );
      } else if (result.action === 'open_unreadable') {
        showAlertModal(
          'Fiş İptal Edilemedi',
          'Fişin detayları okunamadığı için fişe DOKUNULMADI (cihaz meşgul olabilir veya oturum kurulamadı). Cihazdaki işlemi sonuçlandırın ya da birkaç saniye sonra tekrar deneyin.'
        );
      } else {
        showAlertModal('İptal Hatası', result.message || 'Açık fiş iptal edilemedi. Lütfen cihazı kontrol edin ve tekrar deneyin.');
      }
    } catch (err: any) {
      showAlertModal('Hata', err.message || 'Fiş iptal edilirken bir hata oluştu.');
    } finally {
      setIsIngenicoRecoveryProcessing(false);
      setIsIngenicoRecoveryModalOpen(false);
      setIngenicoRecoveryData(null);
    }
  };

  // Ingenico acik fis kurtarma - odemeye devam et
  const handleIngenicoRecoveryContinue = () => {
    if (ingenicoRecoveryData && ingenicoRecoveryData.items.length > 0) {
      // Ticket'taki urunleri envanterdeki urunler ile eslestir
      const recoveredCart: CartItem[] = [];
      for (const ticketItem of ingenicoRecoveryData.items) {
        const matched = sqlInventoryItems.find(
          (inv: any) => inv.name?.trim().toUpperCase() === ticketItem.name.toUpperCase()
        );

        if (matched) {
          recoveredCart.push({
            id: matched.id,
            name: matched.name,
            price: ticketItem.unitPriceTL,
            quantity: ticketItem.quantity,
            cartItemId: `recovery-${matched.id}-${Date.now()}`,
            vatRate: matched.vatRate || 0,
            vatStatus: 'included',
            unit: matched.unit || 'adet',
          });
          console.log(`[Recovery] Urun eslesti: ${ticketItem.name} → ${matched.name} (${ticketItem.unitPriceTL} TL x ${ticketItem.quantity})`);
        } else {
          recoveredCart.push({
            id: `ingenico-${ticketItem.deptIndex}-${Date.now()}`,
            name: ticketItem.name,
            price: ticketItem.unitPriceTL,
            quantity: ticketItem.quantity,
            cartItemId: `recovery-unknown-${Date.now()}-${Math.random()}`,
            vatRate: 0,
            vatStatus: 'included',
            unit: 'adet',
          });
          console.warn(`[Recovery] Urun eslestirilemedi: ${ticketItem.name} (${ticketItem.unitPriceTL} TL x ${ticketItem.quantity})`);
        }
      }

      if (recoveredCart.length > 0) {
        console.log(`[Recovery] Sepete ${recoveredCart.length} urun eklendi`);
        setCart(recoveredCart);
      }
    }

    setIsIngenicoRecoveryModalOpen(false);
    setIsPartialPaymentModalOpen(true);
  };

  // Parçalı Ödeme Modalı Kapatma Fonksiyonu
  const handleClosePartialPaymentModal = async (paymentDetails: PaymentDetails | null) => {
    setIsPartialPaymentModalOpen(false);

    // Ingenico kurtarma verisini temizle
    if (ingenicoRecoveryData) {
      setIngenicoRecoveryData(null);
    }
    
    // ✅ YENİ: Sepet bölme modunda parçalı ödeme tamamlandıysa
    if (splitCartPartialPaymentInfo && paymentDetails) {
      console.log('💰 [SPLIT CART PARTIAL] Parçalı ödeme tamamlandı, sepet bölme işlemleri yapılıyor...', {
        cartType: splitCartPartialPaymentInfo.cartType,
        paymentDetails
      });
      
      const { cartType, cartItems, newCartAfterPayment } = splitCartPartialPaymentInfo;
      
      // ✅ Sepet bölme ödemesi için gerekli state'leri set et
      newCartAfterPaymentRef.current = newCartAfterPayment;
      
      // ✅ Success callback - nakit/pos ile aynı mantık
      const handleSplitPartialSuccess = () => {
        console.log(`✅ [SPLIT CART PARTIAL] ${cartType} sepet parçalı ödemesi başarılı, callback çağrıldı`);
        
        // Yeni cart'ı set et
        const pendingNewCart = newCartAfterPaymentRef.current;
        if (pendingNewCart !== null) {
          console.log(`📦 [SPLIT CART PARTIAL] Yeni cart set ediliyor: ${pendingNewCart.length} ürün`);
          setCart([...pendingNewCart]);
          newCartAfterPaymentRef.current = null;
        }
        
        // ✅ FIX: Sepet bölme state'lerini temizle (sepet türüne göre)
        if (cartType === 'right') {
          // Sepet 2 (sağ sepet) ödendi - sağ sepeti temizle
          console.log('🧹 [SPLIT CART PARTIAL] Sepet 2 ödendi, sağ sepet temizleniyor...');
          setSplitRightCart([]);
          setSplitRightInvoiceNumber('');
          // B index'i artır (bir sonraki bölmede B3, B4... kullanılacak)
          setSplitBIndex(prev => prev + 1);
        } else {
          // Sepet 1 (sol sepet) ödendi - sağ sepetteki ürünler zaten ana cart'a taşındı
          console.log('🧹 [SPLIT CART PARTIAL] Sepet 1 ödendi, sağ sepet temizleniyor (ürünler ana sepete taşındı)...');
          setSplitRightCart([]);
          setSplitRightInvoiceNumber('');
        }
        
        // Temizlik
        setPendingSplitCartItems([]);
        setPendingSplitActiveSaleId(null); // ✅ Parçalı ödeme sonrası override temizle
        setIsSplitPaymentMode(false);
        setSplitPaymentSuccessCallback(undefined);
        setPendingSplitPayment(null);
        setSplitCartPartialPaymentInfo(null);
        
        // ✅ YENİ: SplitCartModal'ı yenilemek için refreshKey'i artır
        setSplitCartRefreshKey(prev => prev + 1);
      };
      
      // ✅ Parçalı ödeme işlemini başlat (usePayment hook'u üzerinden)
      setIsSplitPaymentMode(true);
      setSplitPaymentSuccessCallback(() => handleSplitPartialSuccess);
      setPendingSplitCartItems([...cartItems]);
      
      // Parçalı ödeme bilgisini temizle (ama sonra useEffect'te kullanılacağı için hemen temizleme)
      // setSplitCartPartialPaymentInfo(null); // useEffect'te yapılacak
      
      // ✅ YENİ: pendingSplitPayment set et - useEffect tetiklenecek ve ödeme başlatılacak
      setPendingSplitPayment({
        paymentType: 'partial',
        partialPaymentDetails: paymentDetails
      });
      
      debugLog("Sepet bölme parçalı ödeme state'leri set edildi, useEffect tetiklenecek...");

      // 💰 ÖN-TETİK: Karma ödemenin nakit bileşeni varsa sepet bölme akışı
      // başlamadan önce çekmeceyi tetikle.
      if ((paymentDetails.paidByCash || 0) > 0) {
        triggerCashDrawer('split-partial-confirm');
      }
    } else if (paymentDetails) {
      // Normal parçalı ödeme
      debugLog("Parçalı ödeme modalından gelen detaylar:", paymentDetails);
      // 💰 ÖN-TETİK: Karma ödemenin nakit bileşeni varsa hook çağrısından önce tetikle
      if ((paymentDetails.paidByCash || 0) > 0) {
        triggerCashDrawer('partial-confirm');
      }
      hookProcessPaymentAfterPartial(paymentDetails);
    } else {
      debugLog("Parçalı ödeme modalı iptal edildi.");
      // Sepet bölme parçalı ödeme bilgisini temizle
      setSplitCartPartialPaymentInfo(null);
    }
  };

  // ✅ Sepet bölünmüşse tüm ödemeleri sepet bölme modalından al
  const isSplitCartActive = splitRightCart.length > 0;

  /**
   * ✅ SEPET BÖLME ÖDEMESİ
   * SplitCartModal'dan çağrılır - usePayment hook'undaki fonksiyonları birebir kullanır
   * Yazarkasa, fiş yazdırma, mutfak ekranı vs. tüm özellikler dahil!
   */
  const splitPaymentResolveRef = useRef<((value: boolean) => void) | null>(null);
  
  // ✅ YENİ: Ödeme sonrası yeni cart'ı saklamak için ref
  const newCartAfterPaymentRef = useRef<CartItem[] | null>(null);
  
  // Sepet bolme odemesi -> useSplitCartPayment hook (refactor)
  const { handleSplitCartPayment } = useSplitCartPayment({
    splitPaymentResolveRef,
    newCartAfterPaymentRef,
    setCart,
    showSnackbar,
    posOptions,
    pendingSplitPayment,
    setPendingSplitPayment,
    isSplitPaymentMode,
    setIsSplitPaymentMode,
    splitPaymentSuccessCallback,
    setSplitPaymentSuccessCallback,
    pendingSplitCartItems,
    setPendingSplitCartItems,
    setPendingSplitActiveSaleId,
    setSplitCartPartialPaymentInfo,
    setIsPartialPaymentModalOpen,
    hookHandleCashPayment,
    hookHandlePosPayment,
    hookProcessPaymentAfterPartial,
  });

  // Kulüp girişi: deposito aktifken ödeme öncesi kart okutma gerekli mi?
  const clubEntryDepositActive = isClubEntryMode && clubEntrySettings?.depositEnabled === true;

  // Kulüp girişi: kart okutma/verme akışı açık mı? (ayar varsayılan AÇIK)
  // Kapalı iken ödeme normal hızlı satış gibi işlenir: ne ödeme öncesi kart okutma
  // modalı, ne ödeme sonrası kart atama modalı/depozito akışı çalışır.
  const clubEntryCardFlowEnabled = isClubEntryMode && clubEntrySettings?.cardScanModalEnabled !== false;

  // Kulüp girişi: service items in cart (for pre-payment card modal)
  // SERVICE tipli ürünler varsa onları kullan, yoksa tüm sepeti kullan (ürün tipi henüz değiştirilmemiş olabilir)
  const getServiceItemsFromCart = useCallback(() => {
    const nonDeposit = cart.filter(item => !item.id.startsWith('DEPOSIT-'));
    const serviceOnly = nonDeposit.filter(item => {
      const inv = sqlInventoryItems.find((i: any) => i.id === item.id);
      return inv?.itemType === 'SERVICE';
    });
    const itemsToUse = serviceOnly.length > 0 ? serviceOnly : nonDeposit;
    return itemsToUse.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
    }));
  }, [cart, sqlInventoryItems]);

  // ✅ KULÜP GİRİŞİ: Sepeti paket-instance gruplarına göre "kart slotları"na çevir.
  // Her slot = bir kart: { serviceItem (giriş hakkı), packageName, entitlements (karta yazılacak ürün hakları) }.
  // Paketten gelen kalemler clubGroupId taşır; bir grup = bir kart. Gruplanmamış (eski/elle) hizmetler
  // geriye dönük uyumlu olarak adet kadar slota açılır (entitlement'sız).
  const buildClubEntryCardSlots = useCallback((sourceCart: any[]) => {
    // Stok kaydı otorite: bundle ekleme yolunda sepet kaleminin itemType'ı 'PRODUCT'a düşebildiği
    // için önce sqlInventoryItems'taki gerçek tip kullanılır (hizmet/ürün ayrımı doğru olsun).
    const resolveType = (it: any): string =>
      sqlInventoryItems.find((i: any) => i.id === it.id)?.itemType || it.itemType || 'PRODUCT';

    const nonDeposit = (sourceCart || []).filter((item: any) => !String(item.id).startsWith('DEPOSIT-'));
    const groups = new Map<string, any[]>();
    const ungrouped: any[] = [];
    nonDeposit.forEach((it: any) => {
      if (it.clubGroupId) {
        if (!groups.has(it.clubGroupId)) groups.set(it.clubGroupId, []);
        groups.get(it.clubGroupId)!.push(it);
      } else {
        ungrouped.push(it);
      }
    });

    const slots: any[] = [];

    groups.forEach((items) => {
      const services = items.filter((it) => resolveType(it) === 'SERVICE');
      const products = items.filter((it) => resolveType(it) !== 'SERVICE');
      const entitlements = products.map((p: any) => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity || 1,
        unitPricePaid: p.price || 0,
      }));
      const primary = services[0] || products[0];
      const serviceUnits = services.reduce((s: number, it: any) => s + (it.quantity || 1), 0) || 1;
      for (let k = 0; k < serviceUnits; k++) {
        slots.push({
          serviceItem: primary
            ? { id: primary.id, name: primary.name, price: primary.price, quantity: 1 }
            : undefined,
          packageName: items[0]?.clubPackageName,
          // Entitlement'lar grubun ilk kartına yazılır (genelde grup başına 1 kart)
          entitlements: k === 0 ? entitlements : [],
        });
      }
    });

    // Gruplanmamış (eski) kalemler: hizmetleri adet kadar slota aç (entitlement yok)
    const ungroupedServices = ungrouped.filter((it) => resolveType(it) === 'SERVICE');
    const soloToUse = ungroupedServices.length > 0 ? ungroupedServices : ungrouped;
    soloToUse.forEach((it: any) => {
      const qty = it.quantity || 1;
      for (let k = 0; k < qty; k++) {
        slots.push({
          serviceItem: { id: it.id, name: it.name, price: it.price, quantity: 1 },
          entitlements: [],
        });
      }
    });

    return slots;
  }, [sqlInventoryItems]);

  const getClubEntryCardSlots = useCallback(() => buildClubEntryCardSlots(cart), [buildClubEntryCardSlots, cart]);

  // ✅ ÖN ÖDEMELİ REDEMPTION: Karta tanımlı ürün haklarını (ve ürün detaylarını) yükleyen ortak fonksiyon.
  // Hem kart seçilince (useEffect) hem de işlem tamamlanınca (onPrepaidPurchaseComplete) çağrılır;
  // böylece hak kullanıldıktan sonra aynı kart tekrar okutulsa bile kalan haklar güncel gösterilir.
  const loadPrepaidEntitlements = useCallback(async (uid?: string | null) => {
    if (!prepaidSystemEnabled || !uid) {
      setPrepaidEntitlements([]);
      setEntitledProductDetailMap({});
      return;
    }
    try {
      const ents = await clubEntryServiceStatic.getCardEntitlements(uid);
      setPrepaidEntitlements(Array.isArray(ents) ? ents : []);
      const ids = Array.from(new Set((ents || []).map((e: any) => e.productId).filter(Boolean)));
      if (ids.length > 0) {
        try {
          const details = await salesService.getProductsByIds(ids as string[]);
          const map: Record<string, any> = {};
          (details || []).forEach((d: any) => {
            const pid = d.id || d.Id;
            if (pid) map[pid] = d;
          });
          setEntitledProductDetailMap(map);
        } catch (detailErr) {
          console.warn('[ENTITLEMENT] Ürün detayları yüklenemedi:', detailErr);
        }
      } else {
        setEntitledProductDetailMap({});
      }
    } catch (err) {
      console.warn('[ENTITLEMENT] Kart hakları yüklenemedi:', err);
      setPrepaidEntitlements([]);
    }
  }, [prepaidSystemEnabled]);

  useEffect(() => {
    loadPrepaidEntitlements(prepaidCard?.cardUID);
  }, [prepaidCard?.cardUID, loadPrepaidEntitlements]);

  // Tanımlı ürün panele tıklanınca: sepete 0 TL ayrı satır (isEntitlement) ekle
  const handleAddEntitlementToCart = useCallback((ent: any) => {
    const inCart = cart
      .filter((i: any) => i.isEntitlement && i.id === ent.productId)
      .reduce((s: number, i: any) => s + (i.quantity || 0), 0);
    if (inCart >= (ent.remaining || 0)) {
      showSnackbar('Bu ürün için tanımlı hak kalmadı. Normal fiyatla ekleyebilirsiniz.', 'warning');
      return;
    }
    const d = entitledProductDetailMap[ent.productId] || {};
    setCart((prev: any[]) => [...prev, {
      id: ent.productId,
      name: ent.productName,
      price: 0,
      quantity: 1,
      vatRate: d.vatRate ?? d.VatRate ?? 0,
      vatStatus: 'included' as const,
      unit: d.unit ?? d.Unit ?? 'adet',
      categoryId: d.categoryId ?? d.CategoryId ?? '',
      cartItemId: `ENT-${ent.productId}-${Date.now()}`,
      isEntitlement: true,
      hasRecipe: d.hasRecipe ?? d.HasRecipe ?? false,
      hasMenu: d.hasMenu ?? d.HasMenu ?? false,
    }]);
    showSnackbar(`${ent.productName} (Girişte ödendi - 0 ₺) sepete eklendi`, 'success');
  }, [cart, entitledProductDetailMap, setCart]);

  // Panelde gösterilecek haklar: sepetteki kullanım kadar kalan düşülür
  const entitlementsForPanel = useMemo(() => {
    return (prepaidEntitlements || []).map((e: any) => {
      const inCart = cart
        .filter((i: any) => i.isEntitlement && i.id === e.productId)
        .reduce((s: number, i: any) => s + (i.quantity || 0), 0);
      return { ...e, remaining: Math.max(0, (e.remaining || 0) - inCart) };
    });
  }, [prepaidEntitlements, cart]);

  // ✅ KULÜP GİRİŞİ — Dış bilet satışını gerçekleştir (FIRSAT ME / BU BİLET):
  //   1) Sepet GERÇEK fiyatıyla platform kasasına satılır (processOtherPaymentWithInvoice
  //      → stok düşer, gün sonuna girer, platform kasası ayrışır).
  //   2) Depozito > 0 → mevcut "Depozito tahsilatı bekleniyor" ekranına devredilir.
  //   3) Depozito = 0 → kartlar + pakete bağlı entitlement doğrudan atanır.
  //   Müşteriden nakit/POS cihazıyla TEKRAR tahsilat yapılmaz (ödeme platformda alınmıştır).
  const runExternalTicketSale = useCallback(async (
    option: any,
    scannedCards: any[],
    depositPaymentMethod: 'CASH' | 'POS',
  ) => {
    if (!option) {
      showSnackbar('Dış bilet platformu bilgisi bulunamadı.', 'error');
      return;
    }
    if (!option.cashboxId) {
      showSnackbar(`${option.label} için kasa tanımlanmamış. Ayarlar → Ödeme'den kasa bağlayın.`, 'error');
      return;
    }

    const saleItems = cart.filter((item: any) => !item.id?.startsWith?.('DEPOSIT-'));
    if (saleItems.length === 0) {
      showSnackbar('Sepet boş, satış yapılamaz.', 'error');
      return;
    }
    const mainAmount = saleItems.reduce((s: number, ci: any) => s + ((ci.price || 0) * (ci.quantity || 1)), 0);

    try {
      const request: QuickSaleOtherPaymentWithInvoiceRequest = {
        CompanyName: companyNameCache ?? undefined,
        ScreenId: currentScreen?.id ? parseInt(currentScreen.id.toString(), 10) : undefined,
        AccountId: parseInt(selectedCustomer?.id || customersHook.generalCariRef?.id || '0', 10),
        AccountName: selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari',
        CashboxId: parseInt(option.cashboxId, 10),
        CashboxName: option.cashboxName,
        TotalAmount: mainAmount,
        Currency: 'TRY',
        Description: `${option.label} ile ödeme`,
        CreatedBy: quickSaleUserData?.uid,
        IsRestaurantMode: isRestaurantMode,
        CashierId: currentCashier?.cashierId,
        CashierName: currentCashier?.cashierName,
        OtherPaymentOptionId: option.id,
        OtherPaymentLabel: option.label,
        PaymentProviderType: option.label,
        PaymentProviderName: option.label,
        Items: saleItems.map((item: any) => {
          const itemWarehouseId = (item as any).warehouseId ||
            (screenSettings as any)?.stockVisibility?.defaultWarehouseId ||
            (screenSettings as any)?.stockVisibility?.warehouses?.[0] ||
            (screenSettings as any)?.StockVisibilityJson?.warehouses?.[0] ||
            null;
          if (!itemWarehouseId) {
            throw new Error(`Ürün için depo bilgisi bulunamadı: ${item.name}. Lütfen ekran ayarlarında depo tanımlayın.`);
          }
          return {
            ProductId: item.id,
            ProductName: item.name,
            WarehouseId: itemWarehouseId,
            Quantity: item.quantity,
            Unit: item.unit,
            UnitPrice: item.price,
            VatRate: item.vatRate,
            PriceType: item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED',
            HasRecipe: item.hasRecipe || false,
            HasMenu: item.hasMenu || false,
            SelectedOptions: item.selectedOptions?.map((so: any) => ({
              ItemId: so.itemId,
              ItemName: so.itemName,
              Quantity: so.quantity,
              SelectedExtraOption: so.selectedExtraOption ? {
                OptionItemId: so.selectedExtraOption.optionItemId,
                OptionItemName: so.selectedExtraOption.optionItemName,
                PriceDifference: so.selectedExtraOption.priceDifference,
              } : undefined,
            })),
            TotalPriceDifference: item.totalPriceDifference,
          };
        }),
      };

      console.log('🎟️ [DIŞ BİLET] Satış isteği:', { platform: option.label, cashboxId: option.cashboxId, total: mainAmount });
      const result = await salesService.processOtherPaymentWithInvoice(request);

      if (!result.Success || !result.QuickSaleReceiptId) {
        showSnackbar(result.Message || 'Dış bilet satışı başarısız oldu.', 'error');
        return;
      }

      const receiptId = result.QuickSaleReceiptId;
      console.log('🎟️ [DIŞ BİLET] Satış başarılı, fiş ID:', receiptId);

      const totalDepositAmount = scannedCards.reduce((sum: number, sc: any) => sum + (sc.depositAmount || 0), 0);

      // Depozito var → mevcut "Depozito tahsilatı bekleniyor" akışına devret.
      if (clubEntryDepositActive && totalDepositAmount > 0) {
        const info = {
          cards: scannedCards,
          depositMethod: depositPaymentMethod,
          totalDeposit: totalDepositAmount,
          mainReceiptId: receiptId,
          mainReferenceNumber: result.ReferenceNumber || '',
          mainPaymentMethod: 'OTHER' as const,
          mainPaymentLabel: option.label,
          mainPavoPaySaleNumber: undefined,
          mainAmount,
        };
        pendingDepositInfoRef.current = info;
        setPendingDepositInfo(info);
        setClubEntryDepositPending(true);
        clearCart();
        refreshOrderNumber();
        showSnackbar(`${option.label} satışı alındı. Şimdi depozito tahsilatını yapın.`, 'info');
        return;
      }

      // Depozito yok → kartları + entitlement'ları doğrudan ata.
      const { clubEntryService } = await import('../../../services/clubEntryService');
      const cards = scannedCards.map((sc: any) => ({
        cardUID: sc.cardUID,
        cardLabel: sc.cardLabel,
        depositAmount: sc.depositAmount || 0,
        serviceProductId: sc.assignedServiceItem?.id,
        serviceProductName: sc.assignedServiceItem?.name,
        entryMode: clubEntrySettings?.entryMode || 'SINGLE_ENTRY',
        totalEntryRights: sc.entryRights || 1,
        entitlements: (sc.entitlements || []).map((e: any) => ({
          productId: e.productId,
          productName: e.productName,
          quantity: e.quantity || 1,
          unitPricePaid: e.unitPricePaid || 0,
        })),
      }));

      await clubEntryService.assignCards({
        saleReceiptId: receiptId,
        saleReceiptNumber: result.ReferenceNumber || undefined,
        screenId: currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined,
        paymentMethod: option.label,
        cards,
      });
      showSnackbar(`${cards.length} kart başarıyla atandı`, 'success');

      for (const sc of scannedCards) {
        const cardId = sc.cardId;
        if (!cardId) continue;
        try {
          await cardManagementService.openSession({
            CardId: cardId,
            AccountId: selectedCustomer?.id || customersHook.generalCariRef?.id || '0',
            AccountName: selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari',
            UserId: currentUser?.email || '',
            ScreenId: currentScreen?.id?.toString() || '',
          });
        } catch (sessionErr: any) {
          const backendMsg = sessionErr.response?.data?.message || sessionErr.response?.data?.title || sessionErr.message;
          console.warn('[DIŞ BİLET] Oturum başlatma hatası:', sc.cardUID, backendMsg);
        }
      }

      setPrePaymentScannedCards([]);
      prePaymentScannedCardsRef.current = [];
      depositPaymentMethodRef.current = 'CASH';
      clearCart();
      refreshOrderNumber();
    } catch (err: any) {
      console.error('[DIŞ BİLET] Satış/kart atama hatası:', err);
      showSnackbar(`Dış bilet işlemi hatası: ${err.message}`, 'error');
    } finally {
      externalTicketOptionRef.current = null;
    }
  }, [cart, companyNameCache, currentScreen, selectedCustomer, customersHook, quickSaleUserData, isRestaurantMode, currentCashier, screenSettings, clubEntryDepositActive, clubEntrySettings, currentUser, showSnackbar, clearCart, refreshOrderNumber]);

  // ✅ KULÜP GİRİŞİ — Dış bilet platformu seçildi: kart okutma modalını prepaid modda aç.
  const handleExternalTicketSelected = useCallback((option: any) => {
    externalTicketOptionRef.current = option;
    setPendingPaymentType('OTHER_TICKET');
    setShowPrePaymentCardModal(true);
  }, []);

  // Kulüp girişi: deposito öncesi kart okutma modalı onaylandığında
  const handlePrePaymentCardConfirm = useCallback(async (scannedCards: any[], depositPaymentMethod: 'CASH' | 'POS') => {
    console.log('[KULÜP GİRİŞİ] Kart okutma tamamlandı:', scannedCards.length, 'kart, deposito ödeme:', depositPaymentMethod);
    setShowPrePaymentCardModal(false);
    setPrePaymentScannedCards(scannedCards);
    prePaymentScannedCardsRef.current = scannedCards;
    depositPaymentMethodRef.current = depositPaymentMethod;

    // ⚠️ YENİ AKIŞ: Depozito artık ana satışın sepetine EKLENMEZ. Önce yalnızca
    //   ana (giriş/hizmet) ödemesi alınır; depozito ana ödeme tamamlandıktan sonra
    //   AYRI bir satış olarak tahsil edilir (onClubEntryPaymentComplete → "Depozito
    //   tahsilatı bekleniyor" ekranı). Böylece çıkışta depozito kendi satış no'su
    //   üzerinden TAM İPTAL (CancelSale) ile iade edilebilir.

    // Kısa gecikme ile ödeme işlemini başlat (cart state güncellenmesi için)
    setTimeout(async () => {
      if (pendingPaymentType === 'CASH') {
        if (screenSettings?.showCashChangeModal) {
          openCashChangeModal();
        } else {
          await hookHandleCashPayment();
        }
      } else if (pendingPaymentType === 'POS' && pendingPosArgs) {
        await hookHandlePosPayment(
          pendingPosArgs.bankAccountId,
          pendingPosArgs.bankSubAccountId,
          pendingPosArgs.bankAccountName,
          pendingPosArgs.useFiscalDevice
        );
      } else if (pendingPaymentType === 'PARTIAL') {
        setIsPartialPaymentModalOpen(true);
      } else if (pendingPaymentType === 'OTHER_TICKET') {
        // Dış bilet (FIRSAT ME / BU BİLET): sepeti platform kasasına sat + depozito/kart akışı.
        await runExternalTicketSale(externalTicketOptionRef.current, scannedCards, depositPaymentMethod);
      }

      setPendingPaymentType(null);
      setPendingPosArgs(null);
    }, 300);
  }, [pendingPaymentType, pendingPosArgs, hookHandleCashPayment, hookHandlePosPayment, setCart, screenSettings, cart, runExternalTicketSale]);

  // Kulüp girişi: kart okutma modalında "Kartsız Devam Et" seçilince — bu işlemde
  // kart verilmez, depozito alınmaz; ödeme doğrudan normal hızlı satış gibi alınır.
  const handlePrePaymentCardSkip = useCallback(async () => {
    console.log('[KULÜP GİRİŞİ] "Kartsız Devam Et" seçildi — normal satış akışına geçiliyor');
    skipCardForCurrentSaleRef.current = true;
    setShowPrePaymentCardModal(false);
    setPrePaymentScannedCards([]);
    prePaymentScannedCardsRef.current = [];
    depositPaymentMethodRef.current = 'CASH';

    setTimeout(async () => {
      if (pendingPaymentType === 'CASH') {
        if (screenSettings?.showCashChangeModal) {
          openCashChangeModal();
        } else {
          await hookHandleCashPayment();
        }
      } else if (pendingPaymentType === 'POS' && pendingPosArgs) {
        await hookHandlePosPayment(
          pendingPosArgs.bankAccountId,
          pendingPosArgs.bankSubAccountId,
          pendingPosArgs.bankAccountName,
          pendingPosArgs.useFiscalDevice
        );
      } else if (pendingPaymentType === 'PARTIAL') {
        setIsPartialPaymentModalOpen(true);
      }

      setPendingPaymentType(null);
      setPendingPosArgs(null);
    }, 300);
  }, [pendingPaymentType, pendingPosArgs, hookHandleCashPayment, hookHandlePosPayment, screenSettings]);

  // Nakit ödeme wrapper - sepet bölünmüşse modalı aç, para üstü modalı aktifse göster
  const handleCashPaymentForQuickSale = async () => {
    debugLog("[handleCashPaymentForQuickSale] Nakit ödeme işlemi başlatılıyor...");
    skipCardForCurrentSaleRef.current = false;
    if (cart.length === 0) {
      showSnackbar('Sepet boş! Ödeme yapılamaz.', 'warning');
      return;
    }
    
    if (selectedFiscalDevice === 'ingenico_5000f' && calculateTotal() > INGENICO_FIS_LIMIT_TL) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${INGENICO_FIS_LIMIT_TL.toLocaleString('tr-TR')} TL) aşmaktadır.\n\nIngenico 5000F cihazı bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if ((selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') && screenSettings?.useFiscalDeviceForCash && calculateTotal() > getBekoFisLimitTL()) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${getBekoFisLimitTL().toLocaleString('tr-TR')} TL) aşmaktadır.\n\nBeko yazarkasa bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if (isSplitCartActive) {
      console.log('🔄 [SPLIT CART] Sepet bölünmüş, ödeme için sepet bölme modalı açılıyor...');
      setSplitCartModalOpen(true);
      return;
    }

    // Kulüp girişi: deposito aktifken ödeme öncesi kart okutma (kart akışı açıksa)
    if (clubEntryCardFlowEnabled && clubEntryDepositActive && prePaymentScannedCards.length === 0) {
      const serviceItems = getServiceItemsFromCart();
      if (serviceItems.length > 0) {
        setPendingPaymentType('CASH');
        setShowPrePaymentCardModal(true);
        return;
      }
    }
    
    if (screenSettings?.showCashChangeModal) {
      openCashChangeModal();
      return;
    }
    
    // 💰 ÖN-TETİK: Tüm cart/validation kontrolleri geçti, ödeme başlamadan
    // önce çekmeceyi tetikle → kasiyer SQL+print beklemek zorunda kalmaz.
    triggerCashDrawer('cash-button');
    await hookHandleCashPayment();
  };

  // ✅ Para üstü modalından ödeme onayı
  const handleCashChangeConfirm = async (receivedAmount: number, changeAmount: number) => {
    // Fişe basılmak üzere sakla (para üstü yoksa fişte satır gösterilmez)
    cashChangeInfoRef.current = changeAmount > 0 ? { receivedAmount, changeAmount } : null;
    setCashChangeModalOpen(false);
    // 💰 ÖN-TETİK: Para üstü modalı onaylandı, ödeme başlıyor
    triggerCashDrawer('cash-change-confirm');
    await hookHandleCashPayment();
  };

  // POS ödeme wrapper - sepet bölünmüşse modalı aç

  const handlePosPaymentForQuickSale = async (
    bankAccountId: number,
    bankSubAccountId: number | null,
    bankAccountName: string,
    useFiscalDevice: boolean
  ) => {
    debugLog("[handlePosPaymentForQuickSale] POS ödeme işlemi başlatılıyor...");
    skipCardForCurrentSaleRef.current = false;
    if (cart.length === 0) {
      showSnackbar('Sepet boş! Ödeme yapılamaz.', 'warning');
      return;
    }
    
    if (selectedFiscalDevice === 'ingenico_5000f' && useFiscalDevice && calculateTotal() > INGENICO_FIS_LIMIT_TL) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${INGENICO_FIS_LIMIT_TL.toLocaleString('tr-TR')} TL) aşmaktadır.\n\nIngenico 5000F cihazı bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if ((selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') && useFiscalDevice && calculateTotal() > getBekoFisLimitTL()) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${getBekoFisLimitTL().toLocaleString('tr-TR')} TL) aşmaktadır.\n\nBeko yazarkasa bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if (isSplitCartActive) {
      console.log('🔄 [SPLIT CART] Sepet bölünmüş, ödeme için sepet bölme modalı açılıyor...');
      setSplitCartModalOpen(true);
      return;
    }

    // Kulüp girişi: deposito aktifken ödeme öncesi kart okutma (kart akışı açıksa)
    if (clubEntryCardFlowEnabled && clubEntryDepositActive && prePaymentScannedCards.length === 0) {
      const serviceItems = getServiceItemsFromCart();
      if (serviceItems.length > 0) {
        setPendingPaymentType('POS');
        setPendingPosArgs({ bankAccountId, bankSubAccountId, bankAccountName, useFiscalDevice });
        setShowPrePaymentCardModal(true);
        return;
      }
    }
    
    await hookHandlePosPayment(bankAccountId, bankSubAccountId, bankAccountName, useFiscalDevice);
  };

  /**
   * Rezervasyondan "Giriş Yap" akışında POS + deposito>0 senaryosu.
   * ClubEntryReservationsModal kart okutmayı tamamladıktan sonra bu callback'i çağırır.
   *
   * Akış (QuickSale complete-sale TAMAMEN BYPASS):
   *  1) PavoPay'e doğrudan Rest-P2P isteği gönderilir (sadece deposito tutarı).
   *     Bilet ücreti online ödendiği için sepete/fatura/processPartialWithInvoice'a
   *     dokunmuyoruz. Sepet kullanmadığımız için "Items boş" validasyon hatası alınmaz.
   *  2) PavoPay başarılı olunca (completed / continue_without_fiscal) backend'e
   *     `assignCardsFromReservation({depositAlreadyCollected: false})` çağrılır:
   *      - Backend `BankAccountManager.DepositAsync` ile muhasebe kaydını yapar
   *      - Kartlar rezervasyona bağlanır, rezervasyon Consumed olarak işaretlenir
   *      - Her kart için openSession yapılır
   *  3) PavoPay fail/cancel → backend çağrısı YAPILMAZ; rezervasyon Approved kalır,
   *     kullanıcı tekrar deneyebilir.
   */
  const handleReservationPosCheckInRequest = useCallback(
    async (
      reservation: any,
      cards: any[],
      expectedCardCount: number
    ): Promise<void> => {
      console.log('[REZERVASYON CHECK-IN] POS akışı tetikleniyor (standalone PavoPay)', {
        reservationId: reservation?.id,
        cardCount: cards.length,
        expectedCardCount,
      });

      const totalDeposit = cards.reduce((sum, c) => sum + (c.depositAmount || 0), 0);
      if (totalDeposit <= 0) {
        showSnackbar('Deposito tutarı 0 — POS akışı uygulanamaz', 'warning');
        return;
      }

      // POS option'ı seç — backend muhasebe kaydı için bankAccountId gerekiyor
      const posOption: any = posOptions.find((p: any) => p.bankAccountId);
      if (!posOption) {
        showSnackbar('POS ödeme seçeneği tanımlı değil. Ekran ayarlarından POS ekleyin.', 'error');
        return;
      }
      const bankAccountIdNum = parseInt(String(posOption.bankAccountId), 10);
      if (!Number.isFinite(bankAccountIdNum)) {
        showSnackbar('POS banka hesabı geçersiz', 'error');
        return;
      }

      // 1) PavoPay'e DOĞRUDAN tahsilat isteği — QuickSale akışına hiç girmiyoruz.
      //    runRestP2PFlow kendi modalını açar, outcome ('completed'|'failed'|'continue_without_fiscal') döner.
      const outcome = await runRestP2PFlow(
        'pos',
        [{ method: 'pos', amount: totalDeposit, label: 'Kart Depozito (Rezervasyon)', order: 0 }],
        totalDeposit,
      );

      console.log('[REZERVASYON CHECK-IN] PavoPay outcome:', outcome);

      if (outcome !== 'completed' && outcome !== 'continue_without_fiscal') {
        showSnackbar('Deposito tahsilatı tamamlanmadı, rezervasyon güncellenmedi', 'warning');
        return;
      }

      // 2) Başarılı → kart atama + muhasebe kaydı (backend `BankAccountManager.DepositAsync` çağırır)
      try {
        const { clubEntryService } = await import('../../../services/clubEntryService');
        const cardAssignments = cards.map((sc: any) => ({
          cardUID: sc.cardUID,
          cardLabel: sc.cardLabel,
          depositAmount: sc.depositAmount || 0,
          serviceProductId: sc.assignedServiceItem?.id,
          serviceProductName: sc.assignedServiceItem?.name,
          entryMode: clubEntrySettings?.entryMode || 'SINGLE_ENTRY',
          totalEntryRights: sc.entryRights || 1,
        }));

        await clubEntryService.assignCardsFromReservation({
          ticketReservationId: reservation.id,
          screenId: currentScreen?.id ? parseInt(currentScreen.id, 10) : undefined,
          depositPaymentMethod: 'POS',
          depositBankAccountId: bankAccountIdNum,
          cards: cardAssignments,
          depositAlreadyCollected: false,
        });

        console.log('[REZERVASYON CHECK-IN] Kartlar atandı, muhasebe kaydı backend tarafından yapıldı');
        showSnackbar(`${cardAssignments.length} kart atandı, rezervasyon kullanıldı olarak işaretlendi`, 'success');

        // 3) Her kart için oturum başlat
        for (const sc of cards) {
          const cardId = sc.cardId;
          if (!cardId) continue;
          try {
            await cardManagementService.openSession({
              CardId: cardId,
              AccountId: selectedCustomer?.id || customersHook.generalCariRef?.id || '0',
              AccountName: selectedCustomer?.companyName || customersHook.generalCariRef?.companyName || 'Genel Cari',
              UserId: currentUser?.email || '',
              ScreenId: currentScreen?.id?.toString() || '',
            });
            console.log('[REZERVASYON CHECK-IN] Oturum başlatıldı:', sc.cardUID);
          } catch (sessionErr: any) {
            const backendMsg = sessionErr.response?.data?.message || sessionErr.response?.data?.title || sessionErr.message;
            console.warn('[REZERVASYON CHECK-IN] Oturum başlatma hatası:', sc.cardUID, backendMsg);
          }
        }
      } catch (err: any) {
        console.error('[REZERVASYON CHECK-IN] Kart atama hatası:', err);
        const status = err?.response?.status;
        const backendMsg = err?.response?.data?.message;
        let userMsg = backendMsg || `Kart atama hatası: ${err.message}`;
        if (status === 409) userMsg = 'Bu rezervasyon daha önce kullanıldı';
        showSnackbar(userMsg, 'error');
      }
    },
    [
      runRestP2PFlow,
      showSnackbar,
      clubEntrySettings,
      currentScreen,
      selectedCustomer,
      customersHook,
      currentUser,
      posOptions,
    ],
  );

  const handleOpenAccountPayment = async () => {
    debugLog("[handleOpenAccountPayment] Açık hesap ödeme işlemi başlatılıyor...");
    if (cart.length === 0) {
      showSnackbar('Sepet boş! Ödeme yapılamaz.', 'warning');
      return;
    }
    await hookHandleOpenAccountPayment();
  };

  const handlePartialPayment = () => {
    skipCardForCurrentSaleRef.current = false;
    if (cart.length === 0) {
      showAlertModal("Uyarı", "Parçalı ödeme için sepette ürün olmalıdır.");
      return;
    }
    
    if (selectedFiscalDevice === 'ingenico_5000f' && calculateTotal() > INGENICO_FIS_LIMIT_TL) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${INGENICO_FIS_LIMIT_TL.toLocaleString('tr-TR')} TL) aşmaktadır.\n\nIngenico 5000F cihazı bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if ((selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') && screenSettings?.useFiscalDeviceForCash && calculateTotal() > getBekoFisLimitTL()) {
      showAlertModal(
        'Mali Fiş Limiti Aşıldı',
        `Sepet toplamı ${calculateTotal().toFixed(2)} TL ile mali fiş limitini (${getBekoFisLimitTL().toLocaleString('tr-TR')} TL) aşmaktadır.\n\nBeko yazarkasa bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
      );
      return;
    }
    
    if (isSplitCartActive) {
      console.log('🔄 [SPLIT CART] Sepet bölünmüş, ödeme için sepet bölme modalı açılıyor...');
      setSplitCartModalOpen(true);
      return;
    }

    // Kulüp girişi: deposito aktifken ödeme öncesi kart okutma (kart akışı açıksa)
    if (clubEntryCardFlowEnabled && clubEntryDepositActive && prePaymentScannedCards.length === 0) {
      const serviceItems = getServiceItemsFromCart();
      if (serviceItems.length > 0) {
        setPendingPaymentType('PARTIAL');
        setShowPrePaymentCardModal(true);
        return;
      }
    }
    
    setIsPartialPaymentModalOpen(true);
  };

  /**
   * ✅ YENİ: Local service kısayollarının gövdesi.
   *
   * Her dal, karşılık gelen butonun kendi devre dışı koşulunu tekrarlar; aksi
   * halde kısayol, ekranda basılamayacak bir butonu tetiklerdi. Ödeme
   * komutlarında onay sorulmaz — kısayolun amacı tek basışta kapatmak.
   */
  signalHandlersRef.current = {
    'cash-payment': () => {
      if (isProcessingPayment || cart.length === 0) return;
      void handleCashPaymentForQuickSale();
    },

    'pos-payment': () => {
      if (isProcessingPayment || cart.length === 0) return;
      posButtonClickRef.current();
    },

    'partial-payment': () => {
      if (cart.length === 0 || prepaidSystemEnabled) return;
      handlePartialPayment();
    },

    'discount': () => {
      if (loadingUserPermissions || !currentUserPermissions?.applyDiscount) return;
      handleOpenDiscountModal();
    },

    'hold-sale': () => {
      if (cart.length === 0 || prepaidSystemEnabled) return;
      handleHoldSale();
    },

    'split-cart': () => {
      // Kulüp girişi modunda sepet bölme butonu hiç gösterilmiyor
      if (cart.length === 0 || isClubEntryMode) return;
      setSplitCartModalOpen(true);
    },

    'cart-increase': () => {
      if (selectedCartItemId === null) return;
      if (isRestaurantMode) {
        if (!waiterPermissions.canAddProduct) return;
        handleIncreaseSelectedWithRestaurant();
        return;
      }
      handleIncreaseSelected();
    },

    'cart-decrease': () => {
      if (selectedCartItemId === null) return;
      if (isRestaurantMode) {
        // ActionControlsPanel'deki efektif silme izniyle aynı kural
        const canRemoveEffective = waiterPermissions.canRemoveProduct || waiterPermissions.canDeleteUnprintedItems;
        if (!canRemoveEffective) return;
        handleDecreaseSelectedWithRestaurant();
        return;
      }
      handleDecreaseSelected();
    },

    // Sayısal tuş takımı yalnızca Miktar modu açıkken dinlenir. handleNumpadClick
    // kullanılmaz; o, arama kutusu odaktaysa değeri barkod kutusuna yazıyor ve
    // barkod okutma alanı burada her zaman odakta.
    'numpad-key': (value?: any) => {
      if (!isQuantityModeActive || typeof value !== 'string') return;
      handleNumpadForQuantity(value);
    },
  };

  // ✅ HOOK'TAN GELİYOR: handleOpenUISettingsModal
  // Bu fonksiyon yukarıda useQuickSaleUI hook'undan geliyor (556 satırı)

  const handleUseFiscalDeviceForCashChange = useCallback(async (enabled: boolean) => {
    const screenIdNum = currentScreen?.id ? parseInt(currentScreen.id, 10) : null;
    if (!screenIdNum) return;
    try {
      await salesService.updateQuickSaleScreen({ Id: screenIdNum, UseFiscalDeviceForCash: enabled });
      if (screenSettings) {
        setScreenSettings({ ...screenSettings, useFiscalDeviceForCash: enabled });
      }
      
      const settingsKey = companyId && currentScreen?.id ? `quickSaleSettings_${companyId}_${currentScreen.id}` : null;
      if (settingsKey) {
        try {
          const stored = localStorage.getItem(settingsKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            parsed.useFiscalDeviceForCash = enabled;
            localStorage.setItem(settingsKey, JSON.stringify(parsed));
          }
        } catch (e) { /* ignore */ }
      }
      console.log(`✅ ÖKC ayarı güncellendi: ${enabled ? 'AKTİF' : 'KAPALI'}`);
    } catch (err) {
      console.error('❌ ÖKC ayarı güncellenemedi:', err);
    }
  }, [currentScreen?.id, companyId, screenSettings, setScreenSettings]);

  // ✅ MOVED TO HOOK: getWarehouseNames, validateSearchPrerequisites, searchAndAddProductToCart, handleSearchSubmit
  // - useQuickSaleProductSearch hook'una taşındı

  /**
   * Autocomplete için server-side ürün arama (debounce ve min 3 karakter ProductSearchBar'da yönetilir).
   * Şube deposu varsa sadece o depodaki ürünler döndürülür; yoksa tüm şirket ürünleri döner.
   */
  const handleAutocompleteSearch = useCallback(async (term: string): Promise<any[]> => {
    if (!companyId || term.trim().length < 3) return [];
    try {
      const results = await salesService.searchProducts(term.trim());
      if (!branchWarehouseId) return results;
      // Şube deposu aktif → sadece o depoda stok kaydı olan ürünleri göster
      return results.filter((p: any) => {
        const stocks: any[] = p.warehouseStocks || p.WarehouseStocks || [];
        return stocks.some(
          (ws: any) =>
            (ws.warehouseId?.toString() || ws.WarehouseId?.toString()) === branchWarehouseId
        );
      });
    } catch {
      return [];
    }
  }, [companyId, branchWarehouseId]);

  // Tüm Kategorileri Çek - ✅ HOOK'TAN GELİYOR (useQuickSaleData)
  // Eski useEffect silindi, artık hook içinde

  // Ayarları veritabanından localStorage'a çeken ve güncelleyen fonksiyon
  // ✅ SQL'DEN ÇEKİYOR (useQuickSaleData) - Wrapper fonksiyon
  const refreshScreenSettings = useCallback(async () => {
    if (!companyId || !screenId) {
      showSnackbar("Ayarlar güncellenemedi: Şirket veya Ekran ID eksik.", "error");
      return;
    }

    setRefreshingSettings(true);
    try {
      // ✅ Hook'tan gelen fonksiyonu çağır - bu artık SQL'den çekiyor
      await refreshScreenSettingsFromHook();
      
      // ✅ SQL'den gelen ayarları kullan (hook zaten tüm ayarları yükledi)
      if (screenSettings) {
        // Seçili depo ID'lerini güncelle
        if (screenSettings.stockVisibility?.warehouses) {
          setSelectedWarehouseIds(screenSettings.stockVisibility.warehouses);
        }

        // Cari giriş işlemleri ayarını güncelle
        const allowAccountCreation = screenSettings.allowAccountCreationOnSale || false;
        setAllowAccountCreationOnSale(allowAccountCreation);
        debugLog(`[QuickSaleScreen.tsx] Cari giriş işlemleri: ${allowAccountCreation ? 'AÇIK' : 'KAPALI'}`);
        
        showSnackbar("Ayarlar başarıyla güncellendi.", "success");
      } else {
        showSnackbar("Ayarlar bulunamadı.", "warning");
      }
      
    } catch (error) {
      console.error("Ayarlar güncellenirken hata:", error);
      showSnackbar("Ayarlar güncellenirken bir hata oluştu.", "error");
    } finally {
      setRefreshingSettings(false);
    }
  }, [companyId, screenId, showSnackbar, setSelectedWarehouseIds, setAllowAccountCreationOnSale, refreshScreenSettingsFromHook, screenSettings]);

  /**
   * EFFECT 3: Initialize settings from localStorage
   * @trigger screenId, companyId changes
   * @purpose Load cached settings on mount/screen change
   */
  useEffect(() => {
    if (screenId && companyId) {
      const settingsKey = `${STORAGE_KEYS.QUICKSALE_SETTINGS_PREFIX}${companyId}_${screenId}`;
      try {
        const savedSettingsJson = localStorage.getItem(settingsKey);
        
        if (savedSettingsJson) {
          const savedSettings = JSON.parse(savedSettingsJson);
          debugLog("[QuickSaleScreen] localStorage'dan ayarlar yüklendi:", savedSettings);
          
          // ⚠️ NOT: SQL'den gelen ayarlar useQuickSaleData hook'unda screenSettings'e yükleniyor
          // localStorage'daki eski değerler SQL değerlerini override etmemeli
          // Bu yüzden sadece localStorage'a özgü ayarları (stockVisibility gibi) burada kullanıyoruz
          // useCashierSystem, useKitchenScreen gibi SQL tabanlı boolean ayarlar
          // useQuickSaleData.ts tarafından SQL'den okunuyor ve öncelikli
          
          // ❌ ESKİ: setScreenSettings(savedSettings); - Bu SQL değerlerini eziyor!
          // ✅ YENİ: Sadece localStorage'a özgü ayarları uygula, SQL boolean değerlerini ezme
          // screenSettings zaten useQuickSaleData'dan geliyor, burada sadece ek ayarları ekle
          debugLog("[QuickSaleScreen] 👤 localStorage'dan okunan useCashierSystem:", savedSettings.useCashierSystem);
          debugLog("[QuickSaleScreen] ℹ️ SQL değerleri useQuickSaleData hook'undan gelecek");
          
          // Sadece bu ekran için geçerli olan ayarları doğrudan uygula
          if (savedSettings.stockVisibility?.warehouses) {
            setSelectedWarehouseIds(savedSettings.stockVisibility.warehouses);
          }
          
          // ✅ Ön ödemeli sistem modu ve ürün resimleri artık hook'tan geliyor
          // setPrepaidSystemEnabled ve setShowProductImages kaldırıldı
            debugLog("[QuickSaleScreen] Ön ödemeli sistem modu:", savedSettings.prepaidSystemEnabled ? "AÇIK" : "KAPALI");
          
          // YENİ: Nakit ÖKC kullanım ayarını yükle ve logla
          if (typeof savedSettings.useFiscalDeviceForCash === 'boolean') {
            // Bu ayar doğrudan screenSettings üzerinden kullanılacağı için ayrı bir state'e set etmiyoruz.
            // screenSettings state'i zaten yukarıda setScreenSettings(savedSettings) ile güncelleniyor.
            // console.log("[QuickSaleScreen] Nakit için ÖKC Kullanım Ayarı (LocalStorage'dan):", savedSettings.useFiscalDeviceForCash);
          }
          
          // Son güncelleme zamanını göster
          if (savedSettings.lastUpdated) {
            const lastUpdateDate = new Date(savedSettings.lastUpdated);
            setLastSettingsRefresh(lastUpdateDate.toLocaleTimeString());
          }
          
          // YENİ: Cari giriş işlemleri ayarını ayarla
          if (typeof savedSettings.allowAccountCreationOnSale === 'boolean') {
            setAllowAccountCreationOnSale(savedSettings.allowAccountCreationOnSale);
            debugLog("[QuickSaleScreen] Cari giriş işlemleri:", savedSettings.allowAccountCreationOnSale ? "AÇIK" : "KAPALI");
          }
        } else {
          debugLog("[QuickSaleScreen] localStorage'da ayar bulunamadı, useQuickSaleData hook'u SQL'den yükleyecek.");
          // ✅ localStorage'da ayar yoksa, useQuickSaleData hook'u zaten SQL'den yüklüyor, ekstra çağrı yapmaya gerek yok
        }
      } catch (error) {
        console.error("localStorage'dan ayarlar okunurken hata:", error);
        // ✅ Hata durumunda da useQuickSaleData hook'u zaten SQL'den yüklüyor
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenId, companyId]); // refreshScreenSettings bağımlılıktan çıkarıldı - sadece mount/unmount'ta çalışmalı

  // ✅ YENİ: Tüm yazarkasa entegrasyonları için localServiceUrl'u set et
  useEffect(() => {
    console.log('[QuickSaleScreen] 🔌 LocalServiceUrl Effect - screenSettings?.localServiceUrl:', screenSettings?.localServiceUrl);
    if (screenSettings?.localServiceUrl) {
      const url = screenSettings.localServiceUrl;
      console.log('[QuickSaleScreen] 🔌 Yazarkasa servisleri için yerel servis URL ayarlanıyor:', url);
      setPavoPayLocalServiceUrl(url);
      setHuginLocalServiceUrl(url);
      setInposLocalServiceUrl(url);
      setBekoTokenLocalServiceUrl(url);
      setBekoTargetDevice(screenSettings?.selectedFiscalDevice || '');
      setIngenicoLocalServiceUrl(url);
    }
  }, [screenSettings?.localServiceUrl]);

  // Local service guncelleme kontrolu
  const {
    hasPendingUpdate: hasLocalServiceUpdate,
    version: localServiceUpdateVersion,
    dismissed: localServiceUpdateDismissed,
    applyUpdateAndRestart: applyLocalServiceUpdate,
    dismiss: dismissLocalServiceUpdate,
  } = useLocalServiceUpdateCheck(screenSettings?.localServiceUrl);

  const [showLocalServiceUpdateModal, setShowLocalServiceUpdateModal] = useState(false);

  useEffect(() => {
    if (hasLocalServiceUpdate && !localServiceUpdateDismissed && cart.length === 0) {
      setShowLocalServiceUpdateModal(true);
    }
  }, [hasLocalServiceUpdate, localServiceUpdateDismissed, cart.length]);

  // ✅ MOVED TO HOOK: allowAccountCreationOnSale - useQuickSaleCustomers hook'undan geliyor
  // ✅ MOVED TO HOOK: isCustomerSelectionModalOpen - useQuickSaleCustomers hook'undan geliyor
  
  // Ön ödemeli kart modunda ekran açılışında ve ödeme sonrası kart arama modalını aç
  // Kasiyer sistemi aktifse, kasiyer giriş yapmadan kart modalı açılmamalı
  useEffect(() => {
    if (prepaidSystemEnabled && !prepaidCard && cart.length === 0 && !isPrepaidCardSearchOpen) {
      const cashierRequired = !isRestaurantMode && screenSettings?.useCashierSystem;
      if (cashierRequired && !currentCashier) {
        return;
      }
      setIsPrepaidCardSearchOpen(true);
    }
  }, [prepaidSystemEnabled, prepaidCard, cart.length, isPrepaidCardSearchOpen, isRestaurantMode, screenSettings?.useCashierSystem, currentCashier]);

  // ✅ MOVED TO HOOK: Müşteri modal handler fonksiyonları - useQuickSaleCustomers hook'undan geliyor
  // handleCloseCustomerSelectionModal, handleSelectCustomerFromModal, handleOpenCustomerSelectionModal
  
  // Müşteri seçildikten sonra arama kutusuna odaklan (handleSelectCustomerFromModal wrapper)
  const handleSelectCustomerFromModalWrapper = useCallback((customer: Customer) => {
    handleSelectCustomerFromModal(customer); // Hook'tan gelen fonksiyonu çağır
    
    // Müşteri seçildikten sonra arama kutusuna odaklan
    setTimeout(() => {
      if (searchBarRef.current) {
        searchBarRef.current.focus();
      }
    }, LAYOUT.TIMEOUTS.FOCUS_DELAY);
  }, [handleSelectCustomerFromModal]);

  /**
   * EFFECT 4: Fetch user permissions
   * @trigger companyId, currentScreen, quickSaleUserData, currentUser, allPersonnel changes
   * @purpose Load user-specific permissions based on role and screen settings
   * ✅ GÜNCELLEME: Kasiyer sistemi aktifse, kasiyer yetkileri kullanılır (handleCashierLogin'de set edilir)
   */
  useEffect(() => {
    const fetchUserPermissions = async () => {
      if (!companyId || !currentScreen || !quickSaleUserData || !currentUser) {
        setLoadingUserPermissions(false);
        setCurrentUserPermissions(null);
        return;
      }

      setLoadingUserPermissions(true);
      const { role, email } = quickSaleUserData;
      const screenIdToUse = currentScreen.id;

      // ✅ Restoran modunda kasiyer giriş yapmışsa, kasiyerin yetkilerini kullan
      if (isRestaurantMode && currentCashier?.permissions) {
        console.log('🔐 [QuickSaleScreen] Restoran kasiyer yetkileri set ediliyor:', currentCashier.cashierName);
        setCurrentUserPermissions({
          changePrice: currentCashier.permissions.canChangePrice || false,
          applyDiscount: currentCashier.permissions.canApplyDiscount || false,
          cancel: currentCashier.permissions.canCancelSale || false,
          viewReports: currentCashier.permissions.canViewReports || false,
          warehouseOperations: currentCashier.permissions.canWarehouseOperations || false,
          viewAccountBalance: currentCashier.permissions.canViewAccountBalance || false,
          listAccounts: currentCashier.permissions.canListAccounts || false,
          canQuickAddItem: currentCashier.permissions.canQuickAddItem || false,
          canAddAccount: currentCashier.permissions.canAddAccount || false,
          canPosRefund: currentCashier.permissions.canPosRefund || false,
          canClearCart: currentCashier.permissions.canClearCart ?? true,
          canViewEndOfDay: currentCashier.permissions.canViewEndOfDay || false,
        });
        setLoadingUserPermissions(false);
        return;
      }

      // ✅ YENİ: Kasiyer sistemi aktifse ve restoran modunda değilsek
      // Kasiyer giriş yapana kadar yetkileri sıfırla (handleCashierLogin'de set edilecek)
      if (!isRestaurantMode && screenSettings?.useCashierSystem) {
        console.log('🔐 [QuickSaleScreen] Kasiyer sistemi aktif, yetkiler kasiyer girişinde belirlenecek');
        // Eğer zaten kasiyer giriş yapmışsa yetkileri değiştirme
        if (!currentCashier) {
          setCurrentUserPermissions({
            changePrice: false,
            applyDiscount: false,
            cancel: false,
            viewReports: false,
            warehouseOperations: false,
            viewAccountBalance: false,
            listAccounts: false,
            canQuickAddItem: false,
            canAddAccount: false
          });
        }
        setLoadingUserPermissions(false);
        return;
      }

      if (role === 'admin') {
        setCurrentUserPermissions({
          changePrice: true,
          applyDiscount: true,
          cancel: true,
          viewReports: true,
          warehouseOperations: true,
          viewAccountBalance: true,
          listAccounts: true,
          canQuickAddItem: true,
          canAddAccount: true
        });
        setLoadingUserPermissions(false);
        return;
      }

      let currentPersonnelId: string | null = null;
      if (email && allPersonnel.length > 0) {
        const matchedPersonnel = allPersonnel.find(p => p.email?.toLowerCase() === email.toLowerCase());
        if (matchedPersonnel) {
          currentPersonnelId = matchedPersonnel.id;
        }
      }

      if (!currentPersonnelId) {
        console.warn(`[QuickSaleScreen.tsx] Admin olmayan kullanıcı (${email}) için personel ID bulunamadı.`);
        setCurrentUserPermissions(null);
        setLoadingUserPermissions(false);
        return;
      }

      // ✅ SQL: izinler QuickSaleScreen.PersonnelPermissionsJson'dan gelir (screenSettings.personnelPermissions).
      // Eski RTDB okumasi (quickSaleScreens/.../settings/personnelPermissions) kaldirildi; tek kaynak SQL.
      const permissionsMap = ((screenSettings as any)?.personnelPermissions || {}) as { [personnelId: string]: PersonnelPermissions };
      const userPermissions = permissionsMap[currentPersonnelId];
      if (userPermissions) {
        setCurrentUserPermissions(userPermissions);
      } else {
        setCurrentUserPermissions({
          changePrice: false,
          applyDiscount: false,
          cancel: false,
          viewReports: false,
          warehouseOperations: false,
          canQuickAddItem: false,
          canAddAccount: false
        });
      }
      setLoadingUserPermissions(false);
    };

    fetchUserPermissions();
  }, [companyId, currentScreen, quickSaleUserData, currentUser, allPersonnel, isRestaurantMode, screenSettings?.useCashierSystem, (screenSettings as any)?.personnelPermissions, currentCashier]);

  // ✅ REFACTORED: addWaiterAction artık useRestaurantCartOperations hook'undan geliyor

  /**
   * ✅ YENİ: Normal Hızlı Satış - Sepete Ekle + Fokus Wrapper
   * Ürün kartlarından sepete ekleme için kullanılır
   * ✅ GÜNCELLEME: Kasiyer sistemi aktifse ve kasiyer giriş yapmadıysa ürün eklemeyi engelle
   */
  const addToCartWithFocus = useCallback((product: any, quantityToAdd: number = 1) => {
    console.log('┌═════════════════════════════════════════════════════════┐');
    console.log('║ 🖱️ ÜRÜN KARTINA TIKLANDI (addToCartWithFocus)          ║');
    console.log('└═════════════════════════════════════════════════════════┘');
    console.log('📦 [ÜRÜN KART]:', {
      productId: product.id,
      productName: product.name,
      price: product.price || product.salePrice,
      eklenecekMiktar: quantityToAdd,
      isRestaurantMode: isRestaurantMode
    });
    
    // ✅ YENİ: Normal hızlı satışta kasiyer sistemi kontrolü
    // Restoran modunda bu fonksiyon kullanılmaz (addToCartWithRestaurant kullanılır)
    if (!isRestaurantMode && screenSettings?.useCashierSystem && !currentCashier) {
      console.log('🚫 [KASİYER KONTROL] Kasiyer sistemi aktif ama kasiyer giriş yapmamış!');
      console.log('🔐 [KASİYER KONTROL] Kasiyer giriş modalı açılıyor...');
      setPendingProductForCashierLogin(product);
      setPendingQuantityForCashierLogin(quantityToAdd);
      setIsCashierLoginModalOpen(true);
      return;
    }

    // Beko cihaz limitleri kontrolü
    const isBekoActive = screenSettings?.useFiscalDeviceForCash && 
      (selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr');

    // ✅ Cihazdan bağımsız sepet kalem limiti (max GLOBAL_KALEM_LIMIT farklı kalem).
    // Sadece YENİ ürün eklenirken kontrol edilir; mevcut ürünün miktarı arttırılırken
    // sepet uzunluğu zaten değişmez. Beko aktifken aşağıdaki BEKO_KALEM_LIMIT
    // (Beko'nun kendi cihaz limiti) devreye girer ve global kontrol atlanır.
    if (!isBekoActive) {
      const isNewItemGlobal = !cart.some(item => item.id === product.id);
      if (isNewItemGlobal && cart.length >= GLOBAL_KALEM_LIMIT) {
        showAlertModal(
          'Sepet Kalem Limiti Doldu',
          `Sepete en fazla ${GLOBAL_KALEM_LIMIT} farklı kalem eklenebilir.\n\nLütfen mevcut sepeti tamamlayın veya bazı ürünleri sepetten çıkarın.`
        );
        return;
      }
    }
    
    if (isBekoActive) {
      const isNewItem = !cart.some(item => item.id === product.id);
      if (isNewItem && cart.length >= BEKO_KALEM_LIMIT) {
        showAlertModal(
          'Kalem Limiti Aşıldı',
          `Sepette ${cart.length} farklı ürün kalemi bulunmaktadır.\n\nBeko yazarkasa tek fişte en fazla ${BEKO_KALEM_LIMIT} farklı kalem desteklemektedir.\n\nLütfen önce mevcut sepeti tamamlayın veya sepet bölme özelliğini kullanın.`
        );
        return;
      }
      
      const productPrice = product.price || product.salePrice || 0;
      const newTotal = calculateTotal() + (productPrice * quantityToAdd);
      const bekoLimit = getBekoFisLimitTL();
      if (newTotal > bekoLimit) {
        showAlertModal(
          'Mali Fiş Limiti Aşıldı',
          `Bu ürünü ekledikten sonra sepet toplamı ${newTotal.toFixed(2)} TL olacak ve mali fiş limitini (${bekoLimit.toLocaleString('tr-TR')} TL) aşacaktır.\n\nBeko yazarkasa bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
        );
        return;
      }
      
      // Kısım limiti kontrolü
      const sectionLimitError = checkBekoSectionLimit(product, quantityToAdd, cart);
      if (sectionLimitError) {
        showAlertModal('Kısım Limiti Aşıldı', sectionLimitError);
        return;
      }
    }
    
    console.log('⏳ [addToCart ÇAĞRILIYOR...]');
    addToCart(product, quantityToAdd);
    console.log('✅ [addToCart TAMAMLANDI]');
    
    if (isQuantityModeActive) {
      setIsQuantityModeActive(false);
      setQuantityInputValue('');
    }
    
    console.log('🎯 [FOKUS] focusSearchBar çağrılıyor...');
    focusSearchBar();
    console.log('✅ [addToCartWithFocus TAMAMLANDI]');
  }, [addToCart, focusSearchBar, isRestaurantMode, screenSettings?.useCashierSystem, currentCashier, screenSettings?.useFiscalDeviceForCash, selectedFiscalDevice, cart, calculateTotal, showAlertModal, isQuantityModeActive]);

  /**
   * ✅ YENİ: Restaurant Modu - Sepete Ekle (SQL'e de kaydet)
   * ✅ WRAPPER: Normal hızlı satış modunda fokus ekle
   */
  const addToCartWithRestaurant = useCallback((product: any, quantityToAdd: number = 1) => {
    console.log('┌═════════════════════════════════════════════════════════┐');
    console.log('║ 🍽️ RESTAURANT MODU - SEPETE EKLE (addToCartWithRestaurant) ║');
    console.log('└═════════════════════════════════════════════════════════┘');
    console.log('📦 [GELEN PARAMETRELER]:', {
      productId: product.id,
      productName: product.name,
      quantityToAdd: quantityToAdd,
      isRestaurantMode: isRestaurantMode,
      tableId: tableId,
      tableName: tableName,
      restaurantScreenId: restaurantScreenId
    });

    // Beko cihaz limitleri kontrolü
    const isBekoActiveR = screenSettings?.useFiscalDeviceForCash && 
      (selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr');

    // ✅ Cihazdan bağımsız sepet kalem limiti (max GLOBAL_KALEM_LIMIT farklı kalem).
    // Beko aktifken Beko'nun kendi limiti (BEKO_KALEM_LIMIT) devreye girer,
    // global kontrol atlanır. Diğer cihazlar / cihaz seçilmemişse global limit uygulanır.
    if (!isBekoActiveR) {
      const isNewItemGlobal = !cart.some(item => item.id === product.id);
      if (isNewItemGlobal && cart.length >= GLOBAL_KALEM_LIMIT) {
        showAlertModal(
          'Sepet Kalem Limiti Doldu',
          `Sepete en fazla ${GLOBAL_KALEM_LIMIT} farklı kalem eklenebilir.\n\nLütfen mevcut sepeti tamamlayın veya bazı ürünleri sepetten çıkarın.`
        );
        return;
      }
    }
    
    if (isBekoActiveR) {
      const isNewItem = !cart.some(item => item.id === product.id);
      if (isNewItem && cart.length >= BEKO_KALEM_LIMIT) {
        showAlertModal(
          'Kalem Limiti Aşıldı',
          `Sepette ${cart.length} farklı ürün kalemi bulunmaktadır.\n\nBeko yazarkasa tek fişte en fazla ${BEKO_KALEM_LIMIT} farklı kalem desteklemektedir.\n\nLütfen önce mevcut sepeti tamamlayın veya sepet bölme özelliğini kullanın.`
        );
        return;
      }
      
      const productPrice = product.price || product.salePrice || 0;
      const newTotal = calculateTotal() + (productPrice * quantityToAdd);
      const bekoLimitR = getBekoFisLimitTL();
      if (newTotal > bekoLimitR) {
        showAlertModal(
          'Mali Fiş Limiti Aşıldı',
          `Bu ürünü ekledikten sonra sepet toplamı ${newTotal.toFixed(2)} TL olacak ve mali fiş limitini (${bekoLimitR.toLocaleString('tr-TR')} TL) aşacaktır.\n\nBeko yazarkasa bu tutarda tek fiş kesemez.\n\nLütfen sepet bölme özelliğini kullanınız.`
        );
        return;
      }
      
      // Kısım limiti kontrolü
      const sectionLimitErrorR = checkBekoSectionLimit(product, quantityToAdd, cart);
      if (sectionLimitErrorR) {
        showAlertModal('Kısım Limiti Aşıldı', sectionLimitErrorR);
        return;
      }
    }

    // ✅ YETKİ KONTROLÜ: Restaurant modunda ürün ekleme yetkisi var mı?
    if (isRestaurantMode && !waiterPermissions.canAddProduct) {
      console.error('❌ [YETKİ HATASI] Garson ürün ekleyemez:', {
        waiterName: waiterName,
        canAddProduct: waiterPermissions.canAddProduct
      });
      showAlertModal("Yetki Hatası", "Bu garsona ürün ekleme yetkisi verilmemiş!");
      return;
    }
    console.log('✅ [YETKİ KONTROLÜ] Garson ürün ekleyebilir');
    
    // ✅ ADİSYON YAZDIRILDIYSA VE "Adisyon Sonrası Ürün Ekleme" YETKİSİ YOKSA ENGELLE
    if (isRestaurantMode && isReceiptPrintedForCurrentTable && !waiterPermissions.canAddProductAfterReceiptPrint) {
      console.error('❌ [YETKİ HATASI] Adisyon yazdırılmış, "Adisyon Sonrası Ürün Ekleme" yetkisi yok:', {
        waiterName: waiterName,
        canAddProductAfterReceiptPrint: waiterPermissions.canAddProductAfterReceiptPrint,
        isReceiptPrinted: isReceiptPrintedForCurrentTable
      });
      setReceiptPrintedNoPermissionOpen(true);
      return;
    }
    
    // ✅ ADİSYON YAZDIRILDIYSA VE YETKİSİ VARSA ONAY MODAL GÖSTER
    if (isRestaurantMode && isReceiptPrintedForCurrentTable && waiterPermissions.canAddProductAfterReceiptPrint) {
      console.log('⚠️ [ADİSYON DURUMU] Adisyon yazdırılmış, onay modalı gösteriliyor...');
      // ✅ FIX: Ürünü normal akıştaki gibi envanter bilgileriyle zenginleştirerek sakla.
      // Ham product'ta canBeNegative/stockQuantity olmadığı için onay sonrası numpad'deki
      // stok kontrolü "Stok: 0, eksiye düşemez" sanıp yanlış "stok yetersiz" veriyordu.
      const pendingInventoryItem = sqlInventoryItems.find((item: InventoryItem) => item.id === product.id);
      const enrichedPendingProduct = {
        ...product,
        vatRate: pendingInventoryItem?.vatRate ?? product.vatRate ?? 0,
        vatStatus: pendingInventoryItem?.vatStatus || product.vatStatus || 'excluded',
        categoryId: getScreenCategoryId(product.id) || pendingInventoryItem?.categoryId || '',
        canBeNegative: (pendingInventoryItem?.canBeNegative ?? product.canBeNegative) === true,
        stockQuantity: pendingInventoryItem?.quantity ?? product.quantity ?? 0,
        hasMenu: pendingInventoryItem?.hasMenu || product.hasMenu || false,
        hasRecipe: pendingInventoryItem?.hasRecipe || product.hasRecipe || false,
      };
      setPendingProductForReceiptConfirm(enrichedPendingProduct);
      setPendingQuantityForReceiptConfirm(1);
      setPendingActionTypeForReceiptConfirm('addToCart');
      setReceiptPrintedConfirmOpen(true);
      return;
    }

    // ✅ Restaurant modunda ÖNCE NUMPAD MODAL AÇ (miktar seç)
    if (isRestaurantMode && tableId && restaurantScreenId && companyId) {
      // ✅ YENİ: Menülü ürün kontrolü - restoran modunda da çalışsın
      const inventoryItem = sqlInventoryItems.find((item: InventoryItem) => item.id === product.id);
      console.log('🔍 [RESTAURANT] inventoryItem lookup:', {
        productId: product.id,
        found: !!inventoryItem,
        inventoryVatStatus: inventoryItem?.vatStatus,
        inventoryVatRate: inventoryItem?.vatRate,
        sqlInventoryItemsCount: sqlInventoryItems.length,
      });
      const hasMenu = inventoryItem?.hasMenu || product.hasMenu || false;
      
      // ✅ STOK KONTROLÜ: Stok eksiye düşemez ise ve stok 0 veya altında ise engelle
      const itemCanBeNegative = (inventoryItem?.canBeNegative ?? product.canBeNegative) === true;
      const itemStockQuantity = inventoryItem?.quantity ?? product.quantity ?? 0;
      if (!itemCanBeNegative && itemStockQuantity <= 0) {
        showAlertModal(
          "Stok Yetersiz",
          `'${product.name}' ürününün stoğu yetersiz!\n\nMevcut Stok: ${itemStockQuantity}\n\nBu ürün için "Stok eksiye düşebilir" ayarı kapalıdır.`
        );
        return;
      }
      
      if (hasMenu) {
        console.log('🍽️ [RESTAURANT - MENÜLÜ ÜRÜN] Opsiyon modalı açılıyor:', product.name);
        // Menülü ürün için opsiyon modalı aç
        setPendingMenuProduct({
          productId: product.id,
          productName: product.name,
          productPrice: product.price || product.salePrice || 0,
          productVatRate: product.vatRate || 0,
          productVatStatus: inventoryItem?.vatStatus || product.vatStatus || 'excluded',
          productUnit: product.unit || 'adet',
          itemCanBeNegative,
          itemStockQuantity,
          hasRecipe: inventoryItem?.hasRecipe || product.hasRecipe || false,
          hasMenu: true
        });
        setIsMenuOptionModalOpen(true);
        return; // Opsiyon modalı açıldı, numpad açma
      }
      
      // Product'a vatStatus, vatRate ve stok bilgilerini inventoryItem'dan ekle
      const enrichedProduct = {
        ...product,
        vatRate: inventoryItem?.vatRate ?? product.vatRate ?? 0,
        vatStatus: inventoryItem?.vatStatus || product.vatStatus || 'excluded',
        categoryId: getScreenCategoryId(product.id) || inventoryItem?.categoryId || '',
        canBeNegative: itemCanBeNegative,
        stockQuantity: itemStockQuantity,
      };
      
      console.log('🔢 [NUMPAD MODAL AÇILIYOR]:', { 
        product: product.name,
        tableId: tableId,
        restaurantScreenId: restaurantScreenId,
        vatStatus: enrichedProduct.vatStatus,
        vatRate: enrichedProduct.vatRate,
        categoryId: enrichedProduct.categoryId,
        canBeNegative: enrichedProduct.canBeNegative,
        stockQuantity: enrichedProduct.stockQuantity,
        screenCategoryId: getScreenCategoryId(product.id),
        inventoryCategoryId: inventoryItem?.categoryId,
      });
      setNumpadProduct(enrichedProduct);
      setNumpadModalOpen(true);
      return; // Modalı aç ve fonksiyondan çık
    }

    console.log('ℹ️ [RESTAURANT MODU DEĞİL] Normal hızlı satış modunda, direkt addToCart çağrılıyor...');
    // Restaurant modu değilse direkt sepete ekle (Normal hızlı satış)
    addToCart(product, quantityToAdd);
    
    if (isQuantityModeActive) {
      setIsQuantityModeActive(false);
      setQuantityInputValue('');
    }
    
    // ✅ YENİ: Normal hızlı satış modunda işlem bitince fokusla
    console.log('🎯 [FOKUS] focusSearchBar çağrılıyor...');
    focusSearchBar();
    console.log('✅ [addToCartWithRestaurant TAMAMLANDI]');
  }, [addToCart, isRestaurantMode, tableId, tableName, restaurantScreenId, companyId, waiterName, waiterPermissions.canAddProduct, waiterPermissions.canAddProductAfterReceiptPrint, isReceiptPrintedForCurrentTable, showAlertModal, focusSearchBar, screenSettings?.useFiscalDeviceForCash, selectedFiscalDevice, cart, calculateTotal, isQuantityModeActive, sqlInventoryItems]);

  // ─────────────────────────────────────────────────────────
  // 4.1 - Quick Sale Product Search Hook (after addToCartWithRestaurant)
  /**
   * ✅ YENİ: Kasiyer giriş olmadan ürün eklenmeye çalışıldığında çağrılır
   * Bekleyen ürünü kaydeder ve kasiyer giriş modalını açar
   */
  const handleOpenCashierLoginWithPendingProduct = useCallback((product: any, quantity: number) => {
    console.log('🔐 [KASİYER] Bekleyen ürün kaydedilip kasiyer giriş modalı açılıyor...', product.name);
    setPendingProductForCashierLogin(product);
    setPendingQuantityForCashierLogin(quantity);
    setIsCashierLoginModalOpen(true);
  }, []);
  
  // Tartı etiketi okuma açıksa hangi barkod prefix'lerinin ağırlık etiketi
  // sayılacağını tanımlı tartılardan öğreniriz
  const weightBarcodePrefixes = useWeightBarcodePrefixes(screenSettings?.readWeightBarcode);

  // ─────────────────────────────────────────────────────────
  const {
    getWarehouseNames,
    validateSearchPrerequisites,
    searchAndAddProductToCart,
    handleSearchSubmit,
    handleAutocompleteSelect,
  } = useQuickSaleProductSearch({
    companyId,
    isRestaurantMode,
    screenSettings,
    currentScreen,
    loadingScreenSettings,
    loadingInventory,
    loadingWarehouses,
    allWarehouses,
    isQuantityModeActive,
    quantityInputValue,
    setIsQuantityModeActive,
    setQuantityInputValue,
    cart,
    addToCart,
    addToCartWithRestaurant,
    showAlertModal,
    pushScanAlert, // ✅ YENİ: Okutulan ürün sepete eklenemediğinde modal yerine ses + sepet şeridi
    focusSearchBar,
    branchId: activeBranchId, // ✅ YENİ: Şube bazlı fiyatlandırma (restoran modunda restaurantBranchId, normal modda quickSaleUserData.branchesId)
    branchWarehouseId, // ✅ YENİ: Şubenin deposu (barkod aramasında öncelikli depo)
    // ✅ YENİ: Kasiyer sistemi kontrol (Normal hızlı satış için)
    useCashierSystem: screenSettings?.useCashierSystem,
    currentCashierId: currentCashier?.cashierId?.toString() ?? null,
    onOpenCashierLoginWithPendingProduct: handleOpenCashierLoginWithPendingProduct,
    // ✅ YENİ: Paket fiyat toggle (paket modu ya da ayar açıksa PackagePrice öncelik)
    isPackageMode,
    usePackagePrices: screenSettings?.stockVisibility?.usePackagePrices === true,
    // ✅ YENİ: Tartı etiketi okuma (tartı bağlantısından bağımsız çalışır)
    readWeightBarcode: screenSettings?.readWeightBarcode,
    weightBarcodePrefixes,
  });

  // ✅ MOVED TO HOOK: handleConfirmOpenTable, handleCancelOpenTable
  // - useRestaurantTableOperations hook'una taşındı

  // ✅ MOVED TO HOOK: handleReceiptPrintedConfirm, handleReceiptPrintedConfirmCancel, 
  // handleNumpadConfirm, handleNumpadCancel
  // - useRestaurantNumpadOperations hook'una taşındı

  // ✅ MOVED TO HOOK: handleOpenTransferTableModal, handleTransferTable, handleRequestMerge, 
  // handleConfirmMergeTables, handleCloseMergeTablesModal, handleCloseTransferTableModal
  // - useRestaurantTableOperations hook'una taşındı

  // ✅ REFACTORED: Restaurant sepet işlemleri artık useRestaurantCartOperations hook'undan geliyor:
  // - removeFromCartWithRestaurant
  // - updateQuantityWithRestaurant
  // - clearCartWithRestaurant
  // - handleIncreaseSelectedWithRestaurant
  // - handleDecreaseSelectedWithRestaurant
  // - handleDeleteSelectedWithRestaurant

  /**
   * ✅ YENİ: Cari hesap ekleme modalını aç (AddAccount)
   * "Yeni Cari Kayıt" butonuna basıldığında çağrılır
   */
  const handleOpenAddAccountModal = useCallback(() => {
    setIsAddAccountModalOpen(true);
  }, []);

  /**
   * ✅ YENİ: Cari hesap ekleme modalını kapat
   */
  const handleCloseAddAccountModal = useCallback(() => {
    setIsAddAccountModalOpen(false);
    // Modal kapandığında cari hesapları yenile
    refreshCustomers();
  }, [refreshCustomers]);

  // Kasiyer giris/cikis handlerlari -> useCashierAuth hook (refactor)
  const {
    handleOpenCashierLogin,
    handleCashierLogin,
    handleCashierLogout,
  } = useCashierAuth({
    currentCashier,
    setCurrentCashier,
    setIsCashierLoginModalOpen,
    setCurrentUserPermissions,
    useCashierSystem: screenSettings?.useCashierSystem,
    prepaidSystemEnabled,
    prepaidCard,
    setPrepaidCard,
    setSelectedPrepaidWalletId,
    setIsPrepaidCardSearchOpen,
    pendingProductForCashierLogin,
    setPendingProductForCashierLogin,
    pendingQuantityForCashierLogin,
    setPendingQuantityForCashierLogin,
    sqlInventoryItems,
    setPendingMenuProduct,
    setIsMenuOptionModalOpen,
    addToCart,
    focusSearchBar,
  });

  // Oto kilitleme: Restaurant modunda QuickSaleScreen açıkken mutfak kontrolü yapıp geri dönme
  const handleAutoLockFromQuickSale = useCallback(async () => {
    if (isRestaurantMode && tableId && restaurantScreenId) {
      try {
        const { restaurantActiveSaleApi } = await import('../../../services/restaurantApiService');
        let hasUnprintedItems = false;

        const numericTableId = parseInt(tableId);
        const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);

        if (activeSale && activeSale.Items && cart.length > 0) {
          const cartGrouped: { [key: string]: number } = {};
          for (const item of cart) {
            cartGrouped[item.id] = (cartGrouped[item.id] || 0) + item.quantity;
          }
          for (const [productId, totalQty] of Object.entries(cartGrouped)) {
            const sqlItem = activeSale.Items.find((i: any) => String(i.ProductId) === String(productId));
            const printedQty = sqlItem ? (sqlItem.PrintedQuantity || 0) : 0;
            if (totalQty - printedQty > 0) {
              hasUnprintedItems = true;
              break;
            }
          }
        } else if (cart.length > 0 && (!activeSale || !activeSale.Items)) {
          hasUnprintedItems = true;
        }

        if (hasUnprintedItems) {
          showAlertModal(
            'Mutfağa Yazdırılmayan Ürünler Var',
            'Sepette mutfağa yazdırılmayan ürünler bulunduğu için ekran kilitlenemedi.\n\nLütfen önce ürünleri mutfağa yazdırın veya masadan çıkın.'
          );
          return;
        }

        navigate(`/restaurant-screen/${restaurantScreenId}`, {
          state: { autoLockTriggered: true }
        });
      } catch (error) {
        console.error('❌ [OTO KİLİT] Mutfak kontrolü hatası:', error);
        navigate(`/restaurant-screen/${restaurantScreenId}`, {
          state: { autoLockTriggered: true }
        });
      }
    } else {
      handleCashierLogout();
    }
  }, [isRestaurantMode, tableId, restaurantScreenId, cart, navigate, showAlertModal, handleCashierLogout]);

  // Oto kilitleme hook'u
  const autoLockEnabled = isRestaurantMode
    ? !!(screenSettings?.autoLockEnabled)
    : !!(screenSettings?.autoLockEnabled && screenSettings?.useCashierSystem && currentCashier);

  const {
    showCountdown: showAutoLockCountdown,
    dismissCountdown: dismissAutoLockCountdown,
  } = useAutoLock({
    enabled: autoLockEnabled,
    timeoutSeconds: screenSettings?.autoLockTimeoutSeconds || 30,
    onLock: handleAutoLockFromQuickSale,
  });

  /**
   * ✅ YENİ: Normal hızlı satış ekranı açıldığında kasiyer sistemi aktifse
   * ve kasiyer giriş yapmamışsa otomatik olarak kasiyer giriş modalını aç
   */
  useEffect(() => {
    // Sadece normal hızlı satış modunda çalış (restoran modunda değil)
    if (isRestaurantMode) {
      return;
    }
    
    // Screen settings yüklenmemişse bekle
    if (!screenSettings) {
      return;
    }
    
    // Kasiyer sistemi aktif değilse çık
    if (!screenSettings.useCashierSystem) {
      return;
    }
    
    // Zaten kasiyer giriş yapmışsa çık
    if (currentCashier) {
      return;
    }
    
    // Modal zaten açıksa tekrar açma
    if (isCashierLoginModalOpen) {
      return;
    }
    
    console.log('🔐 [KASİYER OTOMATİK] Kasiyer sistemi aktif, kasiyer giriş modalı açılıyor...');
    setIsCashierLoginModalOpen(true);
  }, [isRestaurantMode, screenSettings, currentCashier, isCashierLoginModalOpen]);

  // ✅ REFACTORED: Aşağıdaki fonksiyonlar artık useRestaurantKitchenOperations hook'undan geliyor:
  // - handleCloseTable
  // - handleBackButtonClick
  // - handleContinueWithoutPrinting
  // - handlePrintToKitchenAndGoBack
  // - handlePrintCancellationAndGoBack
  // - handlePrintBothAndGoBack
  // - handlePrintToKitchen
  // - handlePrintCheck

  // ✅ FİX: Eski fonksiyonlar başlangıcı - SİLİNDİ (devamı aşağıda)

  // ✅ KALDIRILDI: handlePrintCancellationAndGoBack, handlePrintBothAndGoBack, handlePrintToKitchen başlangıcı

  /* ✅ ESKİ FONKSİYONLAR SİLİNDİ - useRestaurantKitchenOperations hook'una taşındı
   * - handlePrintCancellationAndGoBack
   * - handlePrintBothAndGoBack
   * - handlePrintToKitchen
   */


  // ✅ KALDIRILDI: handlePrintCheck artık useRestaurantKitchenOperations hook'undan geliyor

  // ✅ FIX: Sepet yüklendiğinde cartItemId eksik olanları düzelt (SADECE BİR KERE)
  const hasFixedCartItemIdsRef = useRef(false);
  
  useEffect(() => {
    if (cart.length > 0 && !hasFixedCartItemIdsRef.current) {
      const missingCartItemIds = cart.filter(item => !item.cartItemId);
      
      if (missingCartItemIds.length > 0) {
        console.warn('⚠️ [SEPET FIX] cartItemId eksik ürünler bulundu, düzeltiliyor...', 
          missingCartItemIds.map(item => item.name)
        );
        
        hasFixedCartItemIdsRef.current = true; // ✅ Sadece bir kere düzelt
        
        // Otomatik olarak cartItemId ekle
        setCart(prevCart => prevCart.map(item => {
          if (!item.cartItemId) {
            const uniqueCartItemId = `${item.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            console.log('✅ [SEPET FIX] cartItemId eklendi:', { 
              product: item.name, 
              newCartItemId: uniqueCartItemId 
            });
            return { ...item, cartItemId: uniqueCartItemId };
          }
          return item;
        }));
      } else {
        console.log('✅ [SEPET KONTROL] Tüm ürünlerde cartItemId mevcut');
        hasFixedCartItemIdsRef.current = true;
      }
    }
    
    // Sepet temizlendiğinde flag'i sıfırla
    if (cart.length === 0) {
      hasFixedCartItemIdsRef.current = false;
    }
  }, [cart, setCart]);

  // ✅ RESTAURANT MODU: Masaya girerken SQL'den ürünleri yükle (SADECE BİR KERE)
  const hasLoadedTableRef = useRef(false);
  // ✅ YENİ: Masa ürünleri yüklenirken UI feedback için loading state
  const [isLoadingTableItems, setIsLoadingTableItems] = useState(false);
  // ✅ YENİ: Yükleme tamamen başarısız olduğunda kullanıcıya gösterilecek mesaj
  const [tableLoadError, setTableLoadError] = useState<string | null>(null);
  // ✅ YENİ: Tüm retry'lar tükenince manuel tetikleme için sayaç
  const [tableLoadRetryTrigger, setTableLoadRetryTrigger] = useState(0);

  useEffect(() => {
    // ✅ Sleep yardımcı fonksiyonu (retry backoff için)
    const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

    // ✅ Tek seferlik yükleme + retry mantığı
    const loadRestaurantTable = async () => {
      if (!isRestaurantMode || !tableId || !restaurantScreenId || hasLoadedTableRef.current) return;

      // Ekranın TÜM ürün listesi (sqlInventoryItems) beklenmiyor: o istek yüzlerce
      // ürünü depo stoklarıyla birlikte getirdiği için zayıf POS makinelerinde
      // saniyeler sürüyor ve açık masa boş yere geç açılıyordu. Masadaki kalemlerin
      // KDV/birim bilgisi aşağıdaki "eksik stok kartı" adımında zaten hedefli olarak
      // (yalnızca masadaki ürünler için) çekiliyor — liste sonradan gelse de sepet
      // doğru kalıyor.

      // ✅ Flag'i ÖNCE set ediyoruz ki paralel/aynı render içinden 2. kez giremesin.
      // Tam başarısızlık durumunda en alttaki catch bloğunda RESET edilir → kullanıcı
      // manuel "Yeniden Dene" tetiklediğinde tekrar denenebilir.
      hasLoadedTableRef.current = true;
      setIsLoadingTableItems(true);
      setTableLoadError(null);

      // ✅ 3 deneme: 500ms → 1000ms → 2000ms exponential backoff
      const RETRY_DELAYS_MS = [500, 1000, 2000];
      const MAX_ATTEMPTS = RETRY_DELAYS_MS.length;
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          console.log(`🍽️ [MASA YÜKLEME] Deneme ${attempt}/${MAX_ATTEMPTS} — SQL'den masa ürünleri yükleniyor...`, {
            tableId: parseInt(tableId),
            restaurantScreenId,
          });

          // Zayıf POS makinelerinde hangi adımın yavaş olduğunu ayırt edebilmek için
          // her adım ayrı ayrı ölçülüyor (ağ mı, CPU mu?).
          const tStart = performance.now();
          const ms = (from: number) => Math.round(performance.now() - from);

          const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../services/restaurantApiService');

          // Bu masa için aktif satış var mı?
          const tActiveSale = performance.now();
          const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
          console.log(`⏱️ [MASA YÜKLEME] Aktif satış sorgusu: ${ms(tActiveSale)}ms`);

          if (activeSale) {
            console.log('✅ [MASA YÜKLEME] Aktif satış bulundu, ID:', activeSale.Id);

            // ✅ Adisyon fişi için "masayı ilk açan garson" tespiti.
            // PRIMARY: WaiterActionsJson'daki ilk action'ın `waiter` field'ı (DB'de kalıcı, geriye dönük çalışır)
            // FALLBACK: localStorage (henüz hiç action yoksa — yeni masa, ilk add_item öncesi)
            try {
              const useToggle = getUseTableOpenerForReceiptSetting(restaurantScreenId);

              // localStorage'a kayıt — toggle KAPALIYKEN bile yaz (sonradan açılırsa hazır olsun)
              if (activeSale.OpenedAt && waiterName) {
                recordRestaurantTableOpener(
                  restaurantScreenId,
                  tableId,
                  waiterName,
                  activeSale.OpenedAt
                );
              }

              if (useToggle) {
                // 1) PRIMARY: WaiterActionsJson'daki ilk waiter
                const primaryOpener = extractFirstOpenerFromWaiterActions(activeSale.WaiterActionsJson);

                // 2) FALLBACK: localStorage
                const fallbackOpener = activeSale.OpenedAt
                  ? getRestaurantTableOpenerName(restaurantScreenId, tableId, activeSale.OpenedAt)
                  : null;

                const finalOpener = primaryOpener || fallbackOpener || null;
                setReceiptWaiterName(finalOpener);
                console.log('🧾 [MASA YÜKLEME] receiptWaiterName seçimi (toggle AÇIK):', {
                  primaryFromWaiterActions: primaryOpener,
                  fallbackFromLocalStorage: fallbackOpener,
                  finalReceiptWaiter: finalOpener,
                  loggedInWaiter: waiterName,
                  openedAt: activeSale.OpenedAt,
                  hasWaiterActionsJson: !!activeSale.WaiterActionsJson,
                });
              } else {
                setReceiptWaiterName(null);
                console.log('🧾 [MASA YÜKLEME] receiptWaiterName: toggle KAPALI → fallback waiterName kullanılacak');
              }
            } catch (err) {
              console.warn('[MASA YÜKLEME] tableOpenersStorage hatası:', err);
              setReceiptWaiterName(null);
            }

            // ✅ YENİ: Rezerve edilmiş sipariş numarasını state'e kaydet
            if (activeSale.ReservedShortCode) {
              setReservedShortCode(activeSale.ReservedShortCode);
              setReservedLongCode(activeSale.ReservedLongCode || null);
              console.log(`🎫 [MASA YÜKLEME] Numara yüklendi: ${activeSale.ReservedShortCode}`);
            }

            // ✅ YENİ: Adisyon yazdırıldı mı kontrolü
            if (activeSale.IsReceiptPrinted) {
              setIsReceiptPrintedForCurrentTable(true);
              setReceiptPrintedInfo({
                receiptPrintedAt: activeSale.ReceiptPrintedAt || undefined,
                receiptPrintedBy: activeSale.ReceiptPrintedBy || undefined,
              });
              console.log('🧾 [MASA YÜKLEME] Adisyon zaten yazdırılmış!', {
                by: activeSale.ReceiptPrintedBy,
                at: activeSale.ReceiptPrintedAt,
              });
            } else {
              setIsReceiptPrintedForCurrentTable(false);
              setReceiptPrintedInfo({});
            }

            // ✅ YENİ: Garson işlemleri geçmişini kaydet
            setWaiterActionsJson(activeSale.WaiterActionsJson || null);
            console.log('📜 [MASA YÜKLEME] Garson işlemleri geçmişi yüklendi');

            // Ürünleri getir
            const tItems = performance.now();
            const items = await restaurantActiveSaleItemApi.getByActiveSaleId(activeSale.Id);
            console.log(`✅ [MASA YÜKLEME] Masa kalemleri yüklendi: ${items.length} satır (${ms(tItems)}ms)`);

            // RestaurantActiveSaleItems tablosu KDV oranı saklamaz; oran stok kartından
            // çözülür. Ancak sqlInventoryItems yalnızca BU ekranın ürün görünürlüğündeki
            // kalemleri içerir — masaya başka bir ekrandan eklenen (ya da görünürlük ayarı
            // sonradan değişen) ürün burada bulunamaz ve sepete vatRate=0 girer. Sıfır KDV
            // sonra Pavo fişine KDV0 olarak basıldığı için eksik kalanlar API'den çekilir.
            const inventoryById = new Map<string, any>(
              sqlInventoryItems.map(inv => [inv.id, inv as any])
            );
            const missingProductIds = Array.from(new Set(
              items
                .map(i => i.ProductId?.toString())
                .filter((id): id is string => !!id && !inventoryById.has(id))
            ));

            if (missingProductIds.length > 0) {
              console.log(
                sqlInventoryItems.length === 0
                  ? `🎯 [MASA YÜKLEME] Ekran ürün listesi henüz gelmedi — masadaki ${missingProductIds.length} ürünün stok kartı hedefli çekiliyor...`
                  : `⚠️ [MASA YÜKLEME] ${missingProductIds.length} ürün ekran listesinde yok, stok kartları çekiliyor...`
              );
              const tCards = performance.now();
              try {
                const fetched = await salesService.getProductsByIds(missingProductIds, {
                  includeWarehouseStocks: false,
                });
                console.log(`⏱️ [MASA YÜKLEME] Stok kartı sorgusu: ${ms(tCards)}ms`);
                fetched.forEach((p: any) => {
                  const pid = (p.id ?? p.Id)?.toString();
                  if (!pid) return;
                  inventoryById.set(pid, {
                    id: pid,
                    name: p.name ?? p.Name ?? '',
                    unit: p.unit ?? p.Unit ?? 'adet',
                    // Kartta oran gerçekten boşsa 0'a çevirme; ödeme öncesi kontrol yakalasın.
                    vatRate: p.vatRate ?? p.VatRate,
                    vatStatus: p.vatStatus ?? p.VatStatus ?? 'excluded',
                    hasMenu: p.hasMenu ?? p.HasMenu ?? false,
                    hasRecipe: p.hasRecipe ?? p.HasRecipe ?? false,
                  });
                });
                console.log(`✅ [MASA YÜKLEME] ${fetched.length}/${missingProductIds.length} eksik stok kartı çözüldü`);
              } catch (err) {
                console.error('❌ [MASA YÜKLEME] Eksik stok kartları çekilemedi:', err);
              }
            }

            // Sepete yükle (CartItem formatına dönüştür) - HER SATIR AYRI
            // ✅ YENİ: hasMenu ve hasRecipe bilgisini inventory'den al
            const cartItems: CartItem[] = items.map(item => {
              const inventoryItem = inventoryById.get(item.ProductId.toString());
              const vatStatus = (inventoryItem?.vatStatus as 'included' | 'excluded' | undefined) || 'excluded';
              const cartItemId = `${item.ProductId}-${item.Id}-${item.AddedAt}`;

              return {
                cartItemId,
                id: item.ProductId.toString(),
                name: item.ProductName,
                price: item.UnitPrice,
                quantity: item.Quantity,
                unit: inventoryItem?.unit || 'adet',
                // Stok kartı hâlâ çözülemediyse 0 YAZMA: 0 geçerli bir oran (KDV'siz ürün)
                // olarak yorumlanıp fişe KDV0 basılmasına yol açıyor. Tanımsız bırakılırsa
                // ödeme öncesi KDV kontrolü kalemi yakalayıp kullanıcıyı uyarabiliyor.
                vatRate: inventoryItem?.vatRate,
                vatStatus,
                sqlItemId: item.Id,
                printedQuantity: item.PrintedQuantity || 0,
                note: item.Notes || undefined,
                hasMenu: inventoryItem?.hasMenu || false,
                hasRecipe: inventoryItem?.hasRecipe || false,
              };
            });

            if (DEBUG_MODE) {
              console.log('📊 [MASA YÜKLEME] PrintedQuantity detayları:', items.map(i => ({
                name: i.ProductName,
                qty: i.Quantity,
                printedQty: i.PrintedQuantity || 0,
                unprintedQty: i.Quantity - (i.PrintedQuantity || 0),
              })));
            }

            setCart(cartItems);
            console.log(
              `✅ [MASA YÜKLEME] Sepete yüklendi: ${cartItems.length} satır — masa açılış toplam süresi ${ms(tStart)}ms`
            );
          } else {
            console.log('ℹ️ [MASA YÜKLEME] Bu masa için aktif satış yok (yeni masa)');
            setCart([]);
          }

          // ✅ Başarı: retry döngüsünden çık
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          console.error(`❌ [MASA YÜKLEME] Deneme ${attempt}/${MAX_ATTEMPTS} BAŞARISIZ:`, error);

          // Son deneme değilse bekle ve tekrar dene
          if (attempt < MAX_ATTEMPTS) {
            const delay = RETRY_DELAYS_MS[attempt - 1];
            console.log(`⏳ [MASA YÜKLEME] ${delay}ms sonra tekrar denenecek...`);
            await sleep(delay);
          }
        }
      }

      // ✅ Yükleme bitti — loading kapat
      setIsLoadingTableItems(false);

      if (lastError) {
        // ⚠️ Tüm denemeler başarısız oldu → flag'i RESET et ki kullanıcı manuel tetikleyebilsin
        hasLoadedTableRef.current = false;
        const errMsg = 'Masa ürünleri yüklenemedi. Lütfen "Yeniden Dene" butonuna basın veya masaya tekrar girin.';
        setTableLoadError(errMsg);
        showSnackbar('Masa ürünleri yüklenirken hata oluştu.', 'error');
      } else {
        setTableLoadError(null);
      }
    };

    loadRestaurantTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestaurantMode, tableId, restaurantScreenId, setCart, showSnackbar, sqlInventoryItems, tableLoadRetryTrigger]);

  // ✅ YENİ: Manuel "Yeniden Dene" handler — tüm retry'lar tükenince butondan tetiklenir
  const handleRetryLoadTableItems = useCallback(() => {
    console.log('🔄 [MASA YÜKLEME] Manuel yeniden deneme tetiklendi');
    setTableLoadError(null);
    hasLoadedTableRef.current = false; // Flag'i temizle ki effect tekrar çalışsın
    setTableLoadRetryTrigger(prev => prev + 1);
  }, []);

  // ─────────────────────────────────────────────────────────
  // 🔄 AÇIK MASA CANLI SENKRON
  // Aynı masa başka bir cihazda değişince (ürün ekleme/silme/adet, mutfak
  // yazdırma, kapatma) sepet sunucudan tazelenir. Sunucu her değişiklikte
  // "OnActiveSalesChanged" yayınlar (TableId + ClientId ile). Kendi sekmemizin
  // yayınları yok sayılır; kısa bekleme ile uçuştaki yerel yazmaların bitmesi
  // beklenir. Yazdırma kararı zaten sunucuda olduğu için SignalR kopuk olsa
  // bile mükerrer fiş çıkmaz; bu katman yalnız ekranın güncel kalmasını sağlar.
  // ─────────────────────────────────────────────────────────
  const liveSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveSyncBusyRef = useRef(false);

  const mergeCartFromServer = useCallback(async (): Promise<'merged' | 'closed' | 'skipped'> => {
    if (!isRestaurantMode || !tableId || liveSyncBusyRef.current) return 'skipped';
    liveSyncBusyRef.current = true;
    try {
      const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../services/restaurantApiService');
      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      if (!activeSale) return 'closed';
      const items = await restaurantActiveSaleItemApi.getByActiveSaleId(activeSale.Id);

      // Başka cihazın eklediği ürünün stok kartı bu ekranın listesinde olmayabilir
      const inventoryById = new Map<string, any>(sqlInventoryItems.map(inv => [inv.id, inv as any]));
      const missing = Array.from(new Set(
        items.map(i => i.ProductId?.toString()).filter((id): id is string => !!id && !inventoryById.has(id))
      ));
      if (missing.length > 0) {
        try {
          const fetched = await salesService.getProductsByIds(missing, { includeWarehouseStocks: false });
          fetched.forEach((p: any) => {
            const pid = (p.id ?? p.Id)?.toString();
            if (!pid) return;
            inventoryById.set(pid, {
              id: pid,
              unit: p.unit ?? p.Unit ?? 'adet',
              vatRate: p.vatRate ?? p.VatRate,
              vatStatus: p.vatStatus ?? p.VatStatus ?? 'excluded',
              hasMenu: p.hasMenu ?? p.HasMenu ?? false,
              hasRecipe: p.hasRecipe ?? p.HasRecipe ?? false,
              categoryId: p.categoryId ?? p.CategoryId,
            });
          });
        } catch (err) {
          console.warn('[CANLI SENKRON] Eksik stok kartları çekilemedi:', err);
        }
      }

      setCart(prev => {
        const byServerId = new Map(items.map(i => [i.Id, i]));
        const seen = new Set<number>();
        const next: CartItem[] = [];
        for (const row of prev) {
          if (!row.sqlItemId) { next.push(row); continue; } // henüz kaydedilmemiş yerel satır
          const srv = byServerId.get(row.sqlItemId);
          if (!srv || seen.has(row.sqlItemId)) continue; // başka cihaz sildi / kopya satır
          seen.add(row.sqlItemId);
          next.push({
            ...row,
            quantity: srv.Quantity,
            printedQuantity: srv.PrintedQuantity || 0,
            note: srv.Notes || undefined,
          });
        }
        for (const srv of items) {
          if (seen.has(srv.Id)) continue;
          const inv = inventoryById.get(srv.ProductId.toString());
          next.push({
            cartItemId: `${srv.ProductId}-${srv.Id}-${srv.AddedAt}`,
            id: srv.ProductId.toString(),
            name: srv.ProductName,
            price: srv.UnitPrice,
            quantity: srv.Quantity,
            unit: inv?.unit || 'adet',
            vatRate: inv?.vatRate,
            vatStatus: (inv?.vatStatus as 'included' | 'excluded' | undefined) || 'excluded',
            sqlItemId: srv.Id,
            printedQuantity: srv.PrintedQuantity || 0,
            note: srv.Notes || undefined,
            hasMenu: inv?.hasMenu || false,
            hasRecipe: inv?.hasRecipe || false,
            categoryId: inv?.categoryId,
          } as CartItem);
        }
        return next;
      });
      return 'merged';
    } catch (err) {
      console.warn('[CANLI SENKRON] Sepet tazelenemedi:', err);
      return 'skipped';
    } finally {
      liveSyncBusyRef.current = false;
    }
  }, [isRestaurantMode, tableId, sqlInventoryItems]);

  useEffect(() => {
    if (!isRestaurantMode || !tableId || !restaurantScreenId) return;
    const numericTableId = parseInt(tableId);
    const callbackId = `quick_sale_table_${tableId}`;
    let cancelled = false;
    let myClientId: string | null = null;

    const describe = (p: { Reason?: string; ActorName?: string | null; Detail?: string | null }): string | null => {
      const who = p.ActorName ? `${p.ActorName}: ` : '';
      const detail = p.Detail ? ` (${p.Detail})` : '';
      switch (p.Reason) {
        case 'item_added': return `${who}ürün eklendi${detail}`;
        case 'item_removed': return `${who}ürün silindi${detail}`;
        case 'item_updated': return `${who}adet güncellendi${detail}`;
        case 'kitchen_printed': return `${who}mutfağa yazdırıldı`;
        default: return null;
      }
    };

    const schedule = (payload: { Reason?: string; ActorName?: string | null; Detail?: string | null }) => {
      if (liveSyncTimerRef.current) clearTimeout(liveSyncTimerRef.current);
      liveSyncTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const outcome = await mergeCartFromServer();
        if (cancelled) return;
        if (outcome === 'closed') {
          showSnackbar(`${tableName || 'Masa'} başka bir cihazdan kapatıldı.`, 'warning');
          navigate(`/restaurant-screen/${restaurantScreenId}`);
          return;
        }
        if (outcome === 'merged') {
          const text = describe(payload);
          if (text) showSnackbar(`${tableName || 'Masa'} başka bir cihazdan güncellendi — ${text}`, 'info');
        }
      }, 1200);
    };

    (async () => {
      try {
        const [{ getGtsClientId }, { restaurantSignalRService }] = await Promise.all([
          import('../../../config/apiClient'),
          import('../../../services/restaurantSignalRService'),
        ]);
        if (cancelled) return;
        myClientId = getGtsClientId();
        restaurantSignalRService.onActiveSalesChanged(callbackId, (payload) => {
          if (payload?.ClientId && payload.ClientId === myClientId) return; // kendi değişikliğimiz
          if (payload?.TableId != null && payload.TableId !== numericTableId) return; // başka masa
          if (payload?.TableId == null) {
            // Ekran geneli olay: yalnız bu masayı etkileyebilecek türlerde tazele
            const screenWide = ['table_closed', 'table_cancelled', 'split_closed', 'cart_split', 'item_moved', 'carts_merged', 'carts_merged_all'];
            if (!screenWide.includes(payload?.Reason || '')) return;
          }
          schedule(payload);
        });
        await restaurantSignalRService.connect(restaurantScreenId);
      } catch (err) {
        console.warn('[CANLI SENKRON] SignalR bağlantısı kurulamadı:', err);
      }
    })();

    // Yedek: sekme tekrar görünür olunca bir kez tazele
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule({ Reason: 'visibility' });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (liveSyncTimerRef.current) clearTimeout(liveSyncTimerRef.current);
      import('../../../services/restaurantSignalRService')
        .then(({ restaurantSignalRService }) => restaurantSignalRService.removeActiveSalesChangedCallback(callbackId))
        .catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestaurantMode, tableId, restaurantScreenId, mergeCartFromServer]);

  return (
    <Box 
      onClick={handleContainerClick} // ✅ YENİ: Boş alana tıklanınca otomatik fokuslan
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: isPackageMode ? '100%' : '100vh', // ✅ Paket modunda parent'ın yüksekliğini al
        bgcolor: '#f4f6f8',
      }}
    >
      {/* ═══════════════════════════════════════════════════════════ */}
      {/* SATIR 1: HEADER (AppBar) - PAKET MODUNDA GİZLE */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {!isPackageMode && (
        <Box>
          <QuickSaleAppBar 
            userDetails={quickSaleUserData}
            permittedScreens={permittedScreens}
            currentScreen={currentScreen}
            handleOpenUISettingsModal={screenSettings?.showScreenLayoutButton !== false ? handleOpenUISettingsModal : undefined}
            userFullName={userFullName}
            handleOpenUserMenu={handleOpenUserMenu}
            handleLogout={handleLogout}
            anchorElUserMenu={anchorElUserMenu}
            handleCloseUserMenu={handleCloseUserMenu}
            lastSettingsRefresh={lastSettingsRefresh}
            permittedPykScreens={permittedPykScreens}
            handleGoToTaskBoard={handleGoToTaskBoard}
            currentUserPermissions={currentUserPermissions}
            onOpenBulkAddAccountModal={handleOpenAddAccountModal} // ✅ YENİ: AddAccount modalını aç
            companyId={companyId}
            useKitchenScreen={screenSettings?.useKitchenScreen || false}
            isRestaurantMode={isRestaurantMode} // ✅ YENİ: Restaurant modu
            tableName={tableName} // ✅ YENİ: Masa adı
            waiterName={waiterName} // ✅ YENİ: Garson adı
            // ✅ YENİ: Yazarkasa bağlantı durumu
            fiscalDeviceStatus={fiscalDeviceStatus}
            selectedFiscalDevice={selectedFiscalDevice}
            useFiscalDeviceForCash={screenSettings?.useFiscalDeviceForCash || false}
            // ✅ YENİ: Rezerve edilmiş sipariş numarası (sadece restoran veya mutfak ekranı aktifse göster)
            reservedShortCode={isRestaurantMode || screenSettings?.useKitchenScreen ? reservedShortCode : null}
            // ✅ YENİ: Geri butonu kontrolü (yazdırılmamış ürün kontrolü için)
            onBackClick={isRestaurantMode ? handleBackButtonClick : undefined}
            // ✅ YENİ: Kasiyer sistemi
            useCashierSystem={screenSettings?.useCashierSystem || false}
            // ✅ YENİ: Restoran modunda garson/kasiyer adını göster
            currentCashierName={isRestaurantMode ? waiterName : (currentCashier?.cashierName || null)}
            // ✅ YENİ: Restoran modunda login tipi
            loginType={isRestaurantMode ? loginType : (currentCashier ? 'cashier' : undefined)}
            onOpenCashierLogin={handleOpenCashierLogin}
            onCashierLogout={handleCashierLogout}
            isPackageMode={isPackageMode}
            onOpenKitchenOrdersModal={() => setIsKitchenOrdersModalOpen(true)}
            onOpenProductSortSettings={handleOpenProductSortSettings}
            onOpenEndOfDayPrint={() => setEndOfDayPrintModalOpen(true)}
            // Kayan duyuru. Tahsilat duyurusu yalnız satış ekranında; restoran masasında gösterilmez.
            announcements={
              isRestaurantMode
                ? activeAnnouncements.filter((a) => a.systemSource !== 'renewal')
                : activeAnnouncements
            }
            // 🖥️ Plan 2 — Kiosk Urun Gorunurluk modal toggle
            onOpenKioskProducts={() => setIsKioskProductsModalOpen(true)}
            // Kullanım rehberi (i): hızlı satış modunda (restoran adisyonu ayrı rehber alır)
            guideKey={isRestaurantMode ? undefined : 'sales.quickSaleScreen'}
          />
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* SATIR 2: ANA İÇERİK - 3 SÜTUN */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex',
        overflow: 'hidden',
      }}>
        <Grid container sx={{ height: '100%', margin: 0, width: '100%' }} spacing={0}>
          
          {/* ─────────────────────────────────────────────────────── */}
          {/* SÜTUN 1: Cari + Barkod + Sepet (3 Alt Satır) */}
          {/* ─────────────────────────────────────────────────────── */}
          <Grid item xs={12} md={cartColumnWidth} sx={{ 
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            padding: 0,
            margin: 0,
            transition: 'all 0.3s ease',
          }}>
            {/* Alt Satır 1: Cari Hesap veya Kart Paneli */}
            <Box>
              {prepaidSystemEnabled ? (
                <PrepaidCardPanel
                  card={prepaidCard}
                  selectedWalletId={selectedPrepaidWalletId}
                  onWalletSelect={(walletId: number) => setSelectedPrepaidWalletId(walletId)}
                  onChangeCard={() => {
                    setPrepaidCard(null);
                    setSelectedPrepaidWalletId(null);
                    setIsPrepaidCardSearchOpen(true);
                  }}
                  cartTotal={calculateTotal()}
                  entitlements={entitlementsForPanel}
                  onAddEntitlement={handleAddEntitlementToCart}
                />
              ) : (
                <CustomerSelectionPanel 
                  currentUserPermissions={currentUserPermissions}
                  customers={customers}
                  selectedCustomer={selectedCustomer}
                  setSelectedCustomer={setSelectedCustomer}
                  loadingCustomers={loadingCustomers}
                  customerError={customerError}
                  loadingUserPermissions={loadingUserPermissions}
                  calculateBalance={calculateBalance}
                  formatCurrency={formatCurrency}
                  showAlertModal={showAlertModal}
                  refreshCustomers={refreshCustomers}
                  prepaidSystemEnabled={prepaidSystemEnabled}
                  onOpenCustomerSelectionModal={handleOpenCustomerSelectionModal}
                  focusSearchBar={focusSearchBar}
                  onOpenAddAccountModal={handleOpenAddAccountModal}
                  canAddAccount={currentUserPermissions?.canAddAccount || quickSaleUserData?.role === 'admin'}
                  customerNote={customerNote}
                  onOpenCustomerNoteModal={() => setIsCustomerNoteModalOpen(true)}
                  isCartEmpty={cart.length === 0}
                  isPackageMode={isPackageMode}
                  packageCustomerAddress={packageCustomer?.address}
                  onOpenCustomerQueryModal={(selectedFiscalDevice === 'pavopay' || isRestP2PEnabled) ? () => setIsCustomerQueryModalOpen(true) : undefined}
                  isPavoPayEnabled={selectedFiscalDevice === 'pavopay' || isRestP2PEnabled}
                  pavoPayTempCustomerName={pavoPayTempCustomerName}
                  isEInvoiceCustomer={pavoPayIsEInvoice}
                  onClearTempCustomer={() => {
                    setPavoPayTempCustomerName(null);
                    setPavoPayTempCustomerParty(null);
                    setPavoPayIsEInvoice(false);
                    clearPendingCustomerParty();
                  }}
                />
              )}
            </Box>
            
            {/* Alt Satır 2: Barkod/Arama - Sadece restaurant modu DEĞİLSE ve showSearchBar açıksa göster */}
            {/* ✅ PERFORMANS OPTİMİZASYONU: ProductSearchBar artık kendi local state'ini tutuyor */}
            {/* Bu sayede barkod okuyucu hızında çalışıyor! */}
            {!isRestaurantMode && showSearchBar && (
              <Box>
                <ProductSearchBar
                  onSearchSubmit={handleSearchSubmit}
                  isQuantityModeActive={isQuantityModeActive}
                  quantityInputValue={quantityInputValue}
                  onToggleQuantityMode={handleToggleQuantityMode}
                  numpadValue={numpadSearchValue}
                  onNumpadValueConsumed={() => setNumpadSearchValue('')}
                  onAutocompleteSearch={handleAutocompleteSearch}
                  onAutocompleteSelect={handleAutocompleteSelect}
                  ref={searchBarRef}
                />
              </Box>
            )}

            {/* Alt Satır 3: Sepet (Kalan alan) */}
            <Box sx={{ 
              flexGrow: 1,
              overflow: 'hidden',
              minHeight: 0, // Flex child'ın küçülmesine izin ver
              height: 0, // ✅ Flex ile birlikte çalışması için - scroll için gerekli
            }}>
              <CartSection
                onRegisterPosButtonClick={registerPosButtonClick}
                cart={cart}
                selectedCartItemId={selectedCartItemId}
                handleSelectItem={handleSelectItem}
                clearCart={handleClearCart}
                calculateTotal={calculateTotal}
                lastAppliedDiscountAmount={lastAppliedDiscountAmount}
                formatCurrency={formatCurrency}
                handleCashPaymentForQuickSale={handleCashPaymentForQuickSale}
                handlePosPaymentForQuickSale={handlePosPaymentForQuickSale}
                handlePavoP2pPayment={isRestaurantMode && waiterPermissions.canUsePavoP2P ? hookOpenPavoP2pSelect : undefined}
                pavoP2pPaymentPending={pavoP2pPaymentPending}
                isRestP2PEnabled={Boolean(screenSettings?.useRestP2PForPayments && screenSettings?.pavoRestP2PDeviceScreenCode)}
                restP2PDeviceCode={screenSettings?.pavoRestP2PDeviceScreenCode || null}
                restP2PDeviceName={screenSettings?.pavoRestP2PDeviceName || null}
                useFiscalDeviceForCash={screenSettings?.useFiscalDeviceForCash || false}
                // ✅ YENİ: Okutulduğu hâlde sepete eklenemeyen ürünlerin uyarı şeridi
                scanAlerts={scanAlerts}
                onOpenScanAlerts={openScanAlertList}
                onClearScanAlerts={clearScanAlerts}
                processPaymentAfterPartialModal={hookProcessPaymentAfterPartial}
                setIsPartialPaymentModalOpen={setIsPartialPaymentModalOpen}
                isProcessingPayment={isProcessingPayment}
                isProcessingPosPayment={isProcessingPosPayment} // Sadece POS modal için
                cashPaymentLabel={cashPaymentLabel}
                singlePosOption={singlePosOption}
                posOptions={posOptions}
                showAlertModal={showAlertModal}
                companyId={companyId}
                currentScreen={currentScreen}
                selectedCustomer={selectedCustomer}
                prepaidSystemEnabled={prepaidSystemEnabled}
                prepaidCard={prepaidCard}
                selectedPrepaidWalletId={selectedPrepaidWalletId}
                prepaidCreatedBy={[quickSaleUserData?.email, currentCashier?.cashierName].filter(Boolean).join(' / ') || null}
                onPrepaidPurchaseComplete={() => {
                  clearCart();
                  refreshOrderNumber();
                  // ✅ Hak tüketildiyse kalan hakları yeniden yükle (aynı kart tekrar okutulsa bile güncel olsun)
                  loadPrepaidEntitlements(prepaidCard?.cardUID);
                }}
                printReceiptAfterPayment={printReceiptAfterPayment}
                reservedShortCode={screenSettings?.useKitchenScreen ? reservedShortCode : null}
                reservedLongCode={screenSettings?.useKitchenScreen ? reservedLongCode : null}
                allInventoryItems={sqlInventoryItems}
                screenSettings={screenSettings}
                nextReceiptNumber={currentReceiptNumber}
                showReceiptNumber={showReceiptNumber}
                onQuantityClick={handleQuantityClick}
                isRestaurantMode={isRestaurantMode} // ✅ YENİ: Restaurant modu
                tableName={tableName} // ✅ YENİ: Masa adı (modal için)
                waiterName={waiterName} // ✅ YENİ: Garson adı (modal için)
                waiterCanCloseSale={waiterPermissions.canCloseSale} // ✅ YENİ: Garson yetkisi
                waiterCanCloseTable={waiterPermissions.canCloseTable} // ✅ YENİ: Masa kapatma yetkisi
                waiterCanClearCart={waiterPermissions.canClearCart} // ✅ YENİ: Sepeti temizleme yetkisi
                waiterCanPrintToKitchen={waiterPermissions.canPrintToKitchen} // ✅ YENİ: Mutfağa yazdırma yetkisi
                waiterCanCancelReceipt={waiterPermissions.canCancelReceipt} // ✅ YENİ: Adisyon iptal yetkisi
                waiterCanCancelUnprintedReceipt={waiterPermissions.canCancelUnprintedReceipt} // ✅ YENİ: Yazdırılmamış adisyon iptal yetkisi
                waiterCanPrintReceipt={waiterPermissions.canPrintReceipt} // ✅ YENİ: Adisyon yazdırma yetkisi
                useKitchenScreen={useKitchenScreen} // ✅ YENİ: Mutfak ekranı ayarı
                onCloseTable={handleCloseTable} // ✅ YENİ: Masayı kapat
                cancelReasonTriggerNonce={cancelReasonTriggerNonce} // ✅ YENİ: Son ürün silme → adisyon iptal nedeni modalını dışarıdan tetikle
                onPrintToKitchen={handlePrintToKitchen} // ✅ YENİ: Mutfağa yazdır
                onPrintCheck={handlePrintCheck} // ✅ YENİ: Adisyon yazdır
                isClosingTable={isClosingTable} // ✅ YENİ: Loading state
                // ✅ YENİ: Sepet bölme özellikleri
                hasSplitCart={splitRightCart.length > 0}
                splitRightCart={splitRightCart}
                selectedCartView={selectedCartView}
                onCartViewChange={setSelectedCartView}
                // ✅ YENİ: Masa geçmişi
                waiterActionsJson={waiterActionsJson}
                tableId={tableId || undefined}
                // ✅ YENİ: Diğer ödeme modalı
                onOpenOtherPaymentModal={() => setOtherPaymentModalOpen(true)}
                hasOtherPaymentOptions={otherPaymentOptions.length > 0}
                // ✅ YENİ: Online ödeme modalı
                onOpenOnlinePaymentModal={() => setOnlinePaymentModalOpen(true)}
                hasOnlinePaymentOptions={onlinePaymentOptions.length > 0}
                // ✅ YENİ: Menü opsiyon düzenleme
                onMenuOptionEdit={handleMenuOptionEdit}
                // ✅ YENİ: Açık hesap ödeme
                handleOpenAccountPayment={handleOpenAccountPayment}
                openAccountPaymentOption={openAccountPaymentOption}
                // ✅ YENİ: Paket hızlı satış modu
                isPackageMode={isPackageMode}
                // ✅ KRİTİK: Müşteri bilgileri yüklenmeden sepet onaylanamaz!
                // Paket modunda selectedCustomer ve companyName yüklenmeli
                isPackageCustomerReady={(() => {
                  const ready = !isPackageMode || !!(selectedCustomer && selectedCustomer.companyName);
                  if (isPackageMode) {
                    console.log('🔍 [isPackageCustomerReady DEBUG]', {
                      isPackageMode,
                      selectedCustomer: selectedCustomer ? { id: selectedCustomer.id, companyName: selectedCustomer.companyName } : null,
                      hasCompanyName: !!(selectedCustomer?.companyName),
                      ready
                    });
                  }
                  return ready;
                })()}
                onConfirmPackageCart={async () => {
                  // ✅ Paket sipariş oluşturma
                  console.log('📦 Paket sepeti onaylanıyor:', { cart, selectedCustomer, packageCustomer });
                  console.log('🔍 [CUSTOMER DEBUG]', {
                    selectedCustomer,
                    selectedCustomerId: selectedCustomer?.id,
                    selectedCustomerIdType: typeof selectedCustomer?.id,
                    packageCustomer,
                    packageCustomerId: packageCustomer?.id
                  });
                  
                  // Validasyonlar
                  if (!companyId) {
                    showAlertModal('Hata', 'Şirket bilgisi bulunamadı.');
                    return;
                  }
                  
                  if (cart.length === 0) {
                    showAlertModal('Hata', 'Sepet boş, ürün ekleyiniz.');
                    return;
                  }
                  
                  // Müşteri bilgisi kontrolü - seçili müşteri kullan
                  const customer = selectedCustomer;
                  
                  // DEBUG: Customer kontrolü
                  console.log('🔍 [CUSTOMER CHECK]', {
                    customer,
                    customerId: customer?.id,
                    hasCustomer: !!customer,
                    hasCustomerId: !!customer?.id
                  });
                  
                  if (!customer || !customer.id) {
                    showAlertModal('Hata', 'Lütfen bir müşteri seçiniz.');
                    return;
                  }
                  
                  // Customer ID'yi number'a çevir
                  const accountIdNum = parseInt(customer.id, 10);
                  if (isNaN(accountIdNum) || accountIdNum <= 0) {
                    showAlertModal('Hata', 'Geçersiz müşteri ID\'si.');
                    return;
                  }
                  
                  try {
                    // Sepet kalemlerini DTO'ya çevir
                    const items: CreatePackageOrderItemDto[] = cart.map((item, index) => {
                      // Fiyat ve miktar
                      const unitPrice = item.price;
                      const qty = item.quantity;
                      const taxRate = item.vatRate || 0;
                      
                      // ✅ KDV dahil/hariç kontrolü
                      // Şube fiyatlandırması veya paket fiyatı kullanıldığında vatStatus = 'included' (KDV dahil)
                      const vatStatus = (item as any).vatStatus || 'excluded';
                      const isVatIncluded = vatStatus === 'included';
                      
                      // İndirim hesapla (originalPrice varsa)
                      const discountAmount = item.originalPrice && item.originalPrice > unitPrice 
                        ? (item.originalPrice - unitPrice) * qty 
                        : 0;
                      const discountRate = item.originalPrice && item.originalPrice > unitPrice 
                        ? ((item.originalPrice - unitPrice) / item.originalPrice) * 100 
                        : 0;
                      
                      // ✅ KDV hesaplaması - KDV dahil ise ekleme yapma
                      const subTotal = unitPrice * qty;
                      let taxAmount = 0;
                      let totalAmount = subTotal;
                      
                      if (isVatIncluded) {
                        // ✅ Fiyat zaten KDV dahil - KDV ters hesaplanır (görüntüleme için)
                        // Örnek: 118 TL KDV dahil fiyat, %18 KDV => KDV = 118 * 18 / 118 = 18 TL
                        taxAmount = subTotal - (subTotal / (1 + taxRate / 100));
                        totalAmount = subTotal; // Fiyat değişmez
                        console.log(`📦 [PAKET FİYAT] "${item.name}" KDV dahil fiyat: ${subTotal} TL (KDV: ${taxAmount.toFixed(2)} TL)`);
                      } else {
                        // ❌ Fiyat KDV hariç - KDV eklenir
                        taxAmount = subTotal * taxRate / 100;
                        totalAmount = subTotal + taxAmount;
                      }
                      
                      // ✅ YENİ: Inventory'den hasRecipe ve hasMenu bilgisini al
                      const inventoryItem = sqlInventoryItems.find((inv: InventoryItem) => inv.id === item.id);
                      const itemHasRecipe = item.hasRecipe || inventoryItem?.hasRecipe || false;
                      const itemHasMenu = item.hasMenu || inventoryItem?.hasMenu || false;
                      
                      // ✅ YENİ: Varsayılan depo bilgisi (stok düşümü için)
                      const stockVisibility = (screenSettings as any)?.stockVisibility;
                      const defaultWarehouseId = stockVisibility?.defaultWarehouseId 
                        || stockVisibility?.warehouses?.[0] 
                        || undefined;
                      
                      return {
                        ProductId: item.id || undefined,
                        ProductCode: undefined,
                        ProductName: item.name,
                        ProductBarcode: undefined,
                        CategoryId: (() => {
                          const cid = item.categoryId || inventoryItem?.categoryId;
                          return cid ? Number(cid) : undefined;
                        })(),
                        CategoryName: (() => {
                          const cid = item.categoryId || inventoryItem?.categoryId;
                          return cid ? allCategoriesData.find(cat => cat.id === cid)?.name || undefined : undefined;
                        })(),
                        Quantity: qty,
                        UnitId: undefined,
                        UnitName: item.unit || undefined,
                        UnitPrice: unitPrice,
                        DiscountRate: discountRate,
                        DiscountAmount: discountAmount,
                        TaxRate: taxRate,
                        TaxAmount: taxAmount,
                        SubTotal: subTotal,
                        TotalAmount: totalAmount,
                        // Sepet kaleminin urun aciklamasi/notu. Restoran modunda
                        // ItemNoteModal uzerinden, paket modunda ise ayni modal
                        // uzerinden girilir; PackageOrderItem.Notes olarak SQL'e
                        // yazilir ve mutfak fisinde basilir (musteri/adisyon
                        // fisinde basilmaz - kullanici karari).
                        Notes: item.note || undefined,
                        // ✅ YENİ: Reçete/Menü ve Depo bilgileri (stok düşümü için)
                        HasRecipe: itemHasRecipe,
                        HasMenu: itemHasMenu,
                        WarehouseId: defaultWarehouseId,
                        WarehouseName: undefined,
                        // ✅ YENİ: KDV Dahil/Hariç bilgisi (Fatura hesaplaması için)
                        PriceType: isVatIncluded ? 'VAT_INCLUDED' : 'VAT_EXCLUDED'
                      };
                    });
                    
                    // Toplam hesaplamaları
                    const subTotal = items.reduce((acc, item) => acc + item.SubTotal, 0);
                    const discountAmount = items.reduce((acc, item) => acc + (item.DiscountAmount || 0), 0);
                    const taxAmount = items.reduce((acc, item) => acc + (item.TaxAmount || 0), 0);
                    const totalAmount = items.reduce((acc, item) => acc + item.TotalAmount, 0);
                    
                    // Sipariş DTO'su (PaymentMethod modal'dan gelecek)
                    const orderDto: CreatePackageOrderDto = {
                      BranchId: activeBranchId || undefined,
                      AccountId: accountIdNum,
                      AccountName: customer.companyName,
                      CustomerPhone: customer.mobilePhone || packageCustomer?.phone,
                      CustomerAddressId: packageCustomer?.addressId || undefined,
                      CustomerAddress: packageCustomer?.address || undefined,
                      CustomerNotes: customerNote || undefined,
                      SubTotal: subTotal,
                      DiscountAmount: discountAmount,
                      TaxAmount: taxAmount,
                      TotalAmount: totalAmount,
                      Notes: undefined,
                      CreatedBy: quickSaleUserData?.uid,
                      CreatedByName: quickSaleUserData?.email,
                      CashierId: currentCashier?.cashierId || undefined,
                      CashierName: currentCashier?.cashierName || undefined,
                      Items: items
                    };
                    
                    console.log('📦 Ödeme yöntemi seçimi için modal açılıyor:', orderDto);
                    
                    // ✅ Ödeme yöntemi seçim modalını aç
                    setPendingPackageOrderData({ orderDto, customer });
                    setPackagePaymentModalOpen(true);
                    
                  } catch (error: any) {
                    console.error('❌ Sipariş hazırlama hatası:', error);
                    showAlertModal('Hata', error.message || 'Beklenmeyen bir hata oluştu.');
                  }
                }}
                mobileProductsContent={
                  <ProductDisplayArea
                    selectedCategory={selectedCategory}
                    handleCategoryChange={handleCategoryChange}
                    productCategoryTabSize="small" // Mobil için küçük kategori tabları
                    displayableCategories={displayableCategories}
                    activeDisplayWarehouseId={activeDisplayWarehouseId}
                    filteredProducts={filteredProducts}
                    productCardSize={75} // Mobil için küçük kartlar (4 kart/satır)
                    productCardFontSize={10} // Daha küçük font
                    addToCart={isRestaurantMode ? addToCartWithRestaurant : addToCartWithFocus}
                    isQuantityModeActive={isQuantityModeActive}
                    quantityInputValue={quantityInputValue}
                    showProductImages={false} // Mobilde resimler kapalı (daha kompakt)
                    showProductPrices={showProductPricesUI}
                    productImageSize={30} // Küçük resim boyutu
                    isMobileBottomSheet={true} // ✅ YENİ: Bottom sheet modu
                  />
                }
                mobileButtonsContent={
                  <ActionControlsPanel
                    onOpenDiscountModal={handleOpenDiscountModal}
                    currentUserPermissions={currentUserPermissions}
                    loadingUserPermissions={loadingUserPermissions}
                    onHoldSale={handleHoldSale}
                    cartLength={cart.length}
                    onOpenPendingSalesModal={handleOpenPendingSalesModal}
                    pendingSalesLength={pendingSales.length}
                    onDeleteSelected={isRestaurantMode ? handleDeleteWithReasonModal : handleDeleteSelected}
                    selectedCartItemId={selectedCartItemId}
                    onDecreaseSelected={isRestaurantMode ? handleDecreaseSelectedWithRestaurant : handleDecreaseSelected}
                    onIncreaseSelected={isRestaurantMode ? handleIncreaseSelectedWithRestaurant : handleIncreaseSelected}
                    onPartialPayment={handlePartialPayment}
                    onNumpadClick={handleNumpadClick}
                    prepaidSystemEnabled={prepaidSystemEnabled}
                    onPosRefund={() => {
                      // ✅ Yazarkasa tipine göre farklı modal aç (Rest-P2P de PavoPay iade modalini kullanir)
                      if (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled) {
                        setIsPavoPayRefundModalOpen(true);
                      } else {
                        setIsPosRefundModalOpen(true);
                      }
                    }}
                    hasPendingPosRefunds={
                      (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled)
                        ? true // ✅ PavoPay/Rest-P2P için her zaman aktif
                        : hasPendingPosRefundsInStorage(companyId)
                    }
                    canPosRefund={
                      isRestaurantMode 
                        ? (loginInfo?.canPosRefund ?? true)
                        : (currentUserPermissions?.canPosRefund ?? true)
                    }
                    onOpenCustomerSelectionModal={handleOpenCustomerSelectionModal}
                    onOpenPrepaidCardSearch={() => setIsPrepaidCardSearchOpen(true)}
                    onOpenPurchaseItems={handleOpenPurchaseItems}
                    allowAccountCreationOnSale={allowAccountCreationOnSale}
                    onOpenAccountCreationModal={handleOpenAddAccountModal}
                    onCollapsedChange={undefined}
                    isRestaurantMode={isRestaurantMode}
                    isPackageMode={isPackageMode}
                    restaurantLoginType={isRestaurantMode ? loginType : undefined}
                    waiterPermissions={isRestaurantMode ? waiterPermissions : undefined}
                    onTransferTable={isRestaurantMode ? handleOpenTransferTableModal : undefined}
                    onTransferItems={isRestaurantMode ? () => setTransferItemModalOpen(true) : undefined}
                    isClubEntryMode={isClubEntryMode}
                    onOpenActiveEntries={isClubEntryMode ? () => setShowActiveEntriesModal(true) : undefined}
                    onOpenDirectTrigger={isClubEntryMode ? () => setShowDirectTriggerModal(true) : undefined}
                    onOpenCardReturn={isClubEntryMode ? () => setShowCardReturnModal(true) : undefined}
                    clubEntryDirectTriggerEnabled={clubEntrySettings?.directTriggerEnabled}
                    clubEntryReservationEnabled={isClubEntryMode && clubEntrySettings?.reservationSystemEnabled === true}
                    onOpenClubReservations={isClubEntryMode ? () => setShowClubReservationsModal(true) : undefined}
                    clubEntryCardScanModalEnabled={clubEntrySettings?.cardScanModalEnabled !== false}
                    onSplitCart={() => setSplitCartModalOpen(true)}
                    onClearCart={handleOpenClearCartConfirm}
                    canClearCart={!isRestaurantMode || waiterPermissions.canClearCart}
                    onAddItemNote={(isRestaurantMode || isPackageMode) ? handleOpenItemNoteModal : undefined}
                  />
                }
              />
            </Box>
          </Grid>

          {/* Butonların konumuna göre sıralama - MOBİLDE GİZLE */}
          {!isMobile && controlPanelPosition === 'left' ? (
            <>
              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 2: Butonlar + Numpad (SOL TARAFTA) */}
              {/* ─────────────────────────────────────────────────────── */}
              <Grid item xs={12} md={actionPanelWidth} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                padding: 0,
                margin: 0,
              }}>
                <ActionControlsPanel
                  onOpenDiscountModal={handleOpenDiscountModal}
                  currentUserPermissions={currentUserPermissions}
                  loadingUserPermissions={loadingUserPermissions}
                  onHoldSale={handleHoldSale}
                  cartLength={cart.length}
                  onOpenPendingSalesModal={handleOpenPendingSalesModal}
                  pendingSalesLength={pendingSales.length}
                  onDeleteSelected={isRestaurantMode ? handleDeleteWithReasonModal : handleDeleteSelected}
                  selectedCartItemId={selectedCartItemId}
                  onDecreaseSelected={isRestaurantMode ? handleDecreaseSelectedWithRestaurant : handleDecreaseSelected}
                  onIncreaseSelected={isRestaurantMode ? handleIncreaseSelectedWithRestaurant : handleIncreaseSelected}
                  onPartialPayment={handlePartialPayment}
                  onNumpadClick={handleNumpadClick}
                  prepaidSystemEnabled={prepaidSystemEnabled}
                  onPosRefund={() => {
                    // ✅ Yazarkasa tipine göre farklı modal aç (Rest-P2P de PavoPay iade modalini kullanir)
                    if (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled) {
                      setIsPavoPayRefundModalOpen(true);
                    } else {
                      setIsPosRefundModalOpen(true);
                    }
                  }}
                  hasPendingPosRefunds={
                    (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled)
                      ? true // ✅ PavoPay/Rest-P2P için her zaman aktif (manuel satış numarası girilerek iade yapılabilir)
                      : hasPendingPosRefundsInStorage(companyId)
                  }
                  canPosRefund={
                    isRestaurantMode 
                      ? (loginInfo?.canPosRefund ?? true) // ✅ Restoran modunda loginInfo'dan al (varsayılan true - geriye uyumluluk)
                      : (currentUserPermissions?.canPosRefund ?? true) // ✅ Normal modda kasiyer yetkisinden al
                  }
                  onOpenCustomerSelectionModal={handleOpenCustomerSelectionModal}
                  onOpenPrepaidCardSearch={() => setIsPrepaidCardSearchOpen(true)}
                  onOpenPurchaseItems={handleOpenPurchaseItems}
                  allowAccountCreationOnSale={allowAccountCreationOnSale}
                  onOpenAccountCreationModal={handleOpenAddAccountModal}
                  onCollapsedChange={setIsActionPanelCollapsed}
                  isRestaurantMode={isRestaurantMode}
                  isPackageMode={isPackageMode}
                  restaurantLoginType={isRestaurantMode ? loginType : undefined}
                  waiterPermissions={isRestaurantMode ? waiterPermissions : undefined}
                  onTransferTable={isRestaurantMode ? handleOpenTransferTableModal : undefined}
                  onTransferItems={isRestaurantMode ? () => setTransferItemModalOpen(true) : undefined}
                  onSplitCart={isClubEntryMode ? undefined : () => setSplitCartModalOpen(true)}
                  onClearCart={handleOpenClearCartConfirm}
                  canClearCart={
                    isRestaurantMode 
                      ? (waiterPermissions.canClearCart ?? true)
                      : (currentUserPermissions?.canClearCart ?? true)
                  }
                  onAddItemNote={(isRestaurantMode || isPackageMode) ? handleOpenItemNoteModal : undefined}
                  isClubEntryMode={isClubEntryMode}
                  onOpenActiveEntries={isClubEntryMode ? () => setShowActiveEntriesModal(true) : undefined}
                  onOpenDirectTrigger={isClubEntryMode ? () => setShowDirectTriggerModal(true) : undefined}
                  onOpenCardReturn={isClubEntryMode ? () => setShowCardReturnModal(true) : undefined}
                  clubEntryDirectTriggerEnabled={clubEntrySettings?.directTriggerEnabled}
                  clubEntryReservationEnabled={isClubEntryMode && clubEntrySettings?.reservationSystemEnabled === true}
                  onOpenClubReservations={isClubEntryMode ? () => setShowClubReservationsModal(true) : undefined}
                  clubEntryCardScanModalEnabled={clubEntrySettings?.cardScanModalEnabled !== false}
                />
              </Grid>

              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 3: Ürünler (Kategoriler + Ürün Kartları) */}
              {/* ─────────────────────────────────────────────────────── */}
              <Grid item xs={12} md={productsColumnWidth} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                padding: 0,
                margin: 0,
              }}>
                <ProductDisplayArea
                  selectedCategory={selectedCategory}
                  handleCategoryChange={handleCategoryChange}
                  productCategoryTabSize={productCategoryTabSize}
                  displayableCategories={displayableCategories}
                  activeDisplayWarehouseId={activeDisplayWarehouseId}
                  filteredProducts={filteredProducts}
                  productCardSize={productCardSize}
                  productCardFontSize={productCardFontSize}
                  addToCart={isRestaurantMode ? addToCartWithRestaurant : addToCartWithFocus}
                  isQuantityModeActive={isQuantityModeActive}
                  quantityInputValue={quantityInputValue}
                  showProductImages={showProductImagesUI}
                  showProductPrices={showProductPricesUI}
                  productImageSize={productImageSize}
                  categoryLayout={categoryLayout}
                  categoryPosition={categoryPosition}
                  categoryFontSize={categoryFontSize}
                  isProductSortEditMode={isProductSortEditMode}
                  customProductOrder={customProductOrder[selectedCategory]}
                  onProductOrderChange={(orderedIds) => handleProductOrderChange(selectedCategory, orderedIds)}
                  onSaveProductOrder={handleSaveProductOrder}
                  onResetProductOrder={handleResetProductOrder}
                  onExitProductSortEdit={handleExitProductSortEdit}
                />
              </Grid>

              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 4: Canlı Kart İzleme (Kulüp Girişi modunda) */}
              {/* ─────────────────────────────────────────────────────── */}
              {clubEntryMonitoringPanelEnabled && (
                <Grid item xs={12} md={accessControlPanelWidth} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  padding: 0,
                  margin: 0,
                }}>
                  <AccessControlLivePanel
                    localServiceUrl={screenSettings?.localServiceUrl || 'http://localhost:3003'}
                    onNewCardScan={handleAccessControlCardScan}
                    entryResults={accessControlEntryResults}
                  />
                </Grid>
              )}
            </>
          ) : !isMobile ? (
            <>
              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 2: Ürünler (Kategoriler + Ürün Kartları) */}
              {/* ─────────────────────────────────────────────────────── */}
              <Grid item xs={12} md={productsColumnWidth} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                padding: 0,
                margin: 0,
              }}>
                <ProductDisplayArea
                  selectedCategory={selectedCategory}
                  handleCategoryChange={handleCategoryChange}
                  productCategoryTabSize={productCategoryTabSize}
                  displayableCategories={displayableCategories}
                  activeDisplayWarehouseId={activeDisplayWarehouseId}
                  filteredProducts={filteredProducts}
                  productCardSize={productCardSize}
                  productCardFontSize={productCardFontSize}
                  addToCart={isRestaurantMode ? addToCartWithRestaurant : addToCartWithFocus}
                  isQuantityModeActive={isQuantityModeActive}
                  quantityInputValue={quantityInputValue}
                  showProductImages={showProductImagesUI}
                  showProductPrices={showProductPricesUI}
                  productImageSize={productImageSize}
                  categoryLayout={categoryLayout}
                  categoryPosition={categoryPosition}
                  categoryFontSize={categoryFontSize}
                  isProductSortEditMode={isProductSortEditMode}
                  customProductOrder={customProductOrder[selectedCategory]}
                  onProductOrderChange={(orderedIds) => handleProductOrderChange(selectedCategory, orderedIds)}
                  onSaveProductOrder={handleSaveProductOrder}
                  onResetProductOrder={handleResetProductOrder}
                  onExitProductSortEdit={handleExitProductSortEdit}
                />
              </Grid>

              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 3: Butonlar + Numpad (SAĞ TARAFTA) */}
              {/* ─────────────────────────────────────────────────────── */}
              <Grid item xs={12} md={actionPanelWidth} sx={{ 
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                padding: 0,
                margin: 0,
              }}>
                <ActionControlsPanel
                  onOpenDiscountModal={handleOpenDiscountModal}
                  currentUserPermissions={currentUserPermissions}
                  loadingUserPermissions={loadingUserPermissions}
                  onHoldSale={handleHoldSale}
                  cartLength={cart.length}
                  onOpenPendingSalesModal={handleOpenPendingSalesModal}
                  pendingSalesLength={pendingSales.length}
                  onDeleteSelected={isRestaurantMode ? handleDeleteWithReasonModal : handleDeleteSelected}
                  selectedCartItemId={selectedCartItemId}
                  onDecreaseSelected={isRestaurantMode ? handleDecreaseSelectedWithRestaurant : handleDecreaseSelected}
                  onIncreaseSelected={isRestaurantMode ? handleIncreaseSelectedWithRestaurant : handleIncreaseSelected}
                  onPartialPayment={handlePartialPayment}
                  onNumpadClick={handleNumpadClick}
                  prepaidSystemEnabled={prepaidSystemEnabled}
                  onPosRefund={() => {
                    // ✅ Yazarkasa tipine göre farklı modal aç (Rest-P2P de PavoPay iade modalini kullanir)
                    if (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled) {
                      setIsPavoPayRefundModalOpen(true);
                    } else {
                      setIsPosRefundModalOpen(true);
                    }
                  }}
                  hasPendingPosRefunds={
                    (selectedFiscalDevice === 'pavopay' || isRestP2PEnabled)
                      ? true // ✅ PavoPay/Rest-P2P için her zaman aktif (manuel satış numarası girilerek iade yapılabilir)
                      : hasPendingPosRefundsInStorage(companyId)
                  }
                  canPosRefund={
                    isRestaurantMode 
                      ? (loginInfo?.canPosRefund ?? true) // ✅ Restoran modunda loginInfo'dan al (varsayılan true - geriye uyumluluk)
                      : (currentUserPermissions?.canPosRefund ?? true) // ✅ Normal modda kasiyer yetkisinden al
                  }
                  onOpenCustomerSelectionModal={handleOpenCustomerSelectionModal}
                  onOpenPrepaidCardSearch={() => setIsPrepaidCardSearchOpen(true)}
                  onOpenPurchaseItems={handleOpenPurchaseItems}
                  allowAccountCreationOnSale={allowAccountCreationOnSale}
                  onOpenAccountCreationModal={handleOpenAddAccountModal}
                  onCollapsedChange={setIsActionPanelCollapsed}
                  isRestaurantMode={isRestaurantMode}
                  isPackageMode={isPackageMode}
                  restaurantLoginType={isRestaurantMode ? loginType : undefined}
                  waiterPermissions={isRestaurantMode ? waiterPermissions : undefined}
                  onTransferTable={isRestaurantMode ? handleOpenTransferTableModal : undefined}
                  onTransferItems={isRestaurantMode ? () => setTransferItemModalOpen(true) : undefined}
                  onSplitCart={isClubEntryMode ? undefined : () => setSplitCartModalOpen(true)}
                  onClearCart={handleOpenClearCartConfirm}
                  canClearCart={
                    isRestaurantMode 
                      ? (waiterPermissions.canClearCart ?? true)
                      : (currentUserPermissions?.canClearCart ?? true)
                  }
                  onAddItemNote={(isRestaurantMode || isPackageMode) ? handleOpenItemNoteModal : undefined}
                  isClubEntryMode={isClubEntryMode}
                  onOpenActiveEntries={isClubEntryMode ? () => setShowActiveEntriesModal(true) : undefined}
                  onOpenDirectTrigger={isClubEntryMode ? () => setShowDirectTriggerModal(true) : undefined}
                  onOpenCardReturn={isClubEntryMode ? () => setShowCardReturnModal(true) : undefined}
                  clubEntryDirectTriggerEnabled={clubEntrySettings?.directTriggerEnabled}
                  clubEntryReservationEnabled={isClubEntryMode && clubEntrySettings?.reservationSystemEnabled === true}
                  onOpenClubReservations={isClubEntryMode ? () => setShowClubReservationsModal(true) : undefined}
                  clubEntryCardScanModalEnabled={clubEntrySettings?.cardScanModalEnabled !== false}
                />
              </Grid>

              {/* ─────────────────────────────────────────────────────── */}
              {/* SÜTUN 4: Canlı Kart İzleme (Kulüp Girişi modunda) */}
              {/* ─────────────────────────────────────────────────────── */}
              {clubEntryMonitoringPanelEnabled && (
                <Grid item xs={12} md={accessControlPanelWidth} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  padding: 0,
                  margin: 0,
                }}>
                  <AccessControlLivePanel
                    localServiceUrl={screenSettings?.localServiceUrl || 'http://localhost:3003'}
                    onNewCardScan={handleAccessControlCardScan}
                    entryResults={accessControlEntryResults}
                  />
                </Grid>
              )}
            </>
          ) : null}

        </Grid>
      </Box>

      {/* Tüm Modallar - Tek Component'te Toplandı */}
      <QuickSaleModals
        // Alert Modal
        alertModalOpen={alertModalOpen}
        alertModalTitle={alertModalTitle}
        alertModalMessage={alertModalMessage}
        isCriticalError={isCriticalError}
        showQuickAddButton={showQuickAddButton} // ✅ YENİ: Hızlı stok kaydı butonu kontrolü
        alertModalRetryCallback={alertModalRetryCallback}
        handleCloseAlertModal={handleCloseAlertModal}
        handleCriticalErrorClearCart={handleCriticalErrorClearCart}
        setIsQuickAddItemModalOpen={setIsQuickAddItemModalOpen}
        currentUserPermissions={currentUserPermissions}
        quickSaleUserData={quickSaleUserData}
        loadingUserPermissions={loadingUserPermissions}
        
        // UI Settings Modal
        isUISettingsModalOpen={isUISettingsModalOpen}
        setIsUISettingsModalOpen={setIsUISettingsModalOpen}
        productCardSize={productCardSize}
        productCardFontSize={productCardFontSize}
        productCategoryTabSize={productCategoryTabSize}
        productImageSize={productImageSize}
        controlPanelPosition={controlPanelPosition}
        showProductImages={showProductImagesUI}
        showProductPrices={showProductPricesUI}
        cartColumnWidth={cartColumnWidth}
        showAllCategory={showAllCategory}
        showSearchBar={showSearchBar}
        categoryLayout={categoryLayout}
        categoryPosition={categoryPosition}
        categoryFontSize={categoryFontSize}
        useFiscalDeviceForCash={screenSettings?.useFiscalDeviceForCash}
        onUseFiscalDeviceForCashChange={handleUseFiscalDeviceForCashChange}
        handleSaveUISettings={handleSaveUISettings}
        uiSettingsScreenId={currentScreen?.id ? parseInt(currentScreen.id, 10) : null}
        uiSettingsCompanyId={companyId}
        uiSettingsScreenSettings={screenSettings}
        uiSettingsDisplayableCategories={displayableCategories}
        uiSettingsIsRestaurantMode={isRestaurantMode}
        uiSettingsOnPrintingSettingsSaved={refreshScreenSettingsFromHook}
        
        // Pending Sales Modal
        isPendingSalesModalOpen={isPendingSalesModalOpen}
        setIsPendingSalesModalOpen={setIsPendingSalesModalOpen}
        pendingSales={pendingSales}
        handleRestoreSale={handleRestoreSale}
        handleDeleteSale={handleDeleteSale}
        
        // Partial Payment Modal
        isPartialPaymentModalOpen={isPartialPaymentModalOpen}
        handleClosePartialPaymentModal={handleClosePartialPaymentModal}
        calculateTotal={splitCartPartialPaymentInfo ? () => splitCartPartialPaymentInfo.total : calculateTotal}
        companyId={companyId}
        currentScreen={currentScreen}
        cashPaymentLabel={cashPaymentLabel}
        cashPaymentCashboxId={cashPaymentCashboxId}
        posOptions={posOptions}
        openAccountPaymentOption={openAccountPaymentOption}
        selectedCustomer={selectedCustomer}
        cart={splitCartPartialPaymentInfo ? splitCartPartialPaymentInfo.cartItems : cart}
        selectedFiscalDevice={(screenSettings?.selectedFiscalDevice as FiscalDeviceType) || 'hugin_t300'}
        inventoryItems={sqlInventoryItems}
        showAlertModal={showAlertModal}
        setIsProcessingPosPayment={setIsProcessingPosPayment}
        isRestaurantMode={isRestaurantMode}
        ingenicoRecoveryData={ingenicoRecoveryData}
        useExternalPrinterForPavo={screenSettings?.useExternalPrinterForPavo || false}
        kitchenOrderCode={
          (screenSettings?.selectedFiscalDevice as string) === 'pavopay' && screenSettings?.useKitchenScreen && reservedShortCode
            ? reservedShortCode
            : undefined
        }
        receiptCompanyName={
          (screenSettings?.selectedFiscalDevice as string) === 'pavopay' && screenSettings?.useKitchenScreen
            ? (screenSettings?.receiptDisplaySettings?.companyName || companyNameCache || undefined)
            : undefined
        }
        // 🎫 NUMARA GARANTİSİ: Parçalı ödemede cihaz denemesi yapıldıktan sonra satış
        // tamamlanmadan modal kapatılırsa denenen numara yakılır (restoran modunda
        // refreshOrderNumber zaten erken döner, RS numaraları etkilenmez).
        onPartialPaymentAttemptFailed={() => {
          console.warn('🎫 [PARÇALI ÖDEME] Deneme sonrası satış tamamlanmadan kapatıldı — sipariş numarası yenileniyor...');
          refreshOrderNumber();
        }}
        
        // Discount Modal
        isDiscountModalOpen={isDiscountModalOpen}
        handleCloseDiscountModal={handleCloseDiscountModal}
        discountInputPercentage={discountInputPercentage}
        setDiscountInputPercentage={setDiscountInputPercentage}
        discountInputAmount={discountInputAmount}
        setDiscountInputAmount={setDiscountInputAmount}
        handleRemoveDiscount={handleRemoveDiscount}
        lastAppliedDiscountAmount={lastAppliedDiscountAmount}
        appliedDiscountType={appliedDiscountType}
        handleApplyDiscount={handleApplyDiscount}
        handleApplyQuickDiscount={handleApplyQuickDiscount}
        
        // Weight Input Modal
        isWeightInputModalOpen={isWeightInputModalOpen}
        setIsWeightInputModalOpen={setIsWeightInputModalOpen}
        isCartUpdateMode={isCartUpdateMode}
        setIsCartUpdateMode={setIsCartUpdateMode}
        setSelectedCartItem={setSelectedCartItem}
        handleQuantityUpdateConfirm={handleQuantityUpdateConfirm}
        tempSelectedProduct={tempSelectedProduct}
        addToCart={addToCart}
        // Tarti baglantisi da local-service uzerinden kuruluyor
        localServiceUrl={screenSettings?.localServiceUrl}
        useScale={screenSettings?.useScale}
        selectedScaleId={screenSettings?.selectedScaleId}
        scaleReadMode={screenSettings?.scaleReadMode}
        
        // Bulk Add Account Modal
        isBulkAddAccountModalOpen={isBulkAddAccountModalOpen}
        setIsBulkAddAccountModalOpen={setIsBulkAddAccountModalOpen}
        refreshCustomers={refreshCustomers}
        
        // Snackbar
        snackbarOpen={snackbarOpen}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        handleSnackbarClose={handleSnackbarClose}
        
        // Customer Selection Modal
        isCustomerSelectionModalOpen={isCustomerSelectionModalOpen}
        handleCloseCustomerSelectionModal={handleCloseCustomerSelectionModal}
        handleSelectCustomerFromModalWrapper={handleSelectCustomerFromModalWrapper}
        handleOpenAddAccountModal={handleOpenAddAccountModal} // ✅ YENİ: Yeni cari ekleme butonu
        
        // Quick Add Item Modal
        isQuickAddItemModalOpen={isQuickAddItemModalOpen}
        handleCloseQuickAddItemModal={handleCloseQuickAddItemModal}
        quickAddBarcode={quickAddBarcode} // ✅ YENİ: Uyarı listesinden gelen barkod ön dolgusu
        setQuickAddBarcode={setQuickAddBarcode}
        
        // Scan Alert List Modal (sepete eklenemeyen okutmalar)
        isScanAlertListOpen={isScanAlertListOpen}
        scanAlerts={scanAlerts}
        handleCloseScanAlertList={closeScanAlertList}
        handleDismissScanAlert={dismissScanAlert}
        handleClearScanAlerts={clearScanAlerts}
        
        // Payment Waiting Modal
        paymentWaitingModalOpen={paymentWaitingModalOpen}
        paymentWaitingStatus={paymentWaitingStatus}
        paymentWaitingMessage={paymentWaitingMessage}
      />

      {/* Odeme akisi modalleri -> PaymentFlowModals container (refactor) */}
      <PaymentFlowModals
        isPosErrorModalOpen={isPosErrorModalOpen}
        posPaymentError={posPaymentError}
        posRetryCallback={posRetryCallback}
        posContinueCallback={posContinueCallback}
        handleClosePosErrorModal={handleClosePosErrorModal}
        handleClosePosErrorModalAndClearCart={handleClosePosErrorModalAndClearCart}
        isSplitPaymentMode={isSplitPaymentMode}
        setIsSplitPaymentMode={setIsSplitPaymentMode}
        setSplitPaymentSuccessCallback={setSplitPaymentSuccessCallback}
        setPendingSplitPayment={setPendingSplitPayment}
        setPendingSplitCartItems={setPendingSplitCartItems}
        setPendingSplitActiveSaleId={setPendingSplitActiveSaleId}
        splitPaymentResolveRef={splitPaymentResolveRef}
        isRestaurantMode={isRestaurantMode}
        selectedFiscalDevice={selectedFiscalDevice}
        getFiscalDevice={getFiscalDevice}
        showDeptVatMismatchModal={showDeptVatMismatchModal}
        setShowDeptVatMismatchModal={setShowDeptVatMismatchModal}
        deptVatSyncResult={deptVatSyncResult}
        setDeptVatSyncResult={setDeptVatSyncResult}
        isFiscalDisconnectedConfirmOpen={isFiscalDisconnectedConfirmOpen}
        handleFiscalDisconnectedCancel={handleFiscalDisconnectedCancel}
        handleFiscalDisconnectedConfirm={handleFiscalDisconnectedConfirm}
        isFiscalDisconnectedConfirmOpenPartial={isFiscalDisconnectedConfirmOpenPartial}
        handleFiscalDisconnectedCancelPartial={handleFiscalDisconnectedCancelPartial}
        handleFiscalDisconnectedConfirmPartial={handleFiscalDisconnectedConfirmPartial}
        isFiscalErrorContinueOpen={isFiscalErrorContinueOpen}
        fiscalErrorContinueMessage={fiscalErrorContinueMessage}
        handleFiscalErrorContinueConfirm={handleFiscalErrorContinueConfirm}
        handleFiscalErrorContinueCancel={handleFiscalErrorContinueCancel}
        isPosRefundModalOpen={isPosRefundModalOpen}
        setIsPosRefundModalOpen={setIsPosRefundModalOpen}
        setHasPendingPosRefunds={setHasPendingPosRefunds}
        companyId={companyId}
        screenSettings={screenSettings}
        showAlertModal={showAlertModal}
        isPavoPayRefundModalOpen={isPavoPayRefundModalOpen}
        setIsPavoPayRefundModalOpen={setIsPavoPayRefundModalOpen}
        currentCashier={currentCashier}
        quickSaleUserData={quickSaleUserData}
        isCustomerQueryModalOpen={isCustomerQueryModalOpen}
        setIsCustomerQueryModalOpen={setIsCustomerQueryModalOpen}
        isRestP2PEnabled={isRestP2PEnabled}
        cart={cart}
        setPavoPayTempCustomerParty={setPavoPayTempCustomerParty}
        setPavoPayIsEInvoice={setPavoPayIsEInvoice}
        setPavoPayTempCustomerName={setPavoPayTempCustomerName}
        setPendingCustomerParty={setPendingCustomerParty}
        setPendingIsEInvoice={setPendingIsEInvoice}
        setPendingNotificationPhone={setPendingNotificationPhone}
        activeBranchId={activeBranchId}
        setSelectedCustomer={setSelectedCustomer}
        refreshCustomers={refreshCustomers}
        customers={customers}
        otherPaymentModalOpen={otherPaymentModalOpen}
        setOtherPaymentModalOpen={setOtherPaymentModalOpen}
        otherPaymentOptions={otherPaymentOptions}
        calculateTotal={calculateTotal}
        companyNameCache={companyNameCache}
        currentScreen={currentScreen}
        selectedCustomer={selectedCustomer}
        customersHook={customersHook}
        mealCardFiscalData={mealCardFiscalData}
        sqlInventoryItems={sqlInventoryItems}
        reservedLongCode={reservedLongCode}
        reservedShortCode={reservedShortCode}
        showSnackbar={showSnackbar}
        tableId={tableId}
        tableName={tableName}
        waiterName={waiterName}
        clearCart={clearCart}
        refreshOrderNumber={refreshOrderNumber}
        generalCariRef={generalCariRef}
        focusSearchBar={focusSearchBar}
        restaurantScreenId={restaurantScreenId}
        navigate={navigate}
        printReceiptAfterPayment={printReceiptAfterPayment}
        onlinePaymentModalOpen={onlinePaymentModalOpen}
        setOnlinePaymentModalOpen={setOnlinePaymentModalOpen}
        onlinePaymentOptions={onlinePaymentOptions}
        selectedWarehouseIds={selectedWarehouseIds}
        isClubEntryMode={isClubEntryMode}
        onExternalTicketSelected={handleExternalTicketSelected}
        appliedDiscountType={appliedDiscountType}
        lastAppliedDiscountAmount={lastAppliedDiscountAmount}
      />

      {/* Muhtelif inline diyaloglar -> QuickSaleMiscDialogs container (refactor) */}
      <QuickSaleMiscDialogs
        isIngenicoRecoveryModalOpen={isIngenicoRecoveryModalOpen}
        ingenicoRecoveryData={ingenicoRecoveryData}
        isIngenicoRecoveryProcessing={isIngenicoRecoveryProcessing}
        handleIngenicoRecoveryCancel={handleIngenicoRecoveryCancel}
        handleIngenicoRecoveryContinue={handleIngenicoRecoveryContinue}
        setIsIngenicoRecoveryProcessing={setIsIngenicoRecoveryProcessing}
        setIsIngenicoRecoveryModalOpen={setIsIngenicoRecoveryModalOpen}
        setIngenicoRecoveryData={setIngenicoRecoveryData}
        showAlertModal={showAlertModal}
        clearCartConfirmOpen={clearCartConfirmOpen}
        handleCloseClearCartConfirm={handleCloseClearCartConfirm}
        handleConfirmClearCart={handleConfirmClearCart}
        isCustomerNoteModalOpen={isCustomerNoteModalOpen}
        setIsCustomerNoteModalOpen={setIsCustomerNoteModalOpen}
        customerNote={customerNote}
        setCustomerNote={setCustomerNote}
        packagePaymentModalOpen={packagePaymentModalOpen}
        setPackagePaymentModalOpen={setPackagePaymentModalOpen}
        setPendingPackageOrderData={setPendingPackageOrderData}
        addOnOrderId={addOnOrderId}
        pendingPackageOrderData={pendingPackageOrderData}
        handlePackagePaymentSelect={handlePackagePaymentSelect}
        otherPaymentOptions={otherPaymentOptions}
        isPurchaseItemsOpen={isPurchaseItemsOpen}
        setIsPurchaseItemsOpen={setIsPurchaseItemsOpen}
        selectedRefundItem={selectedRefundItem}
        setSelectedRefundItem={setSelectedRefundItem}
        refundLoading={refundLoading}
        refundConfirmOpen={refundConfirmOpen}
        setRefundConfirmOpen={setRefundConfirmOpen}
        prepaidCard={prepaidCard}
        purchaseItems={purchaseItems}
        purchaseItemsLoading={purchaseItemsLoading}
        purchaseItemsGrandTotal={purchaseItemsGrandTotal}
        handleRefundItem={handleRefundItem}
        formatCurrency={formatCurrency}
        isProductSortPinDialogOpen={isProductSortPinDialogOpen}
        setIsProductSortPinDialogOpen={setIsProductSortPinDialogOpen}
        productSortPinInput={productSortPinInput}
        setProductSortPinInput={setProductSortPinInput}
        productSortPinError={productSortPinError}
        setProductSortPinError={setProductSortPinError}
        handleProductSortPinDigit={handleProductSortPinDigit}
        handleProductSortPinBackspace={handleProductSortPinBackspace}
      />
      
      {/* Local Service Guncelleme Modali */}
      <LocalServiceUpdateModal
        open={showLocalServiceUpdateModal}
        version={localServiceUpdateVersion}
        onRestart={applyLocalServiceUpdate}
        onClose={() => {
          setShowLocalServiceUpdateModal(false);
          dismissLocalServiceUpdate();
        }}
      />

      {/* RestaurantFlowModals container (refactor: restoran modallari tasindi) */}
      <RestaurantFlowModals
        isRestaurantMode={isRestaurantMode}
        tableId={tableId}
        restaurantScreenId={restaurantScreenId}
        tableName={tableName}
        companyId={companyId}
        cart={cart}
        navigate={navigate}
        screenSettings={screenSettings}
        numpadModalOpen={numpadModalOpen}
        numpadProduct={numpadProduct}
        handleNumpadConfirm={handleNumpadConfirm}
        handleNumpadCancel={handleNumpadCancel}
        transferTableModalOpen={transferTableModalOpen}
        handleCloseTransferTableModal={handleCloseTransferTableModal}
        handleTransferTable={handleTransferTable}
        handleRequestMerge={handleRequestMerge}
        isTransferringTable={isTransferringTable}
        transferItemModalOpen={transferItemModalOpen}
        setTransferItemModalOpen={setTransferItemModalOpen}
        restaurantBranchId={restaurantBranchId}
        waiterName={waiterName}
        addWaiterAction={addWaiterAction}
        mergeTargetTableId={mergeTargetTableId}
        mergeTablesModalOpen={mergeTablesModalOpen}
        handleCloseMergeTablesModal={handleCloseMergeTablesModal}
        handleConfirmMergeTables={handleConfirmMergeTables}
        mergeTargetTableName={mergeTargetTableName}
        mergeSourceItems={mergeSourceItems}
        mergeTargetItems={mergeTargetItems}
        isMergingTables={isMergingTables}
        confirmOpenTableModalOpen={confirmOpenTableModalOpen}
        pendingProduct={pendingProduct}
        handleConfirmOpenTable={handleConfirmOpenTable}
        handleCancelOpenTable={handleCancelOpenTable}
        isOpeningTable={isOpeningTable}
        unprintedItemsWarningOpen={unprintedItemsWarningOpen}
        setUnprintedItemsWarningOpen={setUnprintedItemsWarningOpen}
        handleContinueWithoutPrinting={handleContinueWithoutPrinting}
        handlePrintToKitchenAndGoBack={handlePrintToKitchenAndGoBack}
        handlePrintCancellationAndGoBack={handlePrintCancellationAndGoBack}
        handlePrintBothAndGoBack={handlePrintBothAndGoBack}
        unprintedItemsList={unprintedItemsList}
        cancelledItemsList={cancelledItemsList}
        kitchenPrintInfoOpen={kitchenPrintInfoOpen}
        kitchenPrintInfoData={kitchenPrintInfoData}
        noCategoryPrinterModalOpen={noCategoryPrinterModalOpen}
        setNoCategoryPrinterModalOpen={setNoCategoryPrinterModalOpen}
        noCategoryPrinterModalData={noCategoryPrinterModalData}
        kitchenReturnCountdownOpen={kitchenReturnCountdownOpen}
        handleKitchenReturnCountdownDismiss={handleKitchenReturnCountdownDismiss}
        handleKitchenReturnCountdownTimeout={handleKitchenReturnCountdownTimeout}
        receiptPrintedNoPermissionOpen={receiptPrintedNoPermissionOpen}
        setReceiptPrintedNoPermissionOpen={setReceiptPrintedNoPermissionOpen}
        receiptPrintedInfo={receiptPrintedInfo}
        receiptPrintedConfirmOpen={receiptPrintedConfirmOpen}
        handleReceiptPrintedConfirmCancel={handleReceiptPrintedConfirmCancel}
        handleReceiptPrintedConfirm={handleReceiptPrintedConfirm}
        pendingProductForReceiptConfirm={pendingProductForReceiptConfirm}
        deleteReasonModalOpen={deleteReasonModalOpen}
        selectedCartItemId={selectedCartItemId}
        handleDeleteReasonConfirm={handleDeleteReasonConfirm}
        setDeleteReasonModalOpen={setDeleteReasonModalOpen}
        itemNoteModalOpen={itemNoteModalOpen}
        itemNoteCurrentNote={itemNoteCurrentNote}
        handleItemNoteConfirm={handleItemNoteConfirm}
        setItemNoteModalOpen={setItemNoteModalOpen}
        isLoadingTableItems={isLoadingTableItems}
        tableLoadError={tableLoadError}
        handleRetryLoadTableItems={handleRetryLoadTableItems}
      />

      {/* ✅ YENİ: Sepet Bölme Modalı */}
      <SplitCartModal
        open={splitCartModalOpen}
        onClose={() => setSplitCartModalOpen(false)}
        isRestaurantMode={isRestaurantMode}
        tableId={tableId ? parseInt(tableId) : undefined}
        tableName={tableName || undefined}
        restaurantScreenId={restaurantScreenId || undefined}
        companyId={companyId || ''}
        mainCart={cart}
        mainInvoiceNumber={reservedLongCode || `QS-${Date.now()}`}
        setCart={setCart}
        showSnackbar={showSnackbar}
        waiterName={waiterName || undefined}
        screenSettings={screenSettings}
        splitRightCart={splitRightCart}
        onRightCartChange={setSplitRightCart}
        splitRightInvoiceNumber={splitRightInvoiceNumber}
        onRightInvoiceNumberChange={setSplitRightInvoiceNumber}
        splitBIndex={splitBIndex}
        onSplitBIndexChange={setSplitBIndex}
        refreshKey={splitCartRefreshKey}
        onProcessPayment={handleSplitCartPayment}
        onRestaurantPaymentComplete={isRestaurantMode ? () => {
          // ✅ RESTORAN MODU: Tüm ödemeler tamamlandı
          console.log('🏁 [RESTORAN - SPLIT CART] Tüm ödemeler tamamlandı, masalar ekranına dönülüyor...');
          
          // Modal kapat
          setSplitCartModalOpen(false);
          
          // Sepet state'lerini sıfırla
          setCart([]);
          setSplitRightCart([]);
          setSplitRightInvoiceNumber('');
          setSplitBIndex(2);
          
          // Masalar ekranına dön
          navigate(`/restaurant-screen/${restaurantScreenId}`);
        } : undefined}
      />


      {/* ✅ YENİ: Cari Hesap Ekleme Modalı (AddAccount) */}
      <AddAccount
        open={isAddAccountModalOpen}
        onClose={handleCloseAddAccountModal}
      />

      {/* ✅ YENİ: Kasiyer Giriş Modalı */}
      {screenSettings?.useCashierSystem && currentScreen && (
        <CashierLoginModal
          open={isCashierLoginModalOpen}
          screenType="QuickSale"
          quickSaleScreenId={parseInt(currentScreen.id)}
          onLogin={handleCashierLogin}
          onClose={() => {
            setIsCashierLoginModalOpen(false);
            // ✅ YENİ: Modal kapatıldığında bekleyen ürünü de temizle
            setPendingProductForCashierLogin(null);
            setPendingQuantityForCashierLogin(1);
          }}
          allowClose={true}
          title="Kasiyer Girişi"
        />
      )}

      {/* ✅ YENİ: Mutfak Siparişleri Modalı */}
      {screenSettings?.useKitchenScreen && (
        <QuickSaleKitchenOrdersModal
          open={isKitchenOrdersModalOpen}
          onClose={() => setIsKitchenOrdersModalOpen(false)}
          companyId={companyId}
          branchId={activeBranchId}
          selectedQRMenuId={screenSettings?.selectedQRMenuId || null}
          screenId={currentScreen?.id || null}
          screenSettings={screenSettings}
          isRestaurantMode={isRestaurantMode}
          seller={seller}
        />
      )}

      {/* ✅ YENİ: Kasiyere özel Gün Sonu Yazdırma Modalı */}
      <EndOfDayPrintModal
        open={endOfDayPrintModalOpen}
        onClose={() => setEndOfDayPrintModalOpen(false)}
        lockedBranchId={activeBranchId}
      />

      {/* 🖥️ Plan 2 — Kiosk Urun Gorunurluk Modal */}
      {currentScreen?.id && companyId && currentScreen?.branchId && (
        <KioskProductsModal
          open={isKioskProductsModalOpen}
          onClose={() => setIsKioskProductsModalOpen(false)}
          screenId={parseInt(currentScreen.id.toString(), 10)}
          companyId={companyId}
          branchId={currentScreen.branchId}
        />
      )}

      {/* Oto Kilitleme Geri Sayım Modalı */}
      <AutoLockCountdownModal
        open={showAutoLockCountdown}
        onDismiss={dismissAutoLockCountdown}
        onTimeout={handleAutoLockFromQuickSale}
      />

      {/* ✅ YENİ: Para Üstü Modalı */}
      <CashChangeModal
        open={cashChangeModalOpen}
        onClose={() => setCashChangeModalOpen(false)}
        totalAmount={calculateTotal()}
        onConfirmPayment={handleCashChangeConfirm}
        formatCurrency={formatCurrency}
        isProcessing={isProcessingPayment}
      />

      {/* ✅ YENİ: Sepeti Temizleme Onay Modalı (ActionControlsPanel için) */}
      {/* ✅ YENİ: Menü Opsiyon Seçim Modalı */}
      {pendingMenuProduct && companyId && (
        <MenuOptionSelectionModal
          open={isMenuOptionModalOpen}
          onClose={() => {
            setIsMenuOptionModalOpen(false);
            setPendingMenuProduct(null);
            setEditingCartItem(null); // ✅ Düzenleme modunu sıfırla
          }}
          menuProductId={pendingMenuProduct.productId}
          menuProductName={pendingMenuProduct.productName}
          menuPrice={pendingMenuProduct.productPrice}
          companyId={companyId}
          initialSelections={editingCartItem?.selectedOptions}
          onConfirm={(selections) => {
            if (editingCartItem) {
              // ✅ DÜZENLEME MODU: Mevcut sepet öğesini güncelle
              // ⚠️ KRİTİK: Fiyatı da güncelle! 
              // Yeni fiyat = base fiyat + yeni ekstra opsiyon fiyatı
              const newPrice = pendingMenuProduct.productPrice + selections.totalPriceDifference;
              
              setCart(prevCart => prevCart.map(item => {
                if (item.cartItemId === editingCartItem.cartItemId) {
                  return {
                    ...item,
                    price: newPrice, // ✅ YENİ: Fiyatı güncelle
                    selectedOptions: selections.selectedOptions,
                    totalPriceDifference: selections.totalPriceDifference,
                    menuNormalItems: selections.normalItems
                  };
                }
                return item;
              }));
              showSnackbar('Menü opsiyonları güncellendi.', 'success');
              setEditingCartItem(null);
            } else if (isRestaurantMode && tableId && restaurantScreenId) {
              // ✅ YENİ: RESTORAN MODU - Opsiyonlar seçildi, şimdi numpad aç
              console.log('🍽️ [RESTAURANT - MENÜ ONAYLANDI] Numpad açılıyor...');
              const menuProductWithOptions = {
                ...pendingMenuProduct,
                price: pendingMenuProduct.productPrice + selections.totalPriceDifference,
                salePrice: pendingMenuProduct.productPrice + selections.totalPriceDifference,
                id: pendingMenuProduct.productId,
                name: pendingMenuProduct.productName,
                unit: pendingMenuProduct.productUnit,
                vatRate: pendingMenuProduct.productVatRate,
                vatStatus: pendingMenuProduct.productVatStatus,
                categoryId: pendingMenuProduct.categoryId || getScreenCategoryId(pendingMenuProduct.productId) || '',
                selectedOptions: selections.selectedOptions,
                totalPriceDifference: selections.totalPriceDifference,
                menuNormalItems: selections.normalItems,
                hasMenu: true,
                hasRecipe: pendingMenuProduct.hasRecipe,
                canBeNegative: pendingMenuProduct.itemCanBeNegative,
                stockQuantity: pendingMenuProduct.itemStockQuantity,
              };
              setNumpadProduct(menuProductWithOptions);
              setNumpadModalOpen(true);
            } else {
              // ✅ NORMAL HIZLI SATIŞ: Sepete ekle
              addToCartWithOptions(
                pendingMenuProduct,
                selections.selectedOptions,
                selections.totalPriceDifference,
                selections.normalItems
              );
            }
            setIsMenuOptionModalOpen(false);
            setPendingMenuProduct(null);
          }}
        />
      )}

      {/* MÜŞTERİ EKRANI: Tam Ekran bildirimi şimdilik devre dışı - tarayıcı kısıtlaması nedeniyle çalışmıyor */}

      {/* Ön ödemeli kart arama modalı */}
      {prepaidSystemEnabled && companyId && (
        <PrepaidCardSearchModal
          open={isPrepaidCardSearchOpen}
          onClose={() => setIsPrepaidCardSearchOpen(false)}
          onCardSelected={(card) => {
            setPrepaidCard(card);
            const wallets = card.wallets || card.activeSession?.walletBalances || [];
            if (wallets.length === 1) {
              setSelectedPrepaidWalletId(wallets[0].walletId);
            } else {
              setSelectedPrepaidWalletId(null);
            }
            setIsPrepaidCardSearchOpen(false);
            setTimeout(() => {
              focusSearchBar();
            }, LAYOUT.TIMEOUTS.FOCUS_DELAY);
          }}
          companyId={companyId}
        />
      )}

      {/* ClubEntryFlowModals container (refactor: kulup girisi modallari tasindi) */}
      {isClubEntryMode && (
        <ClubEntryFlowModals
          showPrePaymentCardModal={showPrePaymentCardModal}
          setShowPrePaymentCardModal={setShowPrePaymentCardModal}
          setPendingPaymentType={setPendingPaymentType}
          setPendingPosArgs={setPendingPosArgs}
          handlePrePaymentCardConfirm={handlePrePaymentCardConfirm}
          handlePrePaymentCardSkip={handlePrePaymentCardSkip}
          clubEntrySettings={clubEntrySettings}
          getServiceItemsFromCart={getServiceItemsFromCart}
          getClubEntryCardSlots={getClubEntryCardSlots}
          lastCardSlots={lastCardSlots}
          currentUserEmail={currentUser?.email || ''}
          pendingPaymentType={pendingPaymentType}
          showActiveEntriesModal={showActiveEntriesModal}
          setShowActiveEntriesModal={setShowActiveEntriesModal}
          currentScreen={currentScreen}
          showDirectTriggerModal={showDirectTriggerModal}
          setShowDirectTriggerModal={setShowDirectTriggerModal}
          screenSettings={screenSettings}
          showCardReturnModal={showCardReturnModal}
          setShowCardReturnModal={setShowCardReturnModal}
          companyId={companyId}
          showClubReservationsModal={showClubReservationsModal}
          setShowClubReservationsModal={setShowClubReservationsModal}
          selectedCustomer={selectedCustomer}
          currentUser={currentUser}
          cashPaymentCashboxId={cashPaymentCashboxId}
          posOptions={posOptions}
          handleReservationPosCheckInRequest={handleReservationPosCheckInRequest}
          showPostSaleCardModal={showPostSaleCardModal}
          setShowPostSaleCardModal={setShowPostSaleCardModal}
          lastSaleReceiptId={lastSaleReceiptId}
          setLastSaleReceiptId={setLastSaleReceiptId}
          setLastServiceItems={setLastServiceItems}
          clearCart={clearCart}
          lastReferenceNumber={lastReferenceNumber}
          lastPavoPaySaleNumber={lastPavoPaySaleNumber}
          lastPaymentMethod={lastPaymentMethod}
          lastServiceItems={lastServiceItems}
          refreshOrderNumber={refreshOrderNumber}
        />
      )}

      {/* ✅ KULÜP GİRİŞİ — "Ödeme alındı, depozito tahsilatı bekleniyor" ekranı.
          Ana (giriş/hizmet) ödemesi alındıktan sonra açılır; operatör "Depozitoyu
          Tahsil Et" deyip kartı POS'a tekrar okutarak depozitoyu AYRI bir satış
          olarak tahsil eder. Çıkışta bu satış TAM İPTAL (CancelSale) ile iade edilir. */}
      <Dialog
        open={clubEntryDepositPending}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        onClose={(_e, reason) => { if (reason === 'backdropClick') return; }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          ✅ Giriş ödemesi alındı — Depozito tahsilatı bekleniyor
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Giriş/hizmet ödemesi tamamlandı. Şimdi <b>depozitoyu ayrı olarak</b> tahsil edin.
            {pendingDepositInfo?.depositMethod === 'POS'
              ? ' Kartı POS cihazına tekrar okutmanız istenecek.'
              : ' Depozito NAKİT olarak tahsil edilecek.'}
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Depozito ({pendingDepositInfo?.depositMethod === 'POS' ? 'POS' : 'Nakit'})
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              ₺{(pendingDepositInfo?.totalDeposit || 0).toFixed(2)}
            </Typography>
          </Box>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1.5 }}>
            Depozito tahsil edilmeden kart ataması tamamlanmaz.
          </Typography>
          {clubEntryDepositBusy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">İşlem sürüyor, lütfen bekleyin...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={clubEntryDepositBusy}
            startIcon={<PaymentsIcon />}
            onClick={handleCollectDeposit}
          >
            Depozitoyu Tahsil Et ({pendingDepositInfo?.depositMethod === 'POS' ? 'POS' : 'Nakit'} ₺{(pendingDepositInfo?.totalDeposit || 0).toFixed(2)})
          </Button>
          {pendingDepositInfo?.depositMethod === 'POS' && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              disabled={clubEntryDepositBusy}
              onClick={handleSwitchDepositToCash}
            >
              Nakitle Al (POS yerine)
            </Button>
          )}
          <Button
            variant="text"
            color="error"
            fullWidth
            disabled={clubEntryDepositBusy}
            onClick={handleCancelClubEntrySale}
          >
            Satışı İptal Et (depozito alınamadı)
          </Button>
        </DialogActions>
      </Dialog>

      {/* PavoPay P2P modalleri -> PrepaidPavoP2PModals container (refactor) */}
      <PrepaidPavoP2PModals
        pavoP2pSelectOpen={pavoP2pSelectOpen}
        setPavoP2pSelectOpen={setPavoP2pSelectOpen}
        hookHandlePavoP2pDeviceSelected={hookHandlePavoP2pDeviceSelected}
        companyId={companyId}
        activeBranchId={activeBranchId}
        activePavoPayment={activePavoPayment}
        cancelActivePavoPayment={cancelActivePavoPayment}
        restP2PModalOpen={restP2PModalOpen}
        restP2PStatus={restP2PStatus}
        restP2PMode={restP2PMode}
        restP2PTotal={restP2PTotal}
        restP2PParts={restP2PParts}
        restP2PProgress={restP2PProgress}
        restP2PMessage={restP2PMessage}
        restP2PDeviceName={restP2PDeviceName}
        restP2PErrorCode={restP2PErrorCode}
        handleRestP2PCancel={handleRestP2PCancel}
        handleRestP2PClose={handleRestP2PClose}
        handleRestP2PContinueWithoutDevice={handleRestP2PContinueWithoutDevice}
      />

    </Box>
  );
};

export default QuickSaleScreen; 